import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Container,
  Paper,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { categorizeKrishnaVatu, getKrishnaGrandhamInfo } from '../utils/krishnaYajurvedaUtils';
import { getGrandhamTypePhase } from '../utils/grandhamDataUtils';

// Grandham tree structure data - BASELINE for comparison
const grandhamTreeData = {
  telugu: {
    'బ్రాహ్మణము': {
      'ప్రథమాష్టకము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయాష్టకము': [1, 2, 3, 4, 5, 6, 7, 8],
      'తృతీయాష్ఠకము': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    },
    'ఆరణ్యకభాగము': {
      'main': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      'ఏకాగ్ని కాండము': [1, 2],
    },
    'సంహితా': {
      'ప్రథమకాండము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయకాండము': [1, 2, 3, 4, 5, 6],
      'తృతీయకాండము': [1, 2, 3, 4, 5],
      'చతుర్థకాండము': [1, 2, 3, 4, 5, 6, 7],
      'పంచమకాండము': [1, 2, 3, 4, 5, 6, 7],
      'షష్ఠకాండము': [1, 2, 3, 4, 5, 6],
      'సప్తమకాండము': [1, 2, 3, 4, 5]
    },
    'పదము': {
      'ప్రథమకాండము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయకాండము': [1, 2, 3, 4, 5, 6],
      'తృతీయకాండము': [1, 2, 3, 4, 5],
      'చతుర్థకాండము': [1, 2, 3, 4, 5, 6, 7],
      'పంచమకాండము': [1, 2, 3, 4, 5, 6, 7],
      'షష్ఠకాండము': [1, 2, 3, 4, 5, 6],
      'సప్తమకాండము': [1, 2, 3, 4, 5]
    },
    'క్రమము': {
      'ప్రథమకాండము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయకాండము': [1, 2, 3, 4, 5, 6],
      'తృతీయకాండము': [1, 2, 3, 4, 5],
      'చతుర్థకాండము': [1, 2, 3, 4, 5, 6, 7],
      'పంచమకాండము': [1, 2, 3, 4, 5, 6, 7],
      'షష్ఠకాండము': [1, 2, 3, 4, 5, 6],
      'సప్తమకాండము': [1, 2, 3, 4, 5]
    },
    'జటా': {
      'ప్రథమకాండము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయకాండము': [1, 2, 3, 4, 5, 6],
      'తృతీయకాండము': [1, 2, 3, 4, 5],
      'చతుర్థకాండము': [1, 2, 3, 4, 5, 6, 7],
      'పంచమకాండము': [1, 2, 3, 4, 5, 6, 7],
      'షష్ఠకాండము': [1, 2, 3, 4, 5, 6],
      'సప్తమకాండము': [1, 2, 3, 4, 5]
    },
    'ఘనము': {
      'ప్రథమకాండము': [1, 2, 3, 4, 5, 6, 7, 8],
      'ద్వితీయకాండము': [1, 2, 3, 4, 5, 6],
      'తృతీయకాండము': [1, 2, 3, 4, 5],
      'చతుర్థకాండము': [1, 2, 3, 4, 5, 6, 7],
      'పంచమకాండము': [1, 2, 3, 4, 5, 6, 7],
      'షష్ఠకాండము': [1, 2, 3, 4, 5, 6],
      'సప్తమకాండము': [1, 2, 3, 4, 5]
    }
  },
  sanskrit: {
    'ब्राह्मणम्': {
      'प्रथमाष्टकम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयाष्टकम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'तृतीयाष्टकम्': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    },
    'आरण्यकभागः': {
      'main': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      'एकाग्नि काण्डम्': [1, 2],
    },
    'संहिता': {
      'प्रथमकाण्डम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयकाण्डम्': [1, 2, 3, 4, 5, 6],
      'तृतीयकाण्डम्': [1, 2, 3, 4, 5],
      'चतुर्थकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'पञ्चमकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'षष्ठकाण्डम्': [1, 2, 3, 4, 5, 6],
      'सप्तमकाण्डम्': [1, 2, 3, 4, 5]
    },
    'पदम्': {
      'प्रथमकाण्डम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयकाण्डम्': [1, 2, 3, 4, 5, 6],
      'तृतीयकाण्डम्': [1, 2, 3, 4, 5],
      'चतुर्थकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'पञ्चमकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'षष्ठकाण्डम्': [1, 2, 3, 4, 5, 6],
      'सप्तमकाण्डम्': [1, 2, 3, 4, 5]
    },
    'क्रमम्': {
      'प्रथमकाण्डम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयकाण्डम्': [1, 2, 3, 4, 5, 6],
      'तृतीयकाण्डम्': [1, 2, 3, 4, 5],
      'चतुर्थकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'पञ्चमकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'षष्ठकाण्डम्': [1, 2, 3, 4, 5, 6],
      'सप्तमकाण्डम्': [1, 2, 3, 4, 5]
    },
    'जटा': {
      'प्रथमकाण्डम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयकाण्डम्': [1, 2, 3, 4, 5, 6],
      'तृतीयकाण्डम्': [1, 2, 3, 4, 5],
      'चतुर्थकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'पञ्चमकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'षष्ठकाण्डम्': [1, 2, 3, 4, 5, 6],
      'सप्तमकाण्डम्': [1, 2, 3, 4, 5]
    },
    'घनम्': {
      'प्रथमकाण्डम्': [1, 2, 3, 4, 5, 6, 7, 8],
      'द्वितीयकाण्डम्': [1, 2, 3, 4, 5, 6],
      'तृतीयकाण्डम्': [1, 2, 3, 4, 5],
      'चतुर्थकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'पञ्चमकाण्डम्': [1, 2, 3, 4, 5, 6, 7],
      'षष्ठकाण्डम्': [1, 2, 3, 4, 5, 6],
      'सप्तमकाण्डम्': [1, 2, 3, 4, 5]
    }
  }
};

const labels = {
  telugu: {
    title: 'పరీక్షాధికారి వీక్షణ',
    pathasala: 'పాఠశాల',
    vatuName: 'వటువు పేరు',
    selectPathasala: 'పాఠశాలను ఎంచుకోండి',
    selectVatu: 'వటువును ఎంచుకోండి',
    loading: 'ఆశంచోడపడుతుంది...',
    marks: 'సత్కారం',
    moolam: 'మూలము',
    previousGrandham: 'మునుపటి గ్రంథం',
    currentGrandham: 'ప్రస్తుత గ్రంథం',
    grandhamDetails: 'గ్రంథ విశేషాలు',
    allGiven: 'అన్నీ ఇవ్వబడినవి',
    markComplete: 'పూర్తిగా సూచించండి',
    completionStatus: 'పూర్తత్వ స్థితి',
    passPercentage: 'ఉత్తీర్ణత',
    examReward: 'పరీక్ష పురస్కారం',
    clearAll: 'అన్నీ తొలిగించండి',
    recordingStart: 'రికార్డింగ్ ప్రారంభించండి',
    recordingStop: 'రికార్డింగ్ ఆపండి',
    playRecording: 'రికార్డింగ్ వినండి',
    recordedSuccessfully: 'సఫలంగా రికార్డ్ చేయబడింది',
    notRecorded: 'ఇంకా రికార్డ్ చేయబడలేదు',
    underperformingTitle: '⚠️ విద్యార్థి పనితీరు',
    underperformingMessage: 'ఈ విద్యార్థి సంస్థ నిర్ధారిత నియమాల ప్రకారం పనితీరు తక్కువగా ఉంది మరియు అర్హత లేనిదిగా పరిగణించబడుతుంది. కానీ పరీక్షాధికారిగా, మీరు మీ స్వంత విచక్షణకు అనుకూలంగా పరీక్ష కొనసాగించే అవకాశం ఉంది.',
    continueExam: 'పరీక్ష కొనసాగించండి',
    stopExam: 'పరీక్ష ఆపండి'
  },
  sanskrit: {
    title: 'परीक्षाधिकारी दृश्यम्',
    pathasala: 'पाठशाला',
    vatuName: 'वटोः नाम',
    selectPathasala: 'पाठशालां चिनोतु',
    selectVatu: 'वटुं चिनोतु',
    loading: 'आयाति...',
    marks: 'इतिहास',
    moolam: 'मूलम्',
    previousGrandham: 'पूर्वग्रन्थम्',
    currentGrandham: 'वर्तमानग्रन्थम्',
    grandhamDetails: 'ग्रन्थविशेषाः',
    allGiven: 'सर्वाणि दत्तानि',
    markComplete: 'पूर्णतया चिह्नितं करोतु',
    completionStatus: 'पूर्णता स्थितिः',
    passPercentage: 'उत्तीर्णता',
    examReward: 'परीक्षापुरस्कारम्',
    clearAll: 'सर्वाणि मार्जयतु',
    recordingStart: 'ध्वनिलेखनं आरभ्यताम्',
    recordingStop: 'ध्वनिलेखनं निषेध्यताम्',
    playRecording: 'ध्वनिलेखनं शृणुतु',
    recordedSuccessfully: 'सफलतया लिखितम्',
    notRecorded: 'अद्यापि न लिखितम्',
    underperformingTitle: '⚠️ वटोः कार्यप्रदर्शनम्',
    underperformingMessage: 'अयं वटुः संस्थानिर्धारितनियमानां अनुसारम् अल्पकार्यप्रदर्शनः अस्ति तथा अयोग्यः मन्यते। तथापि परीक्षाधिकारी भवन् भवान् स्वविवेकेन परीक्षां सन्धारयितुं शक्नोति।',
    continueExam: 'परीक्षां सन्धारयतु',
    stopExam: 'परीक्षां निषेध्यताम्'
  }
};

export default function ParishaadhikariView() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { teluguYearTelugu, teluguYearSanskrit, yearId } = useTeluguYear();
  
  // User must be authenticated to access this page (API validates parikshaadhikari access)
  if (!user) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const [yearList, setYearList] = useState([]);
  const [pathasalaList, setPathasalaList] = useState([]);
  const [selectedPathasala, setSelectedPathasala] = useState('');
  const [allVatusData, setAllVatusData] = useState([]); // Store full vatu dataset
  const [vatuList, setVatuList] = useState([]);
  const [selectedVatu, setSelectedVatu] = useState('');
  const [selectedVatuData, setSelectedVatuData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grandhamToShow, setGrandhamToShow] = useState({ previous: [], current: [] });
  const [marksData, setMarksData] = useState({});
  const [pannamRatings, setPannamRatings] = useState({}); // For both previous (pass/fail) and current grandham ratings
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [parikshaadhikariInfo, setParikshaadhikariInfo] = useState(null);
  const [isUnavailableToday, setIsUnavailableToday] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false); // Track if parikshaadhikari not found
  
  // Feedback related states
  const [batchParikshaadhikaris, setBatchParikshaadhikaris] = useState([]);
  const [feedbackData, setFeedbackData] = useState({}); // Structure: { parikshaadhikari_id: { positive: blob, improvement: blob } }
  const [recordingState, setRecordingState] = useState({}); // Structure: { `${id}_positive`: isRecording, `${id}_improvement`: isRecording }
  const [audioBlobs, setAudioBlobs] = useState({}); // Structure: { `${id}_positive`: blob, `${id}_improvement`: blob }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef({});
  const streamRef = useRef(null);
  
  // State for underperforming student dialog
  const [isUnderperformingDialogOpen, setIsUnderperformingDialogOpen] = useState(false);
  const [hasStudentStopped, setHasStudentStopped] = useState(false);
  const [pendingRatingChange, setPendingRatingChange] = useState(null); // To store rating change after dialog confirmation
  
  // State for special Krishna conversion dialog (when failures > 3)
  const [isSpecialKrishnaConversionDialogOpen, setIsSpecialKrishnaConversionDialogOpen] = useState(false);
  const [specialKrishnaConversionInfo, setSpecialKrishnaConversionInfo] = useState(null); // { from, to, failureCount }

  // State for pariksha type selection (for non-Krishna types)
  const [selectedParikshaType, setSelectedParikshaType] = useState('');
  const [availableParikshaTypes, setAvailableParikshaTypes] = useState([]); // Intersection of parikshaadhikari and vatu types

  // State for Athyuthama exam mode
  const [isAthyuthamaMode, setIsAthyuthamaMode] = useState(false);
  const [athyuthamaVatusData, setAthyuthamaVatusData] = useState([]); // Top vatus from getTopVatusByMarks
  const [availableParikshaadhikarisList, setAvailableParikshaadhikarisList] = useState([]); // Parikshaadhikaris available today
  const [athyuthamaRatings, setAthyuthamaRatings] = useState({}); // { parikshaadhikari_id: rating (1-10) }

  // Section names (language-aware) used in multiple places
  const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
  const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
  const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
  const moolamSections = [brahmanam, aranyakam, samhita];

  // useEffect to check for conversion when ratings change
  useEffect(() => {
    // Only check if we have current items and ratings
    if (!grandhamToShow.current || grandhamToShow.current.length === 0 || Object.keys(pannamRatings).length === 0) return;
    
    // Check each current item that is a special case
    grandhamToShow.current.forEach((item, idx) => {
      if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
        // Skip if already converted to final level (సామాన్య క్రమాంతం or సామాన్య ఘనము)
        const alreadyConverted = [
          'సామాన్య క్రమాంతం', 'सामान्य क्रमन्थम्',
          'సామాన్య ఘనము', 'सामान्य घनम्'
        ].includes(item.section);
        
        if (alreadyConverted) return;
        
        const key = `curr_${item.section}_${idx}`;
        
        // Only proceed if this item has ratings
        if (!pannamRatings[key]) return;
        
        // Count failures
        let failureCount = 0;
        
        if (item.isSpecialKrishna && item.totalDropdowns) {
          // Special Krishna cases use _special_kanda structure with 'fail'/'pass'
          const kandaData = pannamRatings[key]['_special_kanda'];
          if (kandaData && typeof kandaData === 'object') {
            Object.keys(kandaData).forEach(pannam => {
              const rating = kandaData[pannam];
              if (rating === 'fail') {
                failureCount++;
              }
            });
          }
        } else if (item.isSpecialRule) {
          // Special rule cases use kanda/pannam structure with 'fail'/'pass'
          const ratingObj = pannamRatings[key];
          if (ratingObj && typeof ratingObj === 'object') {
            Object.keys(ratingObj).forEach(kanda => {
              if (typeof ratingObj[kanda] === 'object') {
                Object.keys(ratingObj[kanda]).forEach(pannam => {
                  const rating = ratingObj[kanda][pannam];
                  if (rating === 'fail') {
                    failureCount++;
                  }
                });
              }
            });
          }
        }
        
        // If more than 6 failures, show conversion dialog
        if (failureCount > 6 && !isSpecialKrishnaConversionDialogOpen) {
          
          let convertTo = null;
          if (item.isSpecialKrishna && item.totalDropdowns === 100) {
            convertTo = lang === 'telugu' ? 'సామాన్య క్రమాంతం' : 'सामान्य क्रमन्थम्';
          } else if (item.isSpecialKrishna && item.totalDropdowns === 50) {
            convertTo = lang === 'telugu' ? 'సామాన్య ఘనుము' : 'सामान्य घनम्';
          }
          
          if (convertTo) {
            setSpecialKrishnaConversionInfo({
              from: item.section,
              to: convertTo,
              failureCount: failureCount
            });
            setIsSpecialKrishnaConversionDialogOpen(true);
          }
        }
      }
    });
  }, [pannamRatings, grandhamToShow.current, isSpecialKrishnaConversionDialogOpen, lang]);

  // useEffect to mark exam as completed when >10 failures (అనుత్తీర్ణుడయ్యాడు)
  useEffect(() => {
    // Only check if we have current items with special cases
    if (!grandhamToShow.current || grandhamToShow.current.length === 0) return;
    
    const hasSpecialCase = grandhamToShow.current.some(item => 
      (item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule
    );
    
    if (!hasSpecialCase) return;
    
    // Count total failures
    let totalFailures = 0;
    grandhamToShow.current.forEach((item, idx) => {
      if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
        const key = `curr_${item.section}_${idx}`;
        
        if (item.isSpecialKrishna && item.totalDropdowns) {
          const kandaData = pannamRatings[key]?.['_special_kanda'];
          if (kandaData) {
            Object.keys(kandaData).forEach(pannam => {
              if (kandaData[pannam] === 'fail') {
                totalFailures++;
              }
            });
          }
        } else if (item.isSpecialRule) {
          const ratingObj = pannamRatings[key];
          if (ratingObj) {
            Object.keys(ratingObj || {}).forEach(kanda => {
              Object.keys(ratingObj[kanda] || {}).forEach(pannam => {
                if (ratingObj[kanda][pannam] === 'fail') {
                  totalFailures++;
                }
              });
            });
          }
        }
      }
    });
    
    // If >10 failures, mark as completed (అనుత్తీర్ణుడయ్యాడు)
    // If <=10 failures and was previously marked completed, unmark it
    if (totalFailures > 10 && !hasStudentStopped) {
      setHasStudentStopped(true);
    } else if (totalFailures <= 10 && hasStudentStopped) {
      setHasStudentStopped(false);
    }
  }, [pannamRatings, grandhamToShow.current, hasStudentStopped]);

  // ============================================================================
  // HELPER: Convert new nested _selected format to flat array format
  // New format: { "1": { "_selected": true }, "2": { "_selected": true } }
  // Old format: [1, 2]
  // ============================================================================
  const convertNestedToFlatArray = (nestedObj) => {
    if (!nestedObj || typeof nestedObj !== 'object') return [];
    
    const result = [];
    Object.keys(nestedObj).forEach(key => {
      // Skip metadata keys
      if (key.startsWith('_')) return;
      
      const value = nestedObj[key];
      if (value && typeof value === 'object' && value._selected === true) {
        // This is a leaf node with _selected - it's a pannam number
        result.push(key);
      }
    });
    
    // Sort numerically if all are numbers
    const allNumeric = result.every(v => !isNaN(Number(v)));
    if (allNumeric) {
      result.sort((a, b) => Number(a) - Number(b));
    }
    
    return result;
  };

  // ============================================================================
  // HELPER: Convert Krishna Yajurveda grandham data from new nested format to old flat format
  // Input: { "బ్రాహ్మణము": { "ప్రథమాష్టకము": { "1": { "_selected": true }, "2": { "_selected": true } } } }
  // Output: { "బ్రాహ్మణము": { "ప్రథమాష్టకము": [1, 2] } }
  // Special handling for ఆరణ్యకభాగము which has both direct pannams and nested kandas
  // ============================================================================
  const convertKrishnaYajurvedaToFlatFormat = (krishnaData) => {
    if (!krishnaData || typeof krishnaData !== 'object') return krishnaData;
    
    const result = {};
    
    // Section names that need special handling (direct pannams + nested kandas)
    const aranyakamNames = ['ఆరణ్యకభాగము', 'आरण्यकभागः'];
    
    Object.keys(krishnaData).forEach(sectionKey => {
      // Skip metadata keys
      if (sectionKey.startsWith('_')) return;
      
      const sectionData = krishnaData[sectionKey];
      if (!sectionData || typeof sectionData !== 'object') return;
      
      // Check if this section itself has _selected (e.g., జటా, ఘనము)
      if (sectionData._selected === true && Object.keys(sectionData).filter(k => !k.startsWith('_')).length === 0) {
        // This is a simple selected section without children (like జటా or ఘనము)
        result[sectionKey] = { _selected: true };
        return;
      }
      
      result[sectionKey] = {};
      
      // Special handling for ఆరణ్యకభాగము - has both direct pannams and nested kandas
      if (aranyakamNames.includes(sectionKey)) {
        // Collect direct pannams (numeric keys with _selected)
        const directPannams = [];
        
        Object.keys(sectionData).forEach(key => {
          if (key.startsWith('_')) return;
          
          const value = sectionData[key];
          if (value && typeof value === 'object') {
            // Check if this is a direct pannam (numeric key with _selected)
            if (value._selected === true && !isNaN(Number(key))) {
              directPannams.push(Number(key));
            } 
            // Check if this is a nested kanda (like ఏకాగ్ని కాండము)
            else if (isNaN(Number(key))) {
              // This is a nested kanda - process it
              const kandaPannams = convertNestedToFlatArray(value);
              if (kandaPannams.length > 0) {
                result[sectionKey][key] = kandaPannams.map(p => isNaN(Number(p)) ? p : Number(p));
              }
            }
          }
        });
        
        // Add direct pannams under 'main' key (matching baseline structure)
        if (directPannams.length > 0) {
          directPannams.sort((a, b) => a - b);
          result[sectionKey]['main'] = directPannams;
        }
        
        // Clean up if section is empty
        if (Object.keys(result[sectionKey]).length === 0) {
          delete result[sectionKey];
        }
        return;
      }
      
      // Standard processing for other sections (బ్రాహ్మణము, సంహితా, పదము, క్రమము)
      Object.keys(sectionData).forEach(kandaKey => {
        // Skip metadata keys
        if (kandaKey.startsWith('_')) return;
        
        const kandaData = sectionData[kandaKey];
        if (!kandaData || typeof kandaData !== 'object') return;
        
        // Check if kandaData has nested pannams with _selected or is already an array
        if (Array.isArray(kandaData)) {
          // Already in flat format
          result[sectionKey][kandaKey] = kandaData;
        } else {
          // Check if any child has _selected (new nested format)
          const childKeys = Object.keys(kandaData).filter(k => !k.startsWith('_'));
          const hasNestedSelected = childKeys.some(k => 
            kandaData[k] && typeof kandaData[k] === 'object' && kandaData[k]._selected === true
          );
          
          if (hasNestedSelected) {
            // Convert nested _selected format to flat array
            const pannams = convertNestedToFlatArray(kandaData);
            if (pannams.length > 0) {
              result[sectionKey][kandaKey] = pannams.map(p => isNaN(Number(p)) ? p : Number(p));
            }
          } else {
            // This kanda has deeper nesting - try to extract pannams
            const pannams = convertNestedToFlatArray(kandaData);
            if (pannams.length > 0) {
              result[sectionKey][kandaKey] = pannams.map(p => isNaN(Number(p)) ? p : Number(p));
            }
          }
        }
      });
      
      // Clean up empty sections
      if (Object.keys(result[sectionKey]).length === 0) {
        delete result[sectionKey];
      }
    });
    
    return result;
  };

  // ============================================================================
  // HELPER: Extract and convert grandham data from payload
  // Handles the new payload structure where krishna_yajurveda is a key
  // ============================================================================
  const extractAndConvertGrandhamData = (payloadData) => {
    if (!payloadData || typeof payloadData !== 'object') return {};
    
    // Check if krishna_yajurveda exists as a key (new format)
    if (payloadData.krishna_yajurveda) {
      const converted = convertKrishnaYajurvedaToFlatFormat(payloadData.krishna_yajurveda);
      return converted;
    }
    
    // Check if data is already in the old flat format (section names as top-level keys)
    const sectionNames = ['బ్రాహ్మణము', 'ఆరణ్యకభాగము', 'సంహితా', 'పదము', 'క్రమము', 'జటా', 'ఘనము',
                          'ब्राह्मणम्', 'आरण्यकभागः', 'संहिता', 'पदम्', 'क्रमम्', 'जटा', 'घनम्'];
    const hasDirectSections = sectionNames.some(section => payloadData[section]);
    
    if (hasDirectSections) {
      // Data is already in flat format with section names at top level
      // But check if the kanda data uses new nested format
      const result = {};
      Object.keys(payloadData).forEach(sectionKey => {
        if (sectionKey.startsWith('_')) return;
        const sectionData = payloadData[sectionKey];
        if (!sectionData || typeof sectionData !== 'object') return;
        
        result[sectionKey] = {};
        Object.keys(sectionData).forEach(kandaKey => {
          if (kandaKey.startsWith('_')) return;
          const kandaData = sectionData[kandaKey];
          
          if (Array.isArray(kandaData)) {
            result[sectionKey][kandaKey] = kandaData;
          } else if (kandaData && typeof kandaData === 'object') {
            // Check for new nested format
            const pannams = convertNestedToFlatArray(kandaData);
            if (pannams.length > 0) {
              result[sectionKey][kandaKey] = pannams.map(p => isNaN(Number(p)) ? p : Number(p));
            }
          }
        });
        
        if (Object.keys(result[sectionKey]).length === 0) {
          delete result[sectionKey];
        }
      });
      return result;
    }
    
    // Return as-is if format is unrecognized
    return payloadData;
  };

  // ============================================================================
  // HELPER: Check if pariksha type is Krishna Yajurveda
  // ============================================================================
  const isKrishnaYajurveda = (parikshaType) => {
    return parikshaType === 'krishna_yajurveda';
  };

  // ============================================================================
  // HELPER: Extract batch_id for API payloads
  // Gets batch_id from selectedVatuData based on selected pariksha type
  // ============================================================================
  const getBatchIdForPayload = () => {
    if (!selectedVatuData || !selectedParikshaType) return null;
    
    const batchAssignments = selectedVatuData.batch_assignments || [];
    const batchIds = selectedVatuData.batch_ids || (selectedVatuData.batch_id ? [selectedVatuData.batch_id] : []);
    
    // Find batch_id matching the selected pariksha type
    if (batchAssignments.length > 0) {
      const matchingAssignment = batchAssignments.find(ba => ba.pariksha_type === selectedParikshaType);
      if (matchingAssignment?.batch_id) {
        return matchingAssignment.batch_id;
      }
    }
    
    // Fallback to first batch_id if no match found
    if (batchIds.length > 0) {
      return batchIds[0];
    }
    
    return null;
  };

  // ============================================================================
  // HELPER: Extract grandham data for non-Krishna pariksha types
  // Input format: { "sanskrit": { "grandhamName": { "_selected": true, "subItem": { "_selected": true } } } }
  // Output: { grandhamName: [subItems] } or { grandhamName: true } for flat grandhams
  // ============================================================================
  const extractNonKrishnaGrandhamData = (payloadData, parikshaType) => {
    if (!payloadData || typeof payloadData !== 'object') return {};
    
    let typeData = null;
    let originalKeyMap = {}; // Map grandham names to their original keys (for lakshanam)
    
    // Special handling for lakshanam pariksha type
    // lakshanam has sub-keys: lakshanam_kanthastham, lakshanam_vyakhyanam, etc.
    if (parikshaType === 'lakshanam') {
      // Find all keys that start with 'lakshanam_'
      const lakshanamKeys = Object.keys(payloadData).filter(key => key.startsWith('lakshanam_'));
      
      if (lakshanamKeys.length === 0) return {};
      
      // Process each lakshanam_* key separately to preserve original keys
      typeData = {};
      lakshanamKeys.forEach(key => {
        const keyData = payloadData[key];
        if (keyData && typeof keyData === 'object') {
          // For each grandham in this lakshanam type, store the original key
          Object.keys(keyData).forEach(grandhamName => {
            if (!grandhamName.startsWith('_')) {
              originalKeyMap[grandhamName] = key; // Store original key (lakshanam_kanthastham, etc.)
              typeData[grandhamName] = keyData[grandhamName];
            }
          });
        }
      });
    } else {
      // For other types (sanskrit, vedadhyayi_sahityam), use the exact key
      typeData = payloadData[parikshaType];
    }
    
    if (!typeData || typeof typeData !== 'object') return {};
    
    const result = {};
    
    Object.keys(typeData).forEach(grandhamName => {
      if (grandhamName.startsWith('_')) return;
      
      const grandhamData = typeData[grandhamName];
      if (!grandhamData || typeof grandhamData !== 'object') return;
      
      // Check if this grandham has sub-items (like kandas)
      const subItems = [];
      Object.keys(grandhamData).forEach(subKey => {
        if (subKey.startsWith('_')) return;
        if (grandhamData[subKey] && grandhamData[subKey]._selected === true) {
          subItems.push(subKey);
        }
      });
      
      if (subItems.length > 0) {
        result[grandhamName] = { items: subItems, originalKey: originalKeyMap[grandhamName] || parikshaType };
      } else if (grandhamData._selected === true) {
        // Flat grandham with no sub-items
        result[grandhamName] = { selected: true, originalKey: originalKeyMap[grandhamName] || parikshaType };
      }
    });
    
    return result;
  };

  // ============================================================================
  // HELPER: Calculate grandham to show for non-Krishna pariksha types
  // For non-Krishna: Show all current grandhams with pass/fail ratings
  // Show previous grandhams as checkboxes (unless it's "ఏమీ ఇవ్వలేదు")
  // ============================================================================
  const calculateNonKrishnaGrandhamToShow = (previousRaw, currentRaw, parikshaType) => {
    const previousToShow = [];
    const currentToShow = [];
    
    const nothingGivenMarkers = ['ఏమీ ఇవ్వలేదు', 'किमपि नोक्तम्'];
    
    // Extract previous grandhams
    const previousData = extractNonKrishnaGrandhamData(previousRaw, parikshaType);
    
    // Check if previous is "ఏమీ ఇవ్వలేదు" - if so, don't show previous section
    const isPreviousEmpty = Object.keys(previousData).length === 0 || 
      Object.keys(previousData).every(key => nothingGivenMarkers.includes(key));
    
    if (!isPreviousEmpty && Object.keys(previousData).length > 0) {
      Object.keys(previousData).forEach((grandhamName, idx) => {
        if (nothingGivenMarkers.includes(grandhamName)) return;
        
        const dataObj = previousData[grandhamName];
        const items = dataObj.items || [];
        const isSingle = dataObj.selected === true;
        previousToShow.push({
          section: grandhamName,
          data: items.length > 0 ? { '_items': items } : { '_single': true },
          isPrevious: true,
          isNonKrishna: true,
          parikshaType: dataObj.originalKey || parikshaType // Use originalKey as parikshaType
        });
      });
    }
    
    // Extract current grandhams
    const currentData = extractNonKrishnaGrandhamData(currentRaw, parikshaType);
    
    if (Object.keys(currentData).length > 0) {
      Object.keys(currentData).forEach((grandhamName, idx) => {
        const dataObj = currentData[grandhamName];
        const items = dataObj.items || [];
        const isSingle = dataObj.selected === true;
        currentToShow.push({
          section: grandhamName,
          data: items.length > 0 ? { '_items': items } : { '_single': true },
          isPrevious: false,
          isNonKrishna: true,
          parikshaType: dataObj.originalKey || parikshaType // Use originalKey as parikshaType
        });
      });
    }
    
    return {
      previous: previousToShow,
      current: currentToShow,
      isNonKrishna: true
    };
  };

  // Auto-save ratings for non-Krishna pariksha types
  // Helper to get all pannams from baseline for a section
  const getAllPannamsFromBaseline = (sectionName) => {
    const baseline = grandhamTreeData[lang === 'telugu' ? 'telugu' : 'sanskrit'];
    const section = baseline[sectionName];
    if (!section) return [];
    
    const allPannams = [];
    Object.keys(section).forEach(kanda => {
      const pannams = section[kanda];
      if (Array.isArray(pannams)) {
        pannams.forEach(num => {
          allPannams.push({ kanda, pannam: String(num) });
        });
      }
    });
    return allPannams;
  };

  // Helper to get all pannams from API data for a section
  const getAllPannamsFromData = (sectionData) => {
    if (!sectionData || typeof sectionData !== 'object') return [];
    
    const allPannams = [];
    Object.keys(sectionData).forEach(kanda => {
      const pannams = sectionData[kanda];
      if (Array.isArray(pannams)) {
        pannams.forEach(num => {
          allPannams.push({ kanda, pannam: String(num) });
        });
      }
    });
    return allPannams;
  };

  // Helper to check if section has ALL pannams from baseline
  const hasAllPannamsFromBaseline = (sectionData, sectionName) => {
    const baselinePannams = getAllPannamsFromBaseline(sectionName);
    const dataPannams = getAllPannamsFromData(sectionData);
    
    if (baselinePannams.length === 0) return false;
    
    // Check if every baseline pannam exists in data
    return baselinePannams.every(baseline => {
      return dataPannams.some(data => 
        data.kanda === baseline.kanda && data.pannam === baseline.pannam
      );
    });
  };

  // Calculate grandhams to show based on NEW rules
  const calculateGrandhamToShow = (previous, current) => {
    const previousToShow = [];
    const currentToShow = [];
    
    // Get section names based on language
    const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
    const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
    const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
    const padamu = lang === 'telugu' ? 'పదము' : 'पदम्';
    const kramamu = lang === 'telugu' ? 'క్రమము' : 'क्रमम्';
    
    // Also check for Telugu variant
    const samhitaAlt = 'సంహితా';
    const samhitaSanskrit = 'संहिता';
    
    let shouldShowPrevious = false;
    
    if (previous && Object.keys(previous).length > 0) {
      // Check each section that exists in previous data
      const brahmanamComplete = previous[brahmanam] && hasAllPannamsFromBaseline(previous[brahmanam], brahmanam);
      const aranyakamComplete = previous[aranyakam] && hasAllPannamsFromBaseline(previous[aranyakam], aranyakam);
      const samhitaComplete = (previous[samhita] || previous[samhitaAlt]) && 
        hasAllPannamsFromBaseline(previous[samhita] || previous[samhitaAlt], samhita);
      const padamuComplete = previous[padamu] && hasAllPannamsFromBaseline(previous[padamu], padamu);
      const kramamuComplete = previous[kramamu] && hasAllPannamsFromBaseline(previous[kramamu], kramamu);
      
      // Check if Brahmanam or Aranyakam exists in previous data
      const hasBrahmanam = !!previous[brahmanam];
      const hasAranyakam = !!previous[aranyakam];
      
      // RULE 1 - Exception: If at least one pannam of Brahmanam or Aranyakam is missing
      // This includes cases where:
      // 1. One section exists and is complete, the other exists but is incomplete
      // 2. One section exists and is complete, the other is missing entirely
      // 3. Both exist but at least one is incomplete
      const hasAnyBrahmanamOrAranyakam = hasBrahmanam || hasAranyakam;
      const bothComplete = brahmanamComplete && aranyakamComplete;
      
      // Exception triggers when we have at least one of these sections, but not both are complete
      if (hasAnyBrahmanamOrAranyakam && !bothComplete) {
        // Show both Brahmanam and Aranyakam from previous (if they exist)
        if (previous[brahmanam]) {
          previousToShow.push({
            section: brahmanam,
            data: previous[brahmanam],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
        if (previous[aranyakam]) {
          previousToShow.push({
            section: aranyakam,
            data: previous[aranyakam],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
      }
      // RULE 2: Show only INCOMPLETE sections from previous grandhams (applies only if exception didn't trigger)
      else {
        // Add only incomplete sections
        if (previous[brahmanam] && !brahmanamComplete) {
          previousToShow.push({
            section: brahmanam,
            data: previous[brahmanam],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
        
        if (previous[aranyakam] && !aranyakamComplete) {
          previousToShow.push({
            section: aranyakam,
            data: previous[aranyakam],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
        
        if ((previous[samhita] || previous[samhitaAlt]) && !samhitaComplete) {
          previousToShow.push({
            section: samhita,
            data: previous[samhita] || previous[samhitaAlt],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
        
        // Check Padamu and Kramamu sections
        if (previous[padamu] && !padamuComplete) {
          previousToShow.push({
            section: padamu,
            data: previous[padamu],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
        
        if (previous[kramamu] && !kramamuComplete) {
          previousToShow.push({
            section: kramamu,
            data: previous[kramamu],
            isPrevious: true
          });
          shouldShowPrevious = true;
        }
      }
    }
    
    // Current grandham - apply exclusion rules based on Padamu/Kramamu/Jata/Ghana
    if (current && Object.keys(current).length > 0) {      
      Object.keys(current).forEach(section => {
        if (current[section] && typeof current[section] === 'object' && Object.keys(current[section]).length > 0) {
          currentToShow.push({
            section: section,
            data: current[section],
            isPrevious: false
          });
        }
      });
    }
    
    return {
      previous: previousToShow,
      current: currentToShow
    };
  };

  // Fetch vatu data for logged-in parikshaadhikari - triggered when year changes
  useEffect(() => {
    if (!teluguYearTelugu) {
      setAllVatusData([]);
      setPathasalaList([]);
      setVatuList([]);
      setSelectedVatu('');
      setSelectedVatuData(null);
      setGrandhamToShow({ previous: [], current: [] });
      setAccessDenied(false);
      setIsAthyuthamaMode(false);
      setAthyuthamaVatusData([]);
      setAvailableParikshaadhikarisList([]);
      return;
    }

    const fetchVatusForParikshaadhikari = async () => {
      setLoading(true);
      try {
        // ========== FIRST CHECK FOR ATHYUTHAMA MODE ==========
        // If athyuthama mode is enabled, load top vatus instead of normal parikshaadhikari-assigned vatus
        const configRes = await api.get('/api/admin-config/active');
        const configData = await configRes.json();
        
        if (configRes.ok && configData.success && configData.data?.athyuthama_start) {
          // Athyuthama mode is enabled - load top vatus instead
          setIsAthyuthamaMode(true);
          console.log('Athyuthama mode enabled');

          // Fetch top vatus by marks
          const topVatusRes = await api.get(`/api/result/top-vatus?year_id=${encodeURIComponent(yearId)}&isAttendanceRequired=true`);
          const topVatusData = await topVatusRes.json();
          
          if (topVatusRes.ok && topVatusData.success && Array.isArray(topVatusData.data)) {
            setAthyuthamaVatusData(topVatusData.data);
            console.log('Loaded athyuthama top vatus:', topVatusData.data.length);
            console.log('Sample top vatu data:', topVatusData.data[0]);
            
            // Set pathasala list from top vatus
            const pathasalaMap = new Map();
            topVatusData.data.forEach(v => {
              console.log('Processing vatu for pathasala:', v.vatu_id, 'pathasala_id:', v.pathasala_id, 'pathasala_name:', v.pathasala_name);
              if (v.pathasala_id && v.pathasala_name) {
                pathasalaMap.set(v.pathasala_id, {
                  _id: v.pathasala_id,
                  pathasalaName: v.pathasala_name
                });
              }
            });
            const uniquePathasalas = Array.from(pathasalaMap.values())
              .sort((a, b) => a.pathasalaName.localeCompare(b.pathasalaName));
            console.log('Setting pathasala list for athyuthama:', uniquePathasalas);
            setPathasalaList(uniquePathasalas);
            
            // Store athyuthama vatus data
            const mappedVatusData = topVatusData.data.map(v => ({
              ...v,
              pathasala_id: v.pathasala_id || (typeof v.grandham_to_show?.[0]?.pathasala_id === 'string' ? v.grandham_to_show[0].pathasala_id : null)
            }));
            console.log('Setting allVatusData for athyuthama:', mappedVatusData.length, 'vatus');
            setAllVatusData(mappedVatusData);
          } else {
            setAthyuthamaVatusData([]);
            setPathasalaList([]);
            setAllVatusData([]);
          }

          // Fetch all parikshaadhikaris available today
          const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
          const parikshaadhikariRes = await api.get(`/api/parikshaadhikari/available-today?date=${today}`);
          const parikshaadhikariData = await parikshaadhikariRes.json();
          
          if (parikshaadhikariRes.ok && parikshaadhikariData.success && Array.isArray(parikshaadhikariData.data)) {
            setAvailableParikshaadhikarisList(parikshaadhikariData.data);
            console.log('Loaded available parikshaadhikaris for today:', parikshaadhikariData.data.length);
          } else {
            setAvailableParikshaadhikarisList([]);
          }

          // Reset selections
          setSelectedPathasala('');
          setVatuList([]);
          setSelectedVatu('');
          setSelectedVatuData(null);
          setGrandhamToShow({ previous: [], current: [] });
          setAccessDenied(false);
          setIsUnavailableToday(false); // Override - athyuthama mode doesn't check parikshaadhikari availability
          setLoading(false);
          return; // Exit early - athyuthama mode is fully handled
        }
        // ========== END ATHYUTHAMA MODE CHECK ==========

        // Normal mode - athyuthama not enabled
        setIsAthyuthamaMode(false);
        setAthyuthamaVatusData([]);
        setAvailableParikshaadhikarisList([]);
        
        const res = await api.get(`/api/vatu/by-parikshaadhikari-phone/${teluguYearTelugu}`);
        const data = await res.json();
        
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAccessDenied(false);
          // Store full dataset
          setAllVatusData(data.data);
          
          // Extract unique pathasalas from response
          if (data.pathasalas && Array.isArray(data.pathasalas)) {
            setPathasalaList(data.pathasalas);
          } else {
            // Fallback: extract from vatu data if pathasalas not in response
            const pathasalaMap = new Map();
            data.data.forEach(v => {
              if (v.pathasala_id && v.pathasala_name && v.pathasala_name.trim() !== '') {
                pathasalaMap.set(v.pathasala_id, {
                  _id: v.pathasala_id,
                  pathasalaName: v.pathasala_name
                });
              }
            });
            const uniquePathasalas = Array.from(pathasalaMap.values())
              .sort((a, b) => a.pathasalaName.localeCompare(b.pathasalaName));
            setPathasalaList(uniquePathasalas);
          }
          
          // Fetch parikshaadhikari info from parent user object
          if (data.parikshaadhikari_info) {
            setParikshaadhikariInfo(data.parikshaadhikari_info);
            
            // Check if parikshaadhikari is inactive
            if (data.parikshaadhikari_info.active === false) {
              setError('మీ ఖాతా నిష్క్రియం చేయబడింది. దయచేసి అడ్మిన్‌ను సంప్రదించండి / आपका खाता निष्क्रिय कर दिया गया है। कृपया व्यवस्थापक से संपर्क करें');
              setAllVatusData([]);
              setPathasalaList([]);
              setLoading(false);
              return;
            }
            
            // Check if parikshaadhikari is unavailable today
            const availableDates = data.parikshaadhikari_info.available_dates || [];
            const today = new Date();
            const todayStr = today.toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
            
            const isAvailable = availableDates.some(date => {
              const dateStr = new Date(date).toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
              return dateStr === todayStr;
            });
            
            setIsUnavailableToday(!isAvailable && availableDates.length > 0);
          }
          
          // Reset selection
          setSelectedPathasala('');
          setVatuList([]);
          setSelectedVatu('');
          setSelectedVatuData(null);
          setGrandhamToShow({ previous: [], current: [] });
        } else {
          // API returned error (404 or failure) - show access denied message
          setAccessDenied(true);
          setAllVatusData([]);
          setPathasalaList([]);
          setParikshaadhikariInfo(null);
          setIsUnavailableToday(false);
        }
      } catch (err) {
        // Network error or other failure - show access denied message
        setAccessDenied(true);
        setAllVatusData([]);
        setPathasalaList([]);
        setParikshaadhikariInfo(null);
        setIsUnavailableToday(false);
      } finally {
        setLoading(false);
      }
    };

    fetchVatusForParikshaadhikari();
  }, [teluguYearTelugu]);

  // Filter vatus from cached data when pathasala is selected
  useEffect(() => {
    if (!selectedPathasala) {
      setVatuList([]);
      setSelectedVatu('');
      setSelectedVatuData(null);
      setGrandhamToShow([]);
      return;
    }

    // Filter vatus by selected pathasala_id from cached data
    const vatus = allVatusData
      .filter(v => v.pathasala_id === selectedPathasala)
      .map(v => ({
        vatu_id: v.vatu_id,
        fullName: v.vatu_name.trim(),
        fullData: v
      }))
      .filter(v => v.fullName !== '')
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
    
    setVatuList(vatus);
    setSelectedVatu('');
    setSelectedVatuData(null);
    setGrandhamToShow([]);
  }, [selectedPathasala, allVatusData]);

  // Handle vatu selection and calculate grandham to show
  useEffect(() => {
    const loadVatuAndResults = async () => {
      if (selectedVatu && teluguYearTelugu && selectedPathasala) {
        const vatu = vatuList.find(v => v.vatu_id === selectedVatu);
        if (vatu && vatu.fullData) {
          // Reset recording, audio states, and hasStudentStopped when a new vatu is selected
          setRecordingState({});
          setAudioBlobs({});
          setHasStudentStopped(false);
          
          setSelectedVatuData(vatu.fullData);
          
          // ============================================================================
          // ATHYUTHAMA MODE - SKIP NORMAL PARIKSHA TYPE LOGIC
          // ============================================================================
          if (isAthyuthamaMode && availableParikshaadhikarisList.length > 0) {
            console.log('Athyuthama mode active - bypassing normal pariksha type logic');
            
            // Set a dummy pariksha type so the UI shows the grandham section
            setSelectedParikshaType('athyuthama_pariksha');
            setAvailableParikshaTypes(['athyuthama_pariksha']);
            
            // Skip feedback section for athyuthama
            setBatchParikshaadhikaris([]);
            
            // Create athyuthama grandham structure with parikshaadhikari dropdowns
            const athyuthamaSectionName = lang === 'telugu' ? 'అత్యుత్తమ పరీక్ష' : 'अत्युत्तम परीक्षा';
            
            const athyuthamaGrandhamToShow = {
              previous: [],
              current: [{
                section: athyuthamaSectionName,
                data: {
                  '_athyuthama_ratings': availableParikshaadhikarisList.map(p => ({
                    parikshaadhikari_id: p._id,
                    parikshaadhikari_name: lang === 'telugu' ? p.telugu : p.sanskrit,
                    parikshaadhikari_telugu: p.telugu,
                    parikshaadhikari_sanskrit: p.sanskrit
                  }))
                },
                isPrevious: false,
                isAthyuthama: true,
                totalParikshaadhikaris: availableParikshaadhikarisList.length
              }],
              isAthyuthama: true
            };
            
            console.log('Athyuthama grandhamToShow:', athyuthamaGrandhamToShow);
            
            // Fetch existing athyuthama result for this vatu
            try {
              const res = await api.get(
                `/api/result/by-year-pathasala-vatu?year_id=${encodeURIComponent(yearId)}&pathasala_id=${encodeURIComponent(selectedPathasala)}&vatu_id=${encodeURIComponent(vatu.vatu_id)}&pariksha_type=athyuthama_pariksha`,
                {
                  headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                  }
                }
              );
              const data = await res.json();
              
              if (data.success && data.data) {
                // Load saved ratings
                const savedResult = data.data;
                console.log('Loaded existing athyuthama result:', savedResult);
                
                // Initialize ratings from saved data
                // The key must match athyuthamaGrandhamToShow.current structure (always at index 0)
                const initialRatings = {};
                const athyuthamaSection = athyuthamaGrandhamToShow.current[0];
                const expectedKey = `curr_${athyuthamaSection.section}_0`;
                
                // Try to load from pariksha_result first (auto-saved data)
                let foundRatings = false;
                if (savedResult.pariksha_result && Array.isArray(savedResult.pariksha_result)) {
                  for (const item of savedResult.pariksha_result) {
                    if (item.isAthyuthama && item.rating && item.rating._athyuthama_ratings) {
                      // Use the expected key that matches athyuthamaGrandhamToShow
                      initialRatings[expectedKey] = item.rating;
                      foundRatings = true;
                      console.log('Loaded athyuthama ratings from pariksha_result:', expectedKey, item.rating);
                      break; // Only one athyuthama section expected
                    }
                  }
                }
                
                // If no ratings found in pariksha_result, try grandham_to_show
                if (!foundRatings && savedResult.grandham_to_show && Array.isArray(savedResult.grandham_to_show)) {
                  for (const item of savedResult.grandham_to_show) {
                    if (item.isAthyuthama && item.rating) {
                      initialRatings[expectedKey] = item.rating;
                      foundRatings = true;
                      console.log('Loaded athyuthama ratings from grandham_to_show:', expectedKey, item.rating);
                      break;
                    }
                  }
                }
                
                // If still no ratings, initialize empty structure
                if (!foundRatings) {
                  initialRatings[expectedKey] = {
                    '_athyuthama_ratings': {}
                  };
                }
                
                console.log('Final initialRatings for athyuthama:', initialRatings);
                setPannamRatings(initialRatings);
                setGrandhamToShow(athyuthamaGrandhamToShow);
              } else {
                // No saved data - initialize empty ratings
                const initialRatings = {};
                athyuthamaGrandhamToShow.current.forEach((item, idx) => {
                  if (item.isAthyuthama && item.data._athyuthama_ratings) {
                    const key = `curr_${item.section}_${idx}`;
                    initialRatings[key] = {
                      '_athyuthama_ratings': {}
                    };
                  }
                });
                
                setPannamRatings(initialRatings);
                setGrandhamToShow(athyuthamaGrandhamToShow);
              }
            } catch (err) {
              console.error('Error fetching athyuthama result:', err);
              // Initialize empty ratings on error
              const initialRatings = {};
              athyuthamaGrandhamToShow.current.forEach((item, idx) => {
                if (item.isAthyuthama && item.data._athyuthama_ratings) {
                  const key = `curr_${item.section}_${idx}`;
                  initialRatings[key] = {
                    '_athyuthama_ratings': {}
                  };
                }
              });
              
              setPannamRatings(initialRatings);
              setGrandhamToShow(athyuthamaGrandhamToShow);
            }
            
            return; // Exit early - athyuthama mode is fully handled
          }
          
          // ============================================================================
          // NORMAL MODE - PARIKSHA TYPE INTERSECTION LOGIC
          // Compute intersection of parikshaadhikari's pariksha types and vatu's pariksha types
          // ============================================================================
          const parikshaadhikariTypes = parikshaadhikariInfo?.pariksha_types || [];
          const vatuTypes = Array.isArray(vatu.fullData.pariksha_type) 
            ? vatu.fullData.pariksha_type 
            : [vatu.fullData.pariksha_type].filter(Boolean);
          
          // Compute intersection
          const intersection = parikshaadhikariTypes.filter(type => vatuTypes.includes(type));
          setAvailableParikshaTypes(intersection);
          
          // Auto-select if only one type in intersection, otherwise wait for user selection
          let effectiveParikshaType = '';
          if (intersection.length === 1) {
            effectiveParikshaType = intersection[0];
            setSelectedParikshaType(intersection[0]);
          } else if (intersection.length > 1) {
            // If selectedParikshaType is already valid in intersection, keep it
            if (selectedParikshaType && intersection.includes(selectedParikshaType)) {
              effectiveParikshaType = selectedParikshaType;
            } else {
              // Reset - user needs to select
              setSelectedParikshaType('');
                setGrandhamToShow({ previous: [], current: [] });
                setPannamRatings({});
                setMarksData({});
              }
          } else if (intersection.length === 0) {
            // No intersection - set to vatu's pariksha_type
            const vatuParikshaType = Array.isArray(vatuTypes) && vatuTypes.length > 0 ? vatuTypes[0] : '';
            setSelectedParikshaType(vatuParikshaType);
            effectiveParikshaType = vatuParikshaType;
          }
          
          // If we don't have an effective pariksha type yet, wait for user selection
          if (!effectiveParikshaType) {
            return;
          }
          
          // Fetch batch parikshaadhikaris for feedback section
          // Find the correct batch based on the selected pariksha type
          const batchAssignments = vatu.fullData.batch_assignments || [];
          const batchIds = vatu.fullData.batch_ids || (vatu.fullData.batch_id ? [vatu.fullData.batch_id] : []);
          
          // Find batch_id matching the effective pariksha type
          let batchIdForFeedback = null;
          if (batchAssignments.length > 0) {
            const matchingAssignment = batchAssignments.find(ba => ba.pariksha_type === effectiveParikshaType);
            batchIdForFeedback = matchingAssignment?.batch_id;
          }
          
          // Fallback to first batch_id if no match found (for backward compatibility)
          if (!batchIdForFeedback && batchIds.length > 0) {
            batchIdForFeedback = batchIds[0];
          }
          
          if (batchIdForFeedback) {
            try {
              const parikshaadhikariRes = await api.get(`/api/pariksha-batch/parikshaadhikaris/${batchIdForFeedback}`);
              const parikshaadhikariData = await parikshaadhikariRes.json();
              
              if (parikshaadhikariData.success && parikshaadhikariData.parikshaadhikaris) {
                setBatchParikshaadhikaris(parikshaadhikariData.parikshaadhikaris);
              } else {
                setBatchParikshaadhikaris([]);
              }
            } catch (err) {
              setBatchParikshaadhikaris([]);
            }
          }

          // Fetch existing feedback recordings for this vatu
          try {
            if (effectiveParikshaType) {
              const feedbackRes = await api.get(
                `/api/result/feedback-recordings?telugu_year=${encodeURIComponent(teluguYearTelugu)}&vatu_id=${encodeURIComponent(vatu.vatu_id)}&pariksha_type=${encodeURIComponent(effectiveParikshaType)}`,
              );
              const feedbackData = await feedbackRes.json();

              if (
                feedbackData.success &&
                feedbackData.data &&
                Array.isArray(feedbackData.data)
              ) {
                // Load audio from MinIO paths and set to audioBlobs state
                const loadedAudioBlobs = {};

                for (const feedback of feedbackData.data) {
                  const parikshaadhikariId = feedback.parikshaadhikari_id;

                  // Load positive feedback if path exists
                  if (feedback.positive_feedback_path) {
                    try {
                      const positiveRes = await api.get(
                        `/api/result/file/${encodeURIComponent(feedback.positive_feedback_path)}`,
                      );
                      const positiveBlob = await positiveRes.blob();
                      loadedAudioBlobs[`${parikshaadhikariId}_positive`] =
                        positiveBlob;
                    } catch (err) {}
                  }

                  // Load improvement feedback if path exists
                  if (feedback.improvement_feedback_path) {
                    try {
                      const improvementRes = await api.get(
                        `/api/result/file/${encodeURIComponent(feedback.improvement_feedback_path)}`,
                      );
                      const improvementBlob = await improvementRes.blob();
                      loadedAudioBlobs[`${parikshaadhikariId}_improvement`] =
                        improvementBlob;
                    } catch (err) {}
                  }
                }

                // Set the loaded blobs to state
                if (Object.keys(loadedAudioBlobs).length > 0) {
                  setAudioBlobs(loadedAudioBlobs);
                }
              }
            }
          } catch (err) {
            // Don't prevent other operations from continuing if feedback load fails
          }
          
          // Extract and convert grandham data from new nested format to flat format
          const previousRaw = {
            krishna_yajurveda: getGrandhamTypePhase(vatu.fullData, 'krishna_yajurveda', 'previous')
          };
          const currentRaw = {
            krishna_yajurveda: getGrandhamTypePhase(vatu.fullData, 'krishna_yajurveda', 'current')
          };
          
          // ============================================================================
          // PARIKSHA TYPE SPECIFIC PROCESSING
          // Different logic for Krishna Yajurveda vs other pariksha types
          // ============================================================================
          
          let calculatedToShow = null;
          
          if (isKrishnaYajurveda(effectiveParikshaType)) {
            // ========== KRISHNA YAJURVEDA PROCESSING ==========
            // Convert krishna_yajurveda from new nested format to flat array format
            const previous = extractAndConvertGrandhamData(previousRaw);
            const current = extractAndConvertGrandhamData(currentRaw);
            
            
            // Apply Jata/Ghanamu cloning rules using baseline from grandhamTreeData
            const jata = lang === 'telugu' ? 'జటా' : 'जटा';
            const ghanam = lang === 'telugu' ? 'ఘనము' : 'घनम्';
            const kramamu = lang === 'telugu' ? 'క్రమము' : 'क्रमम्';

            // Use baseline kandas from grandhamTreeData (ensure language key exists)
            const baseline = grandhamTreeData[lang === 'telugu' ? 'telugu' : 'sanskrit'] || {};
            const baselineKramamu = baseline[kramamu] || {};

            // Helper to deep-clone the baseline kramamu structure into target section
            const cloneBaselineKramamuToSection = (targetSection) => {
              const newSection = {};
              Object.keys(baselineKramamu).forEach((kanda) => {
                const pannams = baselineKramamu[kanda] || [];
                if (Array.isArray(pannams) && pannams.length > 0) {
                  // Ensure pannam values are numbers where possible
                  newSection[kanda] = pannams.map(p => isNaN(Number(p)) ? p : Number(p));
                }
              });
              // Only assign if we have data
              if (Object.keys(newSection).length > 0) {
                current[targetSection] = newSection;
              }
            };

            // Rule 1: If Jata is in current and Ghanamu is NOT in current, populate Jata from baseline Kramamu
            if (current[jata] && !current[ghanam]) {
              cloneBaselineKramamuToSection(jata);
            }

            // Rule 2: If Ghanamu is in current and Ghanamu is NOT in previous, populate Ghanamu from baseline Kramamu
            if (current[ghanam] && !previous[ghanam]) {
              cloneBaselineKramamuToSection(ghanam);
            }

            
            // ============================================================================
            // SPECIAL KRISHNA YAJURVEDA KRAMANTHAM RULES
            // Apply if vatu has krishnaYajurvedaKramantham set
            // ============================================================================
            const krishnaYajurvedaKramantham = vatu.fullData.krishnaYajurvedaKramantham;
            
            
            // Helper function to create special rating dropdowns structure for Krishna Yajurveda cases
            const createKrishnaRatingStructure = (numDropdowns, structureToShow) => {
              // Create pannams structure with all values from 1 to numDropdowns
              const pannams = {};
              for (let i = 1; i <= numDropdowns; i++) {
                pannams[i.toString()] = 'ఉత్తీర్ణత'; // Default rating value (pass)
              }
              return pannams;
            };
          
          // Helper to calculate the category based on count of failures (అనుత్తీర్ణత) for special Krishna cases
          const calculateSpecialKrishnaCategory = (failureCount) => {
            if (failureCount <= 6) return lang === 'telugu' ? 'అత్యుత్తమం' : 'अत्युत्तमम्';
            if (failureCount <= 8) return lang === 'telugu' ? 'ఉత్తమం' : 'उत्तमम्';
            if (failureCount <= 10) return lang === 'telugu' ? 'సాధారణం' : 'मध्यमम्';
            return lang === 'telugu' ? 'అనుత్తీర్ణుడయ్యాడు' : 'अधमम्';
          };
          
          // Helper to get headers based on language
          const getSpecialHeaders = (type) => {
            const headers = {
              vishesha_kramamu: {
                telugu: 'విశేష క్రమాంతం',
                sanskrit: 'विशेष क्रमन्थम्'
              },
              vishesha_ghanamu: {
                telugu: 'విశేష ఘనం',
                sanskrit: 'विशेष घनम्'
              },
              saamaanya_kramamu: {
                telugu: 'సామాన్య క్రమాంతం',
                sanskrit: 'सामान्य क्रमन्थम्'
              },
              saamaanya_ghanamu: {
                telugu: 'సామాన్య ఘనము',
                sanskrit: 'सामान्य घनम्'
              }
            };
            return headers[type]?.[lang] || type;
          };
          
          // Helper to check whether previous selections are empty.
          const isLastGrandhaEmpty = () => {
            if (!previousRaw || Object.keys(previousRaw).length === 0) return true;
            
            const nothingMarkers = [
              'ఏమీ ఇవ్వలేదు',
              'किमपि नोक्तम्'
            ];
            
            // Get the krishna_yajurveda object if it exists, otherwise use previousRaw directly
            const dataSource = previousRaw.krishna_yajurveda || previousRaw;
            
            // Check if data source is empty or only contains nothing markers
            for (const key of Object.keys(dataSource)) {
              // Skip metadata keys and nothing markers
              if (key.startsWith('_') || nothingMarkers.includes(key)) continue;
              
              const value = dataSource[key];
              if (value && typeof value === 'object') {
                // Check if this key has actual content (non-metadata keys)
                const nonMetadataKeys = Object.keys(value).filter(k => !k.startsWith('_'));
                if (nonMetadataKeys.length > 0) {
                  return false; // Found content, not empty
                }
              }
            }
            return true; // No content found, it's empty
          };
          
          // Helper to check if current grandhams have specific sections with actual content
          const hasCurrentSections = (sectionNames) => {
            // Get the krishna_yajurveda object if it exists, otherwise use currentRaw directly
            const dataSource = currentRaw.krishna_yajurveda || currentRaw;
            
            return sectionNames.every(sectionName => {
              const section = dataSource && dataSource[sectionName];
              if (!section || typeof section !== 'object') return false;
              return section._selected === true;
            });
          };

          const isFullKramanthamOrGhanantham = () => {
            // RULE 3: saamaanya_kramamu - Check if current has brahmanam+aranyakam+kramamu and last_grandha is empty
            // RULE 4: saamaanya_ghanamu - Check if current has all sections including ghana and last_grandha is empty
            const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
            const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
            const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
            const padamu = lang === 'telugu' ? 'పదము' : 'पदम्';
            const kramamu = lang === 'telugu' ? 'క్రమము' : 'क्रमम्';
            const jata = lang === 'telugu' ? 'జటా' : 'जटा';
            const ghanam = lang === 'telugu' ? 'ఘనము' : 'घनम्';
            return hasCurrentSections([brahmanam, aranyakam, samhita, padamu, kramamu, jata, ghanam]) && isLastGrandhaEmpty() ? 'ghanantham' : 
            (hasCurrentSections([brahmanam, aranyakam, samhita, padamu, kramamu]) && isLastGrandhaEmpty() ? 'kramantham' : '');
          }
          
          if (krishnaYajurvedaKramantham === 'vishesha_kramamu') {
            // RULE 1: visheshakramantham - Show 100 rating dropdowns
            const pannams = createKrishnaRatingStructure(100, null);
            calculatedToShow = {
              previous: [],
              current: [{
                section: getSpecialHeaders('vishesha_kramamu'),
                data: {
                  '_special_kanda': Object.keys(pannams)
                },
                isPrevious: false,
                isSpecialKrishna: true,
                totalDropdowns: 100
              }]
            };
          }else if (krishnaYajurvedaKramantham === 'vishesha_ghanamu') {
            // RULE 2: vishesha_ghanamu - Show 50 rating dropdowns
            const pannams = createKrishnaRatingStructure(50, null);
            calculatedToShow = {
              previous: [],
              current: [{
                section: getSpecialHeaders('vishesha_ghanamu'),
                data: {
                  '_special_kanda': Object.keys(pannams)
                },
                isPrevious: false,
                isSpecialKrishna: true,
                totalDropdowns: 50
              }]
            };
          }else if (krishnaYajurvedaKramantham === 'saamaanya_kramamu' || isFullKramanthamOrGhanantham() === 'kramantham') {
            // Show all brahmanam, aranyakam and kramamu full pannams (skip samhita, padamu)
            // Keep as individual accordions but mark with special rule
            calculatedToShow = {
              previous: [],
              current: [],
              specialRule: 'saamaanya_kramamu'
            };
            
            // Add brahmanam
            if (current[brahmanam]) {
              calculatedToShow.current.push({
                section: brahmanam,
                data: current[brahmanam],
                isPrevious: false,
                isSpecialRule: 'saamaanya_kramamu'
              });
            }
            
            // Add aranyakam
            if (current[aranyakam]) {
              calculatedToShow.current.push({
                section: aranyakam,
                data: current[aranyakam],
                isPrevious: false,
                isSpecialRule: 'saamaanya_kramamu'
              });
            }
            
            // Add kramamu
            if (current[kramamu]) {
              calculatedToShow.current.push({
                section: kramamu,
                data: current[kramamu],
                isPrevious: false,
                isSpecialRule: 'saamaanya_kramamu'
              });
            }
          }else if (krishnaYajurvedaKramantham === 'saamaanya_ghanamu' || isFullKramanthamOrGhanantham() === 'ghanantham') {
            // Show only ghanam with all its pannams (44 total from all kandas)
            // Keep as individual accordion but mark with special rule
            calculatedToShow = {
              previous: [],
              current: [],
              specialRule: 'saamaanya_ghanamu'
            };
            
            if (current[ghanam]) {
              calculatedToShow.current.push({
                section: ghanam,
                data: current[ghanam],
                isPrevious: false,
                isSpecialRule: 'saamaanya_ghanamu'
              });
            }
          }
          
          // Use special rules if applicable, otherwise use standard calculateGrandhamToShow
            if (!calculatedToShow) {
              calculatedToShow = calculateGrandhamToShow(previous, current);
            }
          } else {
            // ========== NON-KRISHNA PARIKSHA TYPE PROCESSING ==========
            // For sanskrit, vedadhyayi_sahityam, lakshanam, etc.
            calculatedToShow = calculateNonKrishnaGrandhamToShow(previousRaw, currentRaw, effectiveParikshaType);
          }
          
          // ========== ATHYUTHAMA MODE OVERRIDE ==========
          // If athyuthama mode is active, override the grandham display with parikshaadhikari rating dropdowns
          if (isAthyuthamaMode && availableParikshaadhikarisList.length > 0) {
            console.log('Athyuthama mode active - generating parikshaadhikari rating dropdowns');
            
            // Check vatu's attendance_date for today
            const today = new Date().toLocaleDateString('en-CA');
            const vatuAttendanceDates = vatu.fullData.attendance_dates || [];
            const hasAttendanceToday = vatuAttendanceDates.some(date => {
              const dateStr = new Date(date).toLocaleDateString('en-CA');
              return dateStr === today;
            });
            
            if (!hasAttendanceToday) {
              console.log('Vatu does not have attendance for today - skipping athyuthama');
              // Continue with normal flow if no attendance
            } else {
              // Create athyuthama grandham structure with parikshaadhikari dropdowns
              const athyuthamaSectionName = lang === 'telugu' ? 'అత్యుత్తమ పరీక్ష' : 'अत्युत्तम परीक्षा';
              
              calculatedToShow = {
                previous: [],
                current: [{
                  section: athyuthamaSectionName,
                  data: {
                    '_athyuthama_ratings': availableParikshaadhikarisList.map(p => ({
                      parikshaadhikari_id: p._id,
                      parikshaadhikari_name: lang === 'telugu' ? p.telugu : p.sanskrit,
                      parikshaadhikari_telugu: p.telugu,
                      parikshaadhikari_sanskrit: p.sanskrit
                    }))
                  },
                  isPrevious: false,
                  isAthyuthama: true,
                  totalParikshaadhikaris: availableParikshaadhikarisList.length
                }],
                isAthyuthama: true
              };
              
              console.log('Athyuthama calculatedToShow:', calculatedToShow);
            }
          }
          
          // Fetch existing result for this year/pathasala/vatu
          // For athyuthama mode, use 'athyuthama_pariksha' as pariksha_type
          const resultParikshaType = (isAthyuthamaMode && calculatedToShow?.isAthyuthama) 
            ? 'athyuthama_pariksha' 
            : effectiveParikshaType;
          
          try {
            const res = await api.get(
              `/api/result/by-year-pathasala-vatu?year_id=${encodeURIComponent(yearId)}&pathasala_id=${encodeURIComponent(selectedPathasala)}&vatu_id=${encodeURIComponent(vatu.vatu_id)}&pariksha_type=${encodeURIComponent(resultParikshaType)}`,
              {
                headers: {
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  'Pragma': 'no-cache',
                  'Expires': '0'
                }
              }
            );
            const data = await res.json();
            
            
            if (data.success && data.data) {
              const savedResult = data.data;
              
              // Check if vatu grandham data has changed
              const hasGrandhamDataChanged = () => {
                if (!savedResult.grandham_to_show || !Array.isArray(savedResult.grandham_to_show)) {
                  return true; // No saved data, consider as changed
                }
                
                const savedPrevious = savedResult.grandham_to_show.filter(item => item.isPrevious);
                const savedCurrent = savedResult.grandham_to_show.filter(item => !item.isPrevious);
                
                // Compare structure: sections and pannams
                const compareSections = (arr1, arr2) => {
                  if (arr1.length !== arr2.length) return true;
                  
                  for (let i = 0; i < arr1.length; i++) {
                    const item1 = arr1[i];
                    const item2 = arr2[i];
                    
                    if (item1.section !== item2.section) return true;
                    
                    // Compare data structure (kandas and pannams)
                    const data1Keys = Object.keys(item1.data || {}).sort();
                    const data2Keys = Object.keys(item2.data || {}).sort();
                    
                    if (data1Keys.length !== data2Keys.length) return true;
                    if (data1Keys.join(',') !== data2Keys.join(',')) return true;
                    
                    // Compare pannams in each kanda
                    for (const kanda of data1Keys) {
                      const pannams1 = (item1.data[kanda] || []).sort();
                      const pannams2 = (item2.data[kanda] || []).sort();
                      if (pannams1.join(',') !== pannams2.join(',')) return true;
                    }
                  }
                  return false;
                };
                
                return compareSections(calculatedToShow.previous, savedPrevious) || 
                       compareSections(calculatedToShow.current, savedCurrent);
              };
              
              const dataChanged = hasGrandhamDataChanged();
              
              const initialMarks = {};
              const initialRatings = {};
              
              if (dataChanged) {
                // Vatu data changed - use calculated structure but retain existing ratings for matching pannams
                
                // Helper to extract existing ratings by section/kanda/pannam from grandham_to_show
                const extractSavedRatings = (savedItems) => {
                  const ratingsMap = {};
                  savedItems.forEach(item => {
                    if (!ratingsMap[item.section]) ratingsMap[item.section] = {};
                    
                    if (item.rating && typeof item.rating === 'object') {
                      Object.keys(item.rating).forEach(kanda => {
                        const kandaValue = item.rating[kanda];
                        
                        // Check if this is a non-Krishna flat format: kanda -> value (string)
                        if (typeof kandaValue === 'string') {
                          // Non-Krishna flat format - store directly
                          ratingsMap[item.section][kanda] = kandaValue;
                        } else if (typeof kandaValue === 'object') {
                          // Krishna nested format: kanda -> {pannam -> value}
                          if (!ratingsMap[item.section][kanda]) ratingsMap[item.section][kanda] = {};
                          Object.keys(kandaValue).forEach(pannam => {
                            ratingsMap[item.section][kanda][pannam] = kandaValue[pannam];
                          });
                        }
                      });
                    }
                  });
                  return ratingsMap;
                };

                // Helper to extract ratings from pariksha_result array with isPrevious differentiation
                // For Non-Krishna types: Filter by isPrevious flag
                // For Krishna types: isPrevious flag is not present, so treat all as current (isPrevious=false)
                const extractRatingsFromParikshaResult = (parikshaResults, isPrevious, isNonKrishna) => {
                  const ratingsMap = {};
                  if (Array.isArray(parikshaResults)) {
                    parikshaResults.forEach(item => {
                      // Check if item has isPrevious flag
                      // If it exists, filter by it
                      // If it doesn't exist, treat as current (isPrevious=false)
                      if (item.hasOwnProperty('isPrevious')) {
                        // Item has isPrevious flag - filter by it
                        if (item.isPrevious !== isPrevious) return;
                      } else {
                        // Item doesn't have isPrevious flag - treat as current (isPrevious=false)
                        if (isPrevious !== false) return;
                      }
                      
                      if (item.section && item.rating && typeof item.rating === 'object') {
                        if (!ratingsMap[item.section]) ratingsMap[item.section] = {};
                        Object.keys(item.rating).forEach(kanda => {
                          const kandaValue = item.rating[kanda];
                          
                          // Check if this is a non-Krishna flat format: kanda -> value (string)
                          if (typeof kandaValue === 'string') {
                            // Non-Krishna flat format
                            ratingsMap[item.section][kanda] = kandaValue;
                          } else if (typeof kandaValue === 'object') {
                            // Krishna nested format: kanda -> {pannam -> value}
                            if (!ratingsMap[item.section][kanda]) ratingsMap[item.section][kanda] = {};
                            Object.keys(kandaValue).forEach(pannam => {
                              ratingsMap[item.section][kanda][pannam] = kandaValue[pannam];
                            });
                          }
                        });
                      }
                    });
                  }
                  return ratingsMap;
                };
                
                const savedPrevious = (savedResult.grandham_to_show || []).filter(item => item.isPrevious);
                const savedCurrent = (savedResult.grandham_to_show || []).filter(item => !item.isPrevious);
                
                const savedPreviousRatings = extractSavedRatings(savedPrevious);
                const savedCurrentRatings = extractSavedRatings(savedCurrent);
                
                // Extract ratings from pariksha_result (new format) - SEPARATE for previous and current with isPrevious differentiation
                // Only for Non-Krishna types - Krishna types don't have isPrevious flag
                const isNonKrishnaType = !isKrishnaYajurveda(effectiveParikshaType);
                const parikshaResultPreviousRatings = extractRatingsFromParikshaResult(savedResult.pariksha_result, true, isNonKrishnaType);
                const parikshaResultCurrentRatings = extractRatingsFromParikshaResult(savedResult.pariksha_result, false, isNonKrishnaType);
                
                // Merge pariksha_result ratings with grandham_to_show ratings (pariksha_result takes precedence)
                // Need deep merge: merge item-by-item within each section, not just section-level
                const mergedPreviousRatings = {};
                const mergedCurrentRatings = {};
                
                // First copy all saved ratings
                Object.keys(savedPreviousRatings).forEach(section => {
                  mergedPreviousRatings[section] = { ...savedPreviousRatings[section] };
                });
                Object.keys(savedCurrentRatings).forEach(section => {
                  mergedCurrentRatings[section] = { ...savedCurrentRatings[section] };
                });
                
                // Then overlay pariksha_result ratings (takes precedence) - ONLY for matching isPrevious
                Object.keys(parikshaResultPreviousRatings).forEach(section => {
                  if (!mergedPreviousRatings[section]) mergedPreviousRatings[section] = {};
                  
                  Object.keys(parikshaResultPreviousRatings[section]).forEach(item => {
                    mergedPreviousRatings[section][item] = parikshaResultPreviousRatings[section][item];
                  });
                });
                
                Object.keys(parikshaResultCurrentRatings).forEach(section => {
                  if (!mergedCurrentRatings[section]) mergedCurrentRatings[section] = {};
                  
                  Object.keys(parikshaResultCurrentRatings[section]).forEach(item => {
                    mergedCurrentRatings[section][item] = parikshaResultCurrentRatings[section][item];
                  });
                });
                
                // Process previous items with calculated structure
                calculatedToShow.previous.forEach((item, idx) => {
                  const key = `prev_${item.section}_${idx}`;
                  initialMarks[key] = 0;
                  initialRatings[key] = {};
                  
                  // Special handling for non-Krishna pariksha types (previous items show checkboxes)
                  if (item.isNonKrishna) {
                    if (item.data._items) {
                      // Has sub-items
                      item.data._items.forEach(subItem => {
                        // Get rating directly from merged ratings (flat format for non-Krishna)
                        initialRatings[key][subItem] = mergedPreviousRatings[item.section]?.[subItem] || '';
                      });
                    } else if (item.data._single) {
                      // Single grandham
                      // Get rating directly from merged ratings (flat format for non-Krishna)
                      initialRatings[key]['_completed'] = mergedPreviousRatings[item.section]?.['_completed'] || '';
                    }
                  } else {
                    // Krishna Yajurveda - Retain ratings for matching pannams (use merged ratings with pariksha_result taking precedence)
                    if (mergedPreviousRatings[item.section]) {
                      Object.keys(item.data || {}).forEach(kanda => {
                        if (mergedPreviousRatings[item.section][kanda]) {
                          initialRatings[key][kanda] = {};
                          (item.data[kanda] || []).forEach(pannam => {
                            if (mergedPreviousRatings[item.section][kanda][pannam]) {
                              initialRatings[key][kanda][pannam] = mergedPreviousRatings[item.section][kanda][pannam];
                            }
                          });
                        }
                      });
                    }
                  }
                });
                
                // Process current items with calculated structure
                calculatedToShow.current.forEach((item, idx) => {
                  const key = `curr_${item.section}_${idx}`;
                  initialMarks[key] = 0;
                  
                  // Special handling for non-Krishna pariksha types
                  if (item.isNonKrishna) {
                    initialRatings[key] = {};
                    
                    if (item.data._items) {
                      // Has sub-items
                      item.data._items.forEach(subItem => {
                        // Get rating from mergedCurrentRatings[section][subItem] (flat format for non-Krishna)
                        initialRatings[key][subItem] = mergedCurrentRatings[item.section]?.[subItem] || '';
                      });
                    } else if (item.data._single) {
                      // Single grandham
                      initialRatings[key]['_single'] = mergedCurrentRatings[item.section]?.['_single'] || '';
                    }
                  }
                  // Special handling for Krishna Yajurveda rating dropdowns
                  else if (item.isSpecialKrishna && item.totalDropdowns) {
                    initialRatings[key] = {
                      '_special_kanda': {}
                    };
                    
                    // Initialize all dropdowns and load saved ratings if available
                    for (let i = 1; i <= item.totalDropdowns; i++) {
                      // Try to find saved rating for this pannam from merged ratings
                      let savedRating = '';
                      Object.keys(mergedCurrentRatings).forEach(sectionName => {
                        if (mergedCurrentRatings[sectionName]?.['_special_kanda']?.[i]) {
                          savedRating = mergedCurrentRatings[sectionName]['_special_kanda'][i];
                        }
                      });
                      initialRatings[key]['_special_kanda'][i] = savedRating;
                    }
                  } else if (item.isAthyuthama && item.data._athyuthama_ratings) {
                    // Special handling for athyuthama exam - load saved ratings for each parikshaadhikari
                    initialRatings[key] = {
                      '_athyuthama_ratings': {}
                    };
                    
                    item.data._athyuthama_ratings.forEach(p => {
                      // Try to find saved rating for this parikshaadhikari from merged ratings
                      let savedRating = '';
                      Object.keys(mergedCurrentRatings).forEach(sectionName => {
                        if (mergedCurrentRatings[sectionName]?.['_athyuthama_ratings']?.[p.parikshaadhikari_id]) {
                          savedRating = mergedCurrentRatings[sectionName]['_athyuthama_ratings'][p.parikshaadhikari_id];
                        }
                      });
                      initialRatings[key]['_athyuthama_ratings'][p.parikshaadhikari_id] = savedRating;
                    });
                  } else if (item.isSpecialRule) {
                    // Special handling for saamaanya_kramamu and saamaanya_ghanamu (pass/fail ratings)
                    initialRatings[key] = {};
                    
                    Object.keys(item.data || {}).forEach(kanda => {
                      initialRatings[key][kanda] = {};
                      (item.data[kanda] || []).forEach(pannam => {
                        // Try to find matching ratings from merged ratings
                        let foundRating = null;
                        Object.keys(mergedCurrentRatings).forEach(sectionName => {
                          if (mergedCurrentRatings[sectionName]?.[kanda]?.[pannam]) {
                            foundRating = mergedCurrentRatings[sectionName][kanda][pannam];
                          }
                        });
                        initialRatings[key][kanda][pannam] = foundRating || '';
                      });
                    });
                  } else {
                    initialRatings[key] = {};
                    
                    // Retain ratings for matching pannams (use merged ratings with pariksha_result taking precedence)
                    // For saamaanya_kramamu and saamaanya_ghanamu, the section name is merged but kandas are preserved
                    Object.keys(item.data || {}).forEach(kanda => {
                      initialRatings[key][kanda] = {};
                      (item.data[kanda] || []).forEach(pannam => {
                        // Try to find matching ratings from all possible sections
                        let foundRating = null;
                        
                        // First check if merged ratings have this kanda/pannam
                        Object.keys(mergedCurrentRatings).forEach(sectionName => {
                          if (mergedCurrentRatings[sectionName]?.[kanda]?.[pannam]) {
                            foundRating = mergedCurrentRatings[sectionName][kanda][pannam];
                          }
                        });
                        
                        if (foundRating) {
                          initialRatings[key][kanda][pannam] = foundRating;
                        }
                      });
                    });
                  }
                });
                
                // Also retain marks if saved data exists for current grandham
                if (savedCurrent.length > 0 && savedCurrent[0].marks) {
                  calculatedToShow.current.forEach((item, idx) => {
                    const key = `curr_${item.section}_${idx}`;
                    // Find matching saved item by section
                    const matchingSaved = savedCurrent.find(saved => saved.section === item.section);
                    if (matchingSaved && matchingSaved.marks) {
                      initialMarks[key] = matchingSaved.marks;
                    }
                  });
                }
                
                setGrandhamToShow(calculatedToShow);
                
              } else {
                // No change - use saved data as-is
                
                const previousItems = savedResult.grandham_to_show.filter(item => item.isPrevious);
                const currentItems = savedResult.grandham_to_show.filter(item => !item.isPrevious);
                
                setGrandhamToShow({
                  previous: previousItems,
                  current: currentItems
                });
                
                // Extract ratings from pariksha_result (new format) - SEPARATE for previous and current with isPrevious differentiation
                // For Non-Krishna types: Filter by isPrevious flag
                // For Krishna types: isPrevious flag is not present, so treat all as current (isPrevious=false)
                const extractRatingsFromParikshaResult = (parikshaResults, isPrevious, isNonKrishna) => {
                  const ratingsMap = {};
                  if (Array.isArray(parikshaResults)) {
                    parikshaResults.forEach(item => {
                      // Check if item has isPrevious flag
                      // If it exists, filter by it
                      // If it doesn't exist, treat as current (isPrevious=false)
                      if (item.hasOwnProperty('isPrevious')) {
                        // Item has isPrevious flag - filter by it
                        if (item.isPrevious !== isPrevious) return;
                      } else {
                        // Item doesn't have isPrevious flag - treat as current (isPrevious=false)
                        if (isPrevious !== false) return;
                      }
                      
                      if (item.section && item.rating && typeof item.rating === 'object') {
                        if (!ratingsMap[item.section]) ratingsMap[item.section] = {};
                        Object.keys(item.rating).forEach(kanda => {
                          const kandaValue = item.rating[kanda];
                          
                          // Check if this is a non-Krishna flat format: kanda -> value (string)
                          if (typeof kandaValue === 'string') {
                            // Non-Krishna flat format
                            ratingsMap[item.section][kanda] = kandaValue;
                          } else if (typeof kandaValue === 'object') {
                            // Krishna nested format: kanda -> {pannam -> value}
                            if (!ratingsMap[item.section][kanda]) ratingsMap[item.section][kanda] = {};
                            Object.keys(kandaValue).forEach(pannam => {
                              ratingsMap[item.section][kanda][pannam] = kandaValue[pannam];
                            });
                          }
                        });
                      }
                    });
                  }
                  return ratingsMap;
                };
                
                const isNonKrishnaType = !isKrishnaYajurveda(effectiveParikshaType);
                const parikshaResultPreviousRatings = extractRatingsFromParikshaResult(savedResult.pariksha_result, true, isNonKrishnaType);
                const parikshaResultCurrentRatings = extractRatingsFromParikshaResult(savedResult.pariksha_result, false, isNonKrishnaType);
                
                previousItems.forEach((item, idx) => {
                  const key = `prev_${item.section}_${idx}`;
                  initialMarks[key] = item.marks || 0;
                  
                  // Special handling for non-Krishna pariksha types (previous items show checkboxes)
                  if (item.isNonKrishna) {
                    initialRatings[key] = {};
                    
                    if (item.data._items) {
                      // Has sub-items - ratings are flat in format: item_name -> 'pass'/'fail'
                      item.data._items.forEach(subItem => {
                        initialRatings[key][subItem] = parikshaResultPreviousRatings[item.section]?.[subItem] || '';
                      });
                    } else if (item.data._single) {
                      // Single grandham
                      initialRatings[key]['_completed'] = parikshaResultPreviousRatings[item.section]?.['_completed'] || '';
                    }
                  } else {
                    // Krishna Yajurveda - First priority: Use rating from pariksha_result (new format with proper isPrevious filtering)
                    if (Object.keys(parikshaResultPreviousRatings).length > 0) {
                      // Use ratings from pariksha_result that were filtered by isPrevious=true
                      initialRatings[key] = {};
                      Object.keys(item.data || {}).forEach(kanda => {
                        initialRatings[key][kanda] = {};
                        (item.data[kanda] || []).forEach(pannam => {
                          // Look for ratings in all sections of previous ratings
                          let foundRating = null;
                          Object.keys(parikshaResultPreviousRatings).forEach(sectionName => {
                            if (parikshaResultPreviousRatings[sectionName]?.[kanda]?.[pannam]) {
                              foundRating = parikshaResultPreviousRatings[sectionName][kanda][pannam];
                            }
                          });
                          if (foundRating) {
                            initialRatings[key][kanda][pannam] = foundRating;
                          }
                        });
                      });
                    } else if (item.rating && Object.keys(item.rating).length > 0) {
                      // Second priority: Use rating from grandham_to_show (old format)
                      initialRatings[key] = item.rating;
                    } else if (item.completed && Object.keys(item.completed).length > 0) {
                      // Third priority: Convert completed to rating format (legacy)
                      const convertedRating = {};
                      Object.keys(item.completed).forEach(kanda => {
                        convertedRating[kanda] = {};
                        Object.keys(item.completed[kanda]).forEach(pannam => {
                          convertedRating[kanda][pannam] = item.completed[kanda][pannam] ? 'pass' : 'fail';
                        });
                      });
                      initialRatings[key] = convertedRating;
                    } else {
                      initialRatings[key] = {};
                    }
                  }
                });
                
                currentItems.forEach((item, idx) => {
                  const key = `curr_${item.section}_${idx}`;
                  initialMarks[key] = item.marks || 0;
                  
                  // Special handling for non-Krishna pariksha types
                  if (item.isNonKrishna) {
                    initialRatings[key] = {};
                    
                    if (item.data._items) {
                      // Has sub-items
                      item.data._items.forEach(subItem => {
                        // Get rating directly from pariksha_result ratings (flat format for non-Krishna)
                        initialRatings[key][subItem] = parikshaResultCurrentRatings[item.section]?.[subItem] || '';
                      });
                    } else if (item.data._single) {
                      // Single grandham
                      // Get rating directly from pariksha_result ratings (flat format for non-Krishna)
                      initialRatings[key]['_single'] = parikshaResultCurrentRatings[item.section]?.['_single'] || '';
                    }
                  }
                  // Special handling for Krishna Yajurveda rating dropdowns
                  else if (item.isSpecialKrishna && item.totalDropdowns) {
                    initialRatings[key] = {
                      '_special_kanda': {}
                    };
                    
                    // Load saved ratings for special Krishna cases
                    for (let i = 1; i <= item.totalDropdowns; i++) {
                      // Try to find saved rating for this pannam
                      let savedRating = '';
                      if (parikshaResultCurrentRatings[item.section]?.['_special_kanda']?.[i]) {
                        savedRating = parikshaResultCurrentRatings[item.section]['_special_kanda'][i];
                      } else if (item.rating?.['_special_kanda']?.[i]) {
                        savedRating = item.rating['_special_kanda'][i];
                      }
                      initialRatings[key]['_special_kanda'][i] = savedRating;
                    }
                  } else if (item.isAthyuthama && item.data._athyuthama_ratings) {
                    // Special handling for athyuthama exam - load saved ratings for each parikshaadhikari
                    initialRatings[key] = {
                      '_athyuthama_ratings': {}
                    };
                    
                    item.data._athyuthama_ratings.forEach(p => {
                      // Try to find saved rating for this parikshaadhikari
                      let savedRating = '';
                      if (parikshaResultCurrentRatings[item.section]?.['_athyuthama_ratings']?.[p.parikshaadhikari_id]) {
                        savedRating = parikshaResultCurrentRatings[item.section]['_athyuthama_ratings'][p.parikshaadhikari_id];
                      } else if (item.rating?.['_athyuthama_ratings']?.[p.parikshaadhikari_id]) {
                        savedRating = item.rating['_athyuthama_ratings'][p.parikshaadhikari_id];
                      }
                      initialRatings[key]['_athyuthama_ratings'][p.parikshaadhikari_id] = savedRating;
                    });
                  } else if (item.isSpecialRule) {
                    // Special handling for saamaanya_kramamu and saamaanya_ghanamu (pass/fail ratings)
                    initialRatings[key] = {};
                    
                    Object.keys(item.data || {}).forEach(kanda => {
                      initialRatings[key][kanda] = {};
                      (item.data[kanda] || []).forEach(pannam => {
                        // Try to find saved rating for this pannam
                        let savedRating = '';
                        if (parikshaResultCurrentRatings[item.section]?.[kanda]?.[pannam]) {
                          savedRating = parikshaResultCurrentRatings[item.section][kanda][pannam];
                        } else if (item.rating?.[kanda]?.[pannam]) {
                          savedRating = item.rating[kanda][pannam];
                        }
                        initialRatings[key][kanda][pannam] = savedRating;
                      });
                    });
                  } else {
                    // Krishna Yajurveda - First priority: Use rating from pariksha_result (new format with proper isPrevious filtering)
                    if (Object.keys(parikshaResultCurrentRatings).length > 0) {
                      // Use ratings from pariksha_result that were filtered by isPrevious=false
                      initialRatings[key] = {};
                      Object.keys(item.data || {}).forEach(kanda => {
                        initialRatings[key][kanda] = {};
                        (item.data[kanda] || []).forEach(pannam => {
                          // Look for ratings in all sections of current ratings
                          let foundRating = null;
                          Object.keys(parikshaResultCurrentRatings).forEach(sectionName => {
                            if (parikshaResultCurrentRatings[sectionName]?.[kanda]?.[pannam]) {
                              foundRating = parikshaResultCurrentRatings[sectionName][kanda][pannam];
                            }
                          });
                          if (foundRating) {
                            initialRatings[key][kanda][pannam] = foundRating;
                          }
                        });
                      });
                    } else if (item.rating && Object.keys(item.rating).length > 0) {
                      // Second priority: Use rating from grandham_to_show (old format)
                      initialRatings[key] = item.rating;
                    } else {
                      initialRatings[key] = {};
                    }
                  }
                });
              }
              
              setMarksData(initialMarks);
              setPannamRatings(initialRatings);
              // ========== END NON-KRISHNA RATINGS LOADING ==========

              // Check if already 5+ pannams with <= 5 rating (underperforming condition met)
              const lowRatingCount = Object.keys(initialRatings).filter(itemKey => {
                if (!itemKey.startsWith('curr_')) return false;
                const parts = itemKey.split('_');
                const sectionName = parts[1] || '';
                
                let count = 0;
                Object.keys(initialRatings[itemKey] || {}).forEach(kanda => {
                  Object.keys(initialRatings[itemKey][kanda] || {}).forEach(pannam => {
                    const rating = initialRatings[itemKey][kanda][pannam];
                    const isExcluded = isPannamInPrevious(sectionName, kanda, pannam);
                    if (!isExcluded && rating <= 5) {
                      count++;
                    }
                  });
                });
                return count >= 5;
              }).length > 0;
              
              if (lowRatingCount) {
                setHasStudentStopped(true);
              }
              
            } else {
              // No saved data - use calculated structure
              setGrandhamToShow(calculatedToShow);
              
              const initialMarks = {};
              const initialRatings = {};
              
              calculatedToShow.previous.forEach((item, idx) => {
                const key = `prev_${item.section}_${idx}`;
                initialMarks[key] = 0;
                initialRatings[key] = {};
              });
              
              calculatedToShow.current.forEach((item, idx) => {
                const key = `curr_${item.section}_${idx}`;
                initialMarks[key] = 0;
                
                // Special handling for Krishna Yajurveda rating dropdowns
                if (item.isSpecialKrishna && item.totalDropdowns) {
                  initialRatings[key] = {
                    '_special_kanda': {}
                  };
                  for (let i = 1; i <= item.totalDropdowns; i++) {
                    initialRatings[key]['_special_kanda'][i] = '';
                  }
                } else if (item.isAthyuthama && item.data._athyuthama_ratings) {
                  // Special handling for athyuthama exam - rating dropdowns for each parikshaadhikari
                  initialRatings[key] = {
                    '_athyuthama_ratings': {}
                  };
                  item.data._athyuthama_ratings.forEach(p => {
                    initialRatings[key]['_athyuthama_ratings'][p.parikshaadhikari_id] = '';
                  });
                } else if (item.isSpecialRule) {
                  // Special handling for saamaanya_kramamu and saamaanya_ghanamu (pass/fail ratings)
                  initialRatings[key] = {};
                  Object.keys(item.data || {}).forEach(kanda => {
                    initialRatings[key][kanda] = {};
                    (item.data[kanda] || []).forEach(pannam => {
                      initialRatings[key][kanda][pannam] = '';
                    });
                  });
                } else {
                  initialRatings[key] = {};
                }
              });
              
              setMarksData(initialMarks);
              setPannamRatings(initialRatings);
              
              // Check if already 5+ pannams with <= 5 rating (underperforming condition met)
              const lowRatingCount = Object.keys(initialRatings).filter(itemKey => {
                if (!itemKey.startsWith('curr_')) return false;
                const parts = itemKey.split('_');
                const sectionName = parts[1] || '';
                
                let count = 0;
                Object.keys(initialRatings[itemKey] || {}).forEach(kanda => {
                  Object.keys(initialRatings[itemKey][kanda] || {}).forEach(pannam => {
                    const rating = initialRatings[itemKey][kanda][pannam];
                    const isExcluded = isPannamInPrevious(sectionName, kanda, pannam);
                    if (!isExcluded && rating <= 5) {
                      count++;
                    }
                  });
                });
                return count >= 5;
              }).length > 0;
              
              if (lowRatingCount) {
                setHasStudentStopped(true);
              }
            }
          } catch (err) {
            // Use calculated structure on error
            setGrandhamToShow(calculatedToShow);
            
            const initialMarks = {};
            const initialRatings = {};
            
            calculatedToShow.previous.forEach((item, idx) => {
              const key = `prev_${item.section}_${idx}`;
              initialMarks[key] = 0;
              initialRatings[key] = {};
            });
            
            calculatedToShow.current.forEach((item, idx) => {
              const key = `curr_${item.section}_${idx}`;
              initialMarks[key] = 0;
              
              // Special handling for Krishna Yajurveda rating dropdowns
              if (item.isSpecialKrishna && item.totalDropdowns) {
                initialRatings[key] = {
                  '_special_kanda': {}
                };
                for (let i = 1; i <= item.totalDropdowns; i++) {
                  initialRatings[key]['_special_kanda'][i] = '';
                }
              } else if (item.isAthyuthama && item.data._athyuthama_ratings) {
                // Special handling for athyuthama exam - rating dropdowns for each parikshaadhikari
                initialRatings[key] = {
                  '_athyuthama_ratings': {}
                };
                item.data._athyuthama_ratings.forEach(p => {
                  initialRatings[key]['_athyuthama_ratings'][p.parikshaadhikari_id] = '';
                });
              } else if (item.isSpecialRule) {
                // Special handling for saamaanya_kramamu and saamaanya_ghanamu (pass/fail ratings)
                initialRatings[key] = {};
                Object.keys(item.data || {}).forEach(kanda => {
                  initialRatings[key][kanda] = {};
                  (item.data[kanda] || []).forEach(pannam => {
                    initialRatings[key][kanda][pannam] = '';
                  });
                });
              } else {
                initialRatings[key] = {};
              }
            });
            
            setMarksData(initialMarks);
            setPannamRatings(initialRatings);
            
            // Check if already 5+ pannams with <= 5 rating (underperforming condition met)
            const lowRatingCount = Object.keys(initialRatings).filter(itemKey => {
              if (!itemKey.startsWith('curr_')) return false;
              const parts = itemKey.split('_');
              const sectionName = parts[1] || '';
              
              let count = 0;
              Object.keys(initialRatings[itemKey] || {}).forEach(kanda => {
                Object.keys(initialRatings[itemKey][kanda] || {}).forEach(pannam => {
                  const rating = initialRatings[itemKey][kanda][pannam];
                  const isExcluded = isPannamInPrevious(sectionName, kanda, pannam);
                  if (!isExcluded && rating <= 5) {
                    count++;
                  }
                });
              });
              return count >= 5;
            }).length > 0;
            
            if (lowRatingCount) {
              setHasStudentStopped(true);
            }
          }
        }
      } else {
        setSelectedVatuData(null);
        setGrandhamToShow({ previous: [], current: [] });
        setMarksData({});
        setPannamRatings({});
        setHasStudentStopped(false);
      }
    };
    
    loadVatuAndResults();
  }, [selectedVatu, teluguYearTelugu, selectedPathasala, vatuList, selectedParikshaType, parikshaadhikariInfo, isAthyuthamaMode, availableParikshaadhikarisList]);

  // Helper function to check if all displayed grandhams have all answers selected
  const isAllDisplayedGrandhamCompleted = (isPrevToConsider = false) => {
    // For athyuthama mode, we don't require all ratings to be completed
    if (grandhamToShow?.isAthyuthama) {
      return true; // Always allow save in athyuthama mode
    }
    
    // First check if this is a special case with >10 failures (అనుత్తీర్ణుడయ్యాడు)
    // If so, return true to enable save button
    if (!grandhamToShow.current || grandhamToShow.current.length === 0) return false;
    const hasSpecialCase = grandhamToShow.current.some(item => 
      (item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule
    );
    
    if (hasSpecialCase) {
      let totalFailures = 0;
      grandhamToShow.current.forEach((item, idx) => {
        if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
          const key = `curr_${item.section}_${idx}`;
          
          if (item.isSpecialKrishna && item.totalDropdowns) {
            const kandaData = pannamRatings[key]?.['_special_kanda'];
            if (kandaData) {
              Object.keys(kandaData).forEach(pannam => {
                if (kandaData[pannam] === 'fail') {
                  totalFailures++;
                }
              });
            }
          } else if (item.isSpecialRule) {
            const ratingObj = pannamRatings[key];
            if (ratingObj) {
              Object.keys(ratingObj || {}).forEach(kanda => {
                Object.keys(ratingObj[kanda] || {}).forEach(pannam => {
                  if (ratingObj[kanda][pannam] === 'fail') {
                    totalFailures++;
                  }
                });
              });
            }
          }
        }
      });
      
      // If >10 failures, consider as completed (అనుత్తీర్ణుడయ్యాడు)
      if (totalFailures > 10) {
        return true;
      }
    }
    
    if (isPrevToConsider) {
      // Check all previous grandhams
      for (const item of grandhamToShow.previous) {
        const key = `prev_${item.section}_${grandhamToShow.previous.indexOf(item)}`;
        for (const kanda of Object.keys(item.data || {})) {
          for (const pannam of item.data[kanda] || []) {
            const rating = pannamRatings[key]?.[kanda]?.[pannam];
            if (!rating) {
              return false; // Found an unanswered pannam
            }
          }
        }
      }
    }

    if (hasStudentStopped) return true;
    
    // Check all current grandhams
    for (const item of grandhamToShow.current) {
      const key = `curr_${item.section}_${grandhamToShow.current.indexOf(item)}`;
      
      // ============================================================================
      // NON-KRISHNA TYPE COMPLETION CHECK
      // For non-Krishna types, check if all items have pass/fail ratings
      // ============================================================================
      if (item.isNonKrishna) {
        if (item.data._items) {
          // Has sub-items - check each sub-item has a rating
          for (const subItem of item.data._items) {
            const rating = pannamRatings[key]?.[subItem];
            if (!rating) {
              return false; // Found an unanswered sub-item
            }
          }
        } else {
          // Single grandham - check _single rating
          const rating = pannamRatings[key]?.['_single'];
          if (!rating) {
            return false; // Not answered
          }
        }
        continue; // Move to next item
      }
      
      // Krishna type completion check (existing logic)
      for (const kanda of Object.keys(item.data || {})) {
        for (const pannam of (item.data[kanda] || [])) {
          const rating = pannamRatings[key]?.[kanda]?.[pannam];
          // For current grandhams, exclude pannams that are in previous grandhams
          const isExcluded = isPannamInPrevious(item.section, kanda, pannam);
          if (!rating && !isExcluded) {
            return false; // Found an unanswered pannam
          }
        }
      }
    }
    
    return true; // All displayed grandhams are completed
  };

  // ======================== VOICE RECORDING FUNCTIONS ========================
  
  const startRecording = async (recordingKey) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current[recordingKey] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current[recordingKey].push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current[recordingKey], { type: mimeType });
        setAudioBlobs(prev => ({
          ...prev,
          [recordingKey]: audioBlob
        }));
      };

      mediaRecorder.start();
      setRecordingState(prev => ({
        ...prev,
        [recordingKey]: true
      }));
    } catch (err) {
      setSaveError(lang === 'telugu' ? 'మైక్రోఫోన్‌కు ప్రవేశం నిరాకరించబడింది' : 'माइक्रोफोन एक्सेस अस्वीकृत है');
    }
  };

  const stopRecording = (recordingKey) => {
    if (mediaRecorderRef.current && recordingState[recordingKey]) {
      mediaRecorderRef.current.stop();
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      setRecordingState(prev => ({
        ...prev,
        [recordingKey]: false
      }));
    }
  };

  const playRecording = (recordingKey) => {
    if (audioBlobs[recordingKey]) {
      const audioUrl = URL.createObjectURL(audioBlobs[recordingKey]);
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  // ======================== FEEDBACK SECTION FUNCTIONS ========================
  
  // Fetch parikshaadhikaris for current batch
  const fetchBatchParikshaadhikaris = async () => {
    if (!selectedVatuData) return;
    
    try {
      // Find the correct batch based on the selected pariksha type
      const batchAssignments = selectedVatuData.batch_assignments || [];
      const batchIds = selectedVatuData.batch_ids || (selectedVatuData.batch_id ? [selectedVatuData.batch_id] : []);
      
      // Find batch_id matching the selected pariksha type
      let batchIdForFeedback = null;
      if (batchAssignments.length > 0 && selectedParikshaType) {
        const matchingAssignment = batchAssignments.find(ba => ba.pariksha_type === selectedParikshaType);
        batchIdForFeedback = matchingAssignment?.batch_id;
      }
      
      // Fallback to first batch_id if no match found (for backward compatibility)
      if (!batchIdForFeedback && batchIds.length > 0) {
        batchIdForFeedback = batchIds[0];
      }
      
      if (!batchIdForFeedback) return;
      
      const res = await api.get(`/api/pariksha-batch/parikshaadhikaris/${batchIdForFeedback}`);
      const data = await res.json();
      
      if (data.success && data.parikshaadhikaris) {
        setBatchParikshaadhikaris(data.parikshaadhikaris);
      }
    } catch (err) {
    }
  };

  // Check if feedback requirements are met
  const isFeedbackComplete = () => {
    // In athyuthama mode, skip feedback requirements
    if (isAthyuthamaMode && grandhamToShow?.isAthyuthama) {
      return true;
    }
    
    if (batchParikshaadhikaris.length === 0) return true; // No parikshaadhikaris, no feedback needed
    
    // Check if at least one parikshaadhikari has both positive and improvement feedback
    let hasPositiveFeedback = false;
    let hasImprovementFeedback = false;

    batchParikshaadhikaris.forEach(parikshadhikari => {
      const positiveKey = `${parikshadhikari._id}_positive`;
      const improvementKey = `${parikshadhikari._id}_improvement`;
      
      if (audioBlobs[positiveKey]) hasPositiveFeedback = true;
      if (audioBlobs[improvementKey]) hasImprovementFeedback = true;
    });

    return hasPositiveFeedback && hasImprovementFeedback;
  };

  // Update handleSave to include feedback validation
  const handleSave = async () => {
    // In athyuthama mode, skip feedback validation
    if (!(isAthyuthamaMode && grandhamToShow?.isAthyuthama) && !isFeedbackComplete()) {
      setSaveError(lang === 'telugu' 
        ? 'కనీసం ఒక పరిక్షాధికారి కోసం ఆ వ్యక్తిపరమైన సమీక్ష మరియు మెరుదరణ ఫిడ్‌బ్యాక్ అవసరం'
        : 'कम से कम एक परीक्षाधिकारी के लिए सकारात्मक और सुधार प्रतिक्रिया आवश्यक है');
      return;
    }

    // Continue with original save logic...
    if (!teluguYearTelugu || !selectedPathasala || !selectedVatu || !selectedVatuData) {
      setSaveError('దయచేసి సంవత్సరం, పాఠశాల మరియు వటువును ఎంచుకోండి');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveMessage('');

    try {
      const vatu = vatuList.find(v => v.vatu_id === selectedVatu);
      
      if (!vatu) {
        setSaveError('వటువు సమాచారం కనుగొనబడలేదు');
        setSaving(false);
        return;
      }

      // ========== UPLOAD AUDIO FILES TO MINIO ==========
      const feedbackPaths = {}; // { parikshaadhikari_id: { positive: path, improvement: path } }

      for (const parikshaadhikari of batchParikshaadhikaris) {
        const positiveKey = `${parikshaadhikari._id}_positive`;
        const improvementKey = `${parikshaadhikari._id}_improvement`;
        
        const positiveBlob = audioBlobs[positiveKey];
        const improvementBlob = audioBlobs[improvementKey];

        feedbackPaths[parikshaadhikari._id] = {};

        // Upload positive feedback if exists
        if (positiveBlob) {
          const positiveFormData = new FormData();
          positiveFormData.append('audio', positiveBlob, `positive_${parikshaadhikari._id}.webm`);
          positiveFormData.append('vatu_id', vatu.vatu_id);
          positiveFormData.append('telugu_year', teluguYearTelugu);
          positiveFormData.append('pathasala_id', selectedPathasala);
          positiveFormData.append('parikshaadhikari_id', parikshaadhikari._id);
          positiveFormData.append('feedback_type', 'positive');
          positiveFormData.append('pariksha_type', selectedParikshaType);

          const positiveRes = await api.post('/api/result/upload-audio', positiveFormData);
          const positiveData = await positiveRes.json();

          if (positiveData.success) {
            feedbackPaths[parikshaadhikari._id].positive = positiveData.minioPath;
          } else {
            throw new Error(`Failed to upload positive feedback: ${positiveData.error}`);
          }
        }

        // Upload improvement feedback if exists
        if (improvementBlob) {
          const improvementFormData = new FormData();
          improvementFormData.append('audio', improvementBlob, `improvement_${parikshaadhikari._id}.webm`);
          improvementFormData.append('vatu_id', vatu.vatu_id);
          improvementFormData.append('telugu_year', teluguYearTelugu);
          improvementFormData.append('pathasala_id', selectedPathasala);
          improvementFormData.append('parikshaadhikari_id', parikshaadhikari._id);
          improvementFormData.append('feedback_type', 'improvement');
          improvementFormData.append('pariksha_type', selectedParikshaType);

          const improvementRes = await api.post('/api/result/upload-audio', improvementFormData);
          const improvementData = await improvementRes.json();

          if (improvementData.success) {
            feedbackPaths[parikshaadhikari._id].improvement = improvementData.minioPath;
          } else {
            throw new Error(`Failed to upload improvement feedback: ${improvementData.error}`);
          }
        }
      }

      // // Save feedback paths to database
      // const feedbackRes = await api.post('/api/result/save-feedback-recordings', {
      //   telugu_year: teluguYearTelugu,
      //   pathasala_id: selectedPathasala,
      //   vatu_id: vatu.vatu_id,
      //   feedbackData: feedbackPaths
      // });

      // const feedbackData = await feedbackRes.json();
      // if (!feedbackData.success) {
      //   throw new Error(`Failed to save feedback paths: ${feedbackData.error}`);
      // }

      // ========== SAVE RESULT WITH GRANDHAM DATA ==========
      // Helper function to count failures for any case
      const countFailures = (item, idx) => {
        let failureCount = 0;
        const key = `curr_${item.section}_${idx}`;
        
        
        if (item.isSpecialKrishna && item.totalDropdowns) {
          // Special Krishna cases use _special_kanda structure
          const kandaData = pannamRatings[key]?.['_special_kanda'];
          
          if (kandaData && typeof kandaData === 'object') {
            const keys = Object.keys(kandaData);
            
            keys.forEach(pannam => {
              const rating = kandaData[pannam];
              if (rating === 'fail') {
                failureCount++;
              }
            });
          } else {
          }
        } else if (item.isSpecialRule) {
          // Saamaanya cases use kanda/pannam structure
          const ratingObj = pannamRatings[key];
          
          if (ratingObj && typeof ratingObj === 'object') {
            Object.keys(ratingObj || {}).forEach(kanda => {
              Object.keys(ratingObj[kanda] || {}).forEach(pannam => {
                const rating = ratingObj[kanda][pannam];
                if (rating === 'fail') {
                  failureCount++;
                }
              });
            });
          } else {
          }
        }
        
        return failureCount;
      };
      
      // Check for special Krishna case conversion (if failures > 3)
      const conversionDetails = [];
      const updatedCurrentData = [];
      
      
      grandhamToShow.current.forEach((item, idx) => {
        let finalItem = { ...item };
        
        
        // Check if this is a special case (Krishna or saamaanya) that needs conversion
        if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
          // Skip if already converted to final level (సామాన్య క్రమాంతం or సామాన్య ఘనము)
          const alreadyConverted = [
            'సామాన్య క్రమాంతం', 'सामान्य क्रमन्थम्',
            'సామాన్య ఘనము', 'सामान्य घनम्'
          ].includes(item.section);
          
          if (alreadyConverted) {
          } else {
            const failureCount = countFailures(item, idx);
            
            // If more than 6 failures, prepare conversion
            if (failureCount > 6) {
              let convertTo = null;
              
              if (item.isSpecialKrishna && item.totalDropdowns === 100) {
                convertTo = lang === 'telugu' ? 'సామాన్య క్రమాంతం' : 'सामान्य क्रमन्थम्';
              } else if (item.isSpecialKrishna && item.totalDropdowns === 50) {
                convertTo = lang === 'telugu' ? 'సామాన్య ఘనము' : 'सामान्य घनम्';
              }
              
              
              if (convertTo) {
                conversionDetails.push({
                  from: item.section,
                  to: convertTo,
                  failureCount: failureCount
                });
                
                // Convert the section name
                finalItem = {
                  ...item,
                  section: convertTo
                };
              }
            }
          }
        }
        
        updatedCurrentData.push(finalItem);
      });
      
      
      // Show conversion dialog if needed
      if (conversionDetails.length > 0) {
        setSpecialKrishnaConversionInfo(conversionDetails[0]);
        setIsSpecialKrishnaConversionDialogOpen(true);
        setSaving(false);
        return;
      }
      
      const previousData = grandhamToShow.previous.map((item, idx) => {
        const key = `prev_${item.section}_${idx}`;
        return {
          ...item,
          rating: pannamRatings[key] || {} // Rating contains pass/fail for previous grandham
        };
      });

      // Calculate percentage for current grandham (ఉత్తీర్ణత) - skip for special Krishna/saamaanya cases
      let currentGrandhamPercentage = 100;
      let category = '';
      let marksWithCategory = '';
      
      // Check if current item is a special Krishna or saamaanya case
      const isCurrentSpecialCase = grandhamToShow.current.some(item => (item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule);
      
      if (isCurrentSpecialCase) {
        // For special cases, calculate category based on failure count
        let totalFailures = 0;
        grandhamToShow.current.forEach((item, idx) => {
          if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
            totalFailures += countFailures(item, idx);
          }
        });
        
        category = calculateSpecialKrishnaCategory(totalFailures);
        marksWithCategory = category;
      } else {
        // For regular cases, use original calculation
        currentGrandhamPercentage = calculatePassPercentage();
        category = getPercentageCategory(currentGrandhamPercentage);
        marksWithCategory = category ? `${currentGrandhamPercentage}% (${category})` : `${currentGrandhamPercentage}%`;
      }
      
      const currentData = updatedCurrentData.map((item, idx) => {
        const key = `curr_${item.section}_${idx}`;
        return {
          ...item,
          marks: marksWithCategory,
          rating: pannamRatings[key] || {} // Rating contains numeric/pass/fail for current grandham
        };
      });

      const grandhamData = [...previousData, ...currentData];


      // Get pathasala name from the selected pathasala
      const selectedPathasalaObj = pathasalaList.find(p => p._id === selectedPathasala);
      const pathasalaName = selectedPathasalaObj ? selectedPathasalaObj.pathasalaName : '';

      // Check if all displayed grandhams are completed
      const completed = isAllDisplayedGrandhamCompleted();

      // Check if completion is due to >10 failures (అనుత్తీర్ణుడయ్యాడు case)
      let isCompletedDueToHighFailures = false;
      const hasSpecialCase = grandhamToShow.current.some(item => 
        (item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule
      );
      
      if (hasSpecialCase && completed) {
        let totalFailures = 0;
        grandhamToShow.current.forEach((item, idx) => {
          if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
            totalFailures += countFailures(item, idx);
          }
        });
        isCompletedDueToHighFailures = totalFailures > 10;
      }

      // Categorize vatu and check if patta eligibility applies
      const vatuCategory = categorizeKrishnaVatu(vatu.fullData, lang);
      const pattaEligibleCategories = ["moolam", "saamaanya_kramamu", "saamaanya_ghanamu", "vishesha_ghanamu", "vishesha_kramamu"];
      const isPattaEligible = pattaEligibleCategories.includes(vatuCategory) && isAllDisplayedGrandhamCompleted(true);

      // Calculate overall_result based on pariksha type
      let overallResult = 'pending';
      
      // ========== ATHYUTHAMA MODE HANDLING ==========
      if (isAthyuthamaMode && grandhamToShow?.isAthyuthama) {
        // For athyuthama exam, calculate percentage from ratings
        let totalRatings = 0;
        let sumRatings = 0;
        
        grandhamToShow.current.forEach((item, idx) => {
          if (item.isAthyuthama && item.data._athyuthama_ratings) {
            const key = `curr_${item.section}_${idx}`;
            const ratings = pannamRatings[key]?.['_athyuthama_ratings'] || {};
            
            Object.keys(ratings).forEach(parikshaadhikariId => {
              const rating = Number(ratings[parikshaadhikariId]);
              if (rating && !isNaN(rating)) {
                totalRatings++;
                sumRatings += rating;
              }
            });
          }
        });
        
        const athyuthamaPercentage = totalRatings > 0 ? (sumRatings / (totalRatings * 10)) * 100 : 0;
        currentGrandhamPercentage = Math.round(athyuthamaPercentage * 100) / 100;
        category = getPercentageCategory(currentGrandhamPercentage);
        marksWithCategory = category ? `${currentGrandhamPercentage}% (${category})` : `${currentGrandhamPercentage}%`;
        
        // For athyuthama, save as pending - no need to determine pass/fail here
        overallResult = 'pending';
        
        const athyuthamaGrandhamData = grandhamToShow.current.map((item, idx) => {
          const key = `curr_${item.section}_${idx}`;
          return {
            ...item,
            marks: marksWithCategory,
            rating: pannamRatings[key] || {}
          };
        });
        
        const payload = {
          year_id: yearId,
          telugu_year: teluguYearTelugu,
          pathasala_id: selectedPathasala,
          pathasala_name: pathasalaName,
          vatu_id: vatu.vatu_id,
          vatu_name: vatu.fullName,
          grandham_to_show: athyuthamaGrandhamData,
          pariksha_result: athyuthamaGrandhamData,
          completed: true, // Athyuthama is always considered complete
          pariksha_type: 'athyuthama_pariksha', // Save as athyuthama_pariksha type
          overall_result: overallResult,
          exam_reward: 0 // No exam reward for athyuthama
        };
        
        const res = await api.post('/api/result/save', payload);
        const data = await res.json();
        
        if (data.success) {
          setSaveMessage(data.updated 
            ? (lang === 'telugu' ? 'అత్యుత్తమ పరీక్ష ఫలితాలు విజయవంతంగా నవీకరించబడ్డాయి!' : 'अत्युत्तम परीक्षा परिणामानि सफलतया अद्यतनानि!') 
            : (lang === 'telugu' ? 'అత్యుత్తమ పరీక్ష ఫలితాలు విజయవంతంగా భద్రపరచబడ్డాయి!' : 'अत्युत्तम परीक्षा परिणामानि सफलतया संरक्षितानि!'));
        } else {
          setSaveError(data.error || 'ఫలితాలు భద్రపరచడంలో విఫలమైంది');
        }
        
        setSaving(false);
        return;
      }
      // ========== END ATHYUTHAMA MODE HANDLING ==========
      
      if (isKrishnaYajurveda(selectedParikshaType)) {
        // Krishna Yajurveda logic (existing)
        if (isCompletedDueToHighFailures) {
          overallResult = 'failed';
        } else if (completed) {
          overallResult = 'pass';
        } else if (countAnutheernathaRatings() >= 5 || currentGrandhamPercentage < 40) {
          overallResult = 'failed';
        } else {
          overallResult = 'pending';
        }
      } else {
        // Non-Krishna Yajurveda logic - calculate pass percentage from pass/fail ratings
        if (completed) {
          const passPercentage = calculateNonKrishnaPassPercentage();
          overallResult = passPercentage > 50 ? 'pass' : 'failed';
          console.log(`Non-Krishna exam completed. Pass percentage: ${passPercentage}%, Overall result: ${overallResult}`);
        } else {
          overallResult = 'pending';
        }
      }

      const payload = {
        year_id: yearId,
        telugu_year: teluguYearTelugu,
        pathasala_id: selectedPathasala,
        pathasala_name: pathasalaName,
        vatu_id: vatu.vatu_id,
        vatu_name: vatu.fullName,
        grandham_to_show: grandhamData,
        pariksha_result: grandhamData,
        completed: completed,
        pariksha_type: selectedParikshaType,
        overall_result: overallResult,
        exam_reward: calculateExamReward(),
        ...(getBatchIdForPayload() && { batch_id: getBatchIdForPayload() })
      };

      // Add patta_pariksha if vatu is eligible
      if (isPattaEligible) {
        payload.patta_pariksha = {
          is_patta: true,
          kramantham_type: vatuCategory
        };
      }


      const res = await api.post('/api/result/save', payload);
      const data = await res.json();


      if (data.success) {
        setSaveMessage(data.updated ? 'ఫలితాలు విజయవంతంగా నవీకరించబడ్డాయి!' : 'ఫలితాలు విజయవంతంగా భద్రపరచబడ్డాయి!');
        
        // If exam is completed, refresh the vatu list by calling by-parikshaadhikari-phone
        if (completed) {
          try {
            const refreshRes = await api.get(`/api/vatu/by-parikshaadhikari-phone/${teluguYearTelugu}`);
            const refreshData = await refreshRes.json();
            
            if (refreshRes.ok && refreshData.success && Array.isArray(refreshData.data)) {
              // Update the full dataset
              setAllVatusData(refreshData.data);
              
              // Update pathasala list if provided
              if (refreshData.pathasalas && Array.isArray(refreshData.pathasalas)) {
                setPathasalaList(refreshData.pathasalas);
              setSelectedPathasala(''); // Reset selected pathasala
              }
              
              
              console.log('Vatu list refreshed after exam completion');
            }
          } catch (refreshErr) {
            console.error('Failed to refresh vatu list:', refreshErr);
            // Don't show error to user as the save was successful
          }
        }
      } else {
        setSaveError(data.error || 'ఫలితాలు భద్రపరచడంలో విఫలమైంది');
      }
    } catch (err) {
      setSaveError(err.message || 'ఫలితాలు భద్రపరచడంలో విఫలమైంది');
    } finally {
      setSaving(false);
    }
  };

  // Original vatu selection code for backward compatibility (will be removed after migration)
  useEffect(() => {
    if (selectedVatu) {
      const vatu = vatuList.find(v => v.vatu_id === selectedVatu);
      if (vatu && vatu.fullData) {
        // This is now handled by loadVatuAndResults above
        // Keep this for any remaining UI that depends on it
        const previousRaw = {
          krishna_yajurveda: getGrandhamTypePhase(vatu.fullData, 'krishna_yajurveda', 'previous')
        };
        const currentRaw = {
          krishna_yajurveda: getGrandhamTypePhase(vatu.fullData, 'krishna_yajurveda', 'current')
        };
        
        // Convert from new nested format to flat array format
        const previous = extractAndConvertGrandhamData(previousRaw);
        const current = extractAndConvertGrandhamData(currentRaw);
        
        const toShow = calculateGrandhamToShow(previous, current);
        
        // Initialize marks for all items (only if not already loaded from saved results)
        if (Object.keys(marksData).length === 0) {
          const initialMarks = {};
          const initialRatings = {};
          
          toShow.previous.forEach((item, idx) => {
            const key = `prev_${item.section}_${idx}`;
            initialMarks[key] = '';
            
            // Initialize rating tracking for previous items (will use pass/fail)
            initialRatings[key] = {};
            Object.keys(item.data).forEach(kanda => {
              if (Array.isArray(item.data[kanda])) {
                initialRatings[key][kanda] = {};
                item.data[kanda].forEach(pannam => {
                  initialRatings[key][kanda][pannam] = '';
                });
              }
            });
          });
          
          toShow.current.forEach((item, idx) => {
            const key = `curr_${item.section}_${idx}`;
            initialMarks[key] = '';
            
            // Initialize rating tracking for current items
            initialRatings[key] = {};
            Object.keys(item.data).forEach(kanda => {
              if (Array.isArray(item.data[kanda])) {
                initialRatings[key][kanda] = {};
                item.data[kanda].forEach(pannam => {
                  initialRatings[key][kanda][pannam] = '';
                });
              }
            });
          });
          
        setMarksData(initialMarks);
        setPannamRatings(initialRatings);
      }
    } else {
      setSelectedVatuData(null);
      setGrandhamToShow({ previous: [], current: [] });
      setMarksData({});
      setPannamRatings({});
    }
  }
}, [selectedVatu, vatuList, lang]);

  // Initialize feedback states when vatu is selected
  useEffect(() => {
    const loadFeedbackRecordings = async () => {
      if (selectedVatuData && selectedVatuData.batch_id && teluguYearTelugu) {
        try {
          
          // Fetch feedback recordings from database
          const res = await api.get(
            `/api/result/feedback-recordings?telugu_year=${encodeURIComponent(teluguYearTelugu)}&vatu_id=${encodeURIComponent(selectedVatuData.vatu_id)}&pariksha_type=${encodeURIComponent(selectedParikshaType)}`
          );
          const data = await res.json();

          if (data.success && data.data && Array.isArray(data.data)) {

            // Load audio blobs from MinIO paths
            const newAudioBlobs = {};

            for (const feedback of data.data) {
              const parikshaadhikariId = feedback.parikshaadhikari_id;

              // Load positive feedback if path exists
              if (feedback.positive_feedback_path) {
                try {
                  const audioRes = await fetch(`/api/result/file/${encodeURIComponent(feedback.positive_feedback_path)}`);
                  if (audioRes.ok) {
                    const audioBlob = await audioRes.blob();
                    newAudioBlobs[`${parikshaadhikariId}_positive`] = audioBlob;
                  }
                } catch (err) {
                }
              }

              // Load improvement feedback if path exists
              if (feedback.improvement_feedback_path) {
                try {
                  const audioRes = await fetch(`/api/result/file/${encodeURIComponent(feedback.improvement_feedback_path)}`);
                  if (audioRes.ok) {
                    const audioBlob = await audioRes.blob();
                    newAudioBlobs[`${parikshaadhikariId}_improvement`] = audioBlob;
                  }
                } catch (err) {
                }
              }
            }

            if (Object.keys(newAudioBlobs).length > 0) {
              setAudioBlobs(newAudioBlobs);
            }
          }
        } catch (err) {
          // Reset states but don't show error to user
          setAudioBlobs({});
          setRecordingState({});
        }
      } else {
        // Clear everything when no vatu is selected
        setBatchParikshaadhikaris([]);
        setAudioBlobs({});
        setRecordingState({});
      }
    };

    loadFeedbackRecordings();
  }, [selectedVatuData, teluguYearTelugu]);

  const handlePathasalaChange = (event) => {
    setSelectedPathasala(event.target.value);
  };

  const handleVatuChange = (event) => {
    setSelectedVatu(event.target.value);
    // Reset pariksha type selection when vatu changes
      setSelectedParikshaType('');
    setAvailableParikshaTypes([]);
  };

  // Handler for non-Krishna rating changes with auto-save
  const handleNonKrishnaRatingChange = (key, itemKey, value) => {
    const newRatings = { ...pannamRatings };
    if (!newRatings[key]) newRatings[key] = {};
    newRatings[key][itemKey] = value;
    setPannamRatings(newRatings);
    
    // Auto-save using existing savePannamRating endpoint
    // For non-Krishna types, we use:
    // - kanda: the sub-item name (or '_single' for single grandhams)
    // - pannam: constant '1' (since non-Krishna doesn't use pannam structure)
    const saveToBackend = async () => {
      try {
        const payload = {
          telugu_year: teluguYearTelugu,
          pathasala_id: selectedPathasala,
          vatu_id: selectedVatu,
          itemKey: key,
          kanda: itemKey,  // sub-item name or '_single'
          pannam: '1',     // constant for non-Krishna
          rating: value,
          pariksha_type: selectedParikshaType
        };
        
        await api.post('/api/result/save-pannam-rating', payload);
      } catch (error) {
        console.error('Error auto-saving non-Krishna rating:', error);
      }
    };
    
    setTimeout(saveToBackend, 100);
  };

  const handleMarksChange = (key, value) => {
    setMarksData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle pannam rating change for current grandham
  const handlePannamRatingChange = (itemKey, kanda, pannam, rating) => {
    // Check if this rating change would trigger the underperforming dialog
    const [section, type, idx] = itemKey.split('_');
    const isPrevious = section === 'prev';
    
    // For special Krishna cases, skip the underperforming dialog logic
    const isSpecialKrishna = kanda === '_special_kanda';
    
    // Check if this item is a special rule case (saamaanya_kramamu or saamaanya_ghanamu)
    const itemIndex = parseInt(idx);
    const displayItem = isPrevious 
      ? grandhamToShow.previous[itemIndex]
      : grandhamToShow.current[itemIndex];
    const isSpecialRule = displayItem?.isSpecialRule;
    
    if (!isPrevious && !isSpecialKrishna && !isSpecialRule && rating && rating !== '') {
      const sectionName = itemKey.split('_')[1] || '';
      const isExcluded = isPannamInPrevious(sectionName, kanda, pannam);
      
      // Count current ratings <= 5 (excluding this one)
      let count = 0;
      Object.keys(pannamRatings).forEach(key => {
        if (key.startsWith('curr_')) {
          const sec = key.split('_')[1] || '';
          Object.keys(pannamRatings[key] || {}).forEach(k => {
            // Skip special kanda from underperforming count
            if (k === '_special_kanda') return;
            
            Object.keys(pannamRatings[key][k] || {}).forEach(p => {
              const r = pannamRatings[key][k][p];
              const isExc = isPannamInPrevious(sec, k, p);
              if (!isExc && r <= 5) count++;
            });
          });
        }
      });
      
      // If new rating is <= 5, add 1 to count
      const newCount = !isExcluded && rating <= 5 ? count + 1 : count;
      
      // If this is the 5th rating <= 5, show dialog
      if (newCount === 5 && !isExcluded && rating <= 5) {
        setPendingRatingChange({ itemKey, kanda, pannam, rating });
        setIsUnderperformingDialogOpen(true);
        return; // Don't apply the rating yet
      }
    }
    
    // Apply the rating
    applyPannamRatingChange(itemKey, kanda, pannam, rating);
  };
  
  // Helper function to actually apply the rating change
  const applyPannamRatingChange = (itemKey, kanda, pannam, rating) => {
    // Ensure rating is stored properly:
    // - For pass/fail: keep as string ('pass', 'fail', 'ఉత్తీర్ణత', 'అనుత్తీర్ణత', etc.)
    // - For numeric: convert to number
    // - For empty: keep as empty string
    let normalizedRating = rating;
    if (rating !== '' && rating !== null) {
      // Check if it's a pass/fail value
      const passFail = ['pass', 'fail', 'ఉత్తీర్ణత', 'అనుత్తీర్ణత', 'उत्तीर्णता', 'अनुत्तीर्णता'];
      if (!passFail.includes(String(rating))) {
        // Convert to number only if it's not a pass/fail value
        normalizedRating = Number(rating);
      }
    } else {
      normalizedRating = '';
    }
    
    setPannamRatings(prev => ({
      ...prev,
      [itemKey]: {
        ...prev[itemKey],
        [kanda]: {
          ...prev[itemKey]?.[kanda],
          [pannam]: normalizedRating
        }
      }
    }));
    
    // Mark the pannam as done (completed) when a rating is assigned
    const [section, type, idx] = itemKey.split('_');
    const isPrevious = section === 'prev';
    
    if (!isPrevious && normalizedRating && normalizedRating !== '') {
      // For current grandhams, mark as completed when rating is assigned
      setMarksData(prev => ({
        ...prev,
        [itemKey]: {
          ...prev[itemKey],
          [kanda]: {
            ...prev[itemKey]?.[kanda],
            [pannam]: { marked: true, rating: normalizedRating }
          }
        }
      }));
    }
    
    // Auto-save the pannam rating to DB instantly
    autoSavePannamRating(itemKey, kanda, pannam, normalizedRating);
  };
  
  // Handle continuing the exam after underperforming dialog
  const handleContinueExam = () => {
    if (pendingRatingChange) {
      applyPannamRatingChange(
        pendingRatingChange.itemKey,
        pendingRatingChange.kanda,
        pendingRatingChange.pannam,
        pendingRatingChange.rating
      );
    }
    // Reset hasStudentStopped to false to re-enable rating inputs
    setHasStudentStopped(false);
    setIsUnderperformingDialogOpen(false);
    setPendingRatingChange(null);
  };
  
  // Handle special Krishna conversion confirmation
  const handleSpecialKrishnaConversionConfirm = async () => {
    // Update the grandhamToShow state with converted section name and carry over ratings
    if (specialKrishnaConversionInfo) {
      // Find the index of the item being converted
      const itemIndex = grandhamToShow.current.findIndex(
        item => item.section === specialKrishnaConversionInfo.from
      );
      
      if (itemIndex !== -1) {
        const oldKey = `curr_${specialKrishnaConversionInfo.from}_${itemIndex}`;
        const newKey = `curr_${specialKrishnaConversionInfo.to}_${itemIndex}`;
        
        // Get the old ratings before state update
        const oldRatings = pannamRatings[oldKey];
        
        // Copy ratings from old key to new key and delete the old visesha ratings from state
        setPannamRatings(prev => {
          const updatedRatings = { ...prev };
          
          // Copy the ratings to the new key
          if (prev[oldKey]) {
            updatedRatings[newKey] = { ...prev[oldKey] };
          }
          
          // Delete the old visesha ratings from state
          delete updatedRatings[oldKey];
          
          return updatedRatings;
        });
        
        // Save the converted ratings to database for the new సామాన్య క్రమాంతం section (bulk save)
        try {
          const vatu = vatuList.find(v => v.vatu_id === selectedVatu);
          if (vatu && teluguYearTelugu && selectedPathasala && oldRatings) {
            
            // Prepare bulk ratings data
            const ratingsToSave = [];
            const currentItem = grandhamToShow.current[itemIndex];
            
            if (currentItem.isSpecialKrishna && currentItem.totalDropdowns) {
              // Special Krishna cases use _special_kanda structure
              const kandaData = oldRatings['_special_kanda'];
              if (kandaData && typeof kandaData === 'object') {
                for (const pannam of Object.keys(kandaData)) {
                  const rating = kandaData[pannam];
                  if (rating && rating !== '') {
                    ratingsToSave.push({
                      itemKey: newKey,
                      kanda: '_special_kanda',
                      pannam: pannam,
                      rating: rating,
                      isSpecialKrishna: true
                    });
                  }
                }
              }
            } else {
              // Regular kanda/pannam structure
              for (const kanda of Object.keys(oldRatings || {})) {
                const pannams = oldRatings[kanda];
                if (pannams && typeof pannams === 'object') {
                  for (const pannam of Object.keys(pannams)) {
                    const rating = pannams[pannam];
                    if (rating && rating !== '') {
                      ratingsToSave.push({
                        itemKey: newKey,
                        kanda: kanda,
                        pannam: pannam,
                        rating: rating,
                        isSpecialKrishna: false
                      });
                    }
                  }
                }
              }
            }
            
            // Bulk save all ratings in one API call
            if (ratingsToSave.length > 0) {
              const bulkPayload = {
                telugu_year: teluguYearTelugu,
                pathasala_id: selectedPathasala,
                vatu_id: vatu.vatu_id,
                ratings: ratingsToSave,
                ...(getBatchIdForPayload() && { batch_id: getBatchIdForPayload() })
              };
              
              const response = await api.post('/api/result/save-bulk-pannam-ratings', bulkPayload);
              
              if (response.ok) {
              } else {
              }
            }
          }
        } catch (err) {
        }
        
        // Delete the old visesha ratings from database
        try {
          const vatu = vatuList.find(v => v.vatu_id === selectedVatu);
          if (vatu && teluguYearTelugu && selectedPathasala) {
            
            const deleteRes = await api.post('/api/result/delete-section-ratings', {
              telugu_year: teluguYearTelugu,
              pathasala_id: selectedPathasala,
              vatu_id: vatu.vatu_id,
              section_name: specialKrishnaConversionInfo.from
            });
            
            const deleteData = await deleteRes.json();
            if (deleteData.success) {
            } else {
            }
          }
        } catch (err) {
        }
      }
      
      setGrandhamToShow(prev => ({
        ...prev,
        current: prev.current.map(item => {
          if (item.section === specialKrishnaConversionInfo.from) {
            return {
              ...item,
              section: specialKrishnaConversionInfo.to
            };
          }
          return item;
        })
      }));
      
    }
    
    // Close dialog and reset
    setIsSpecialKrishnaConversionDialogOpen(false);
    setSpecialKrishnaConversionInfo(null);
  };
  
  // Handle stopping the exam after underperforming dialog
  const handleStopExam = () => {
    if (pendingRatingChange) {
      applyPannamRatingChange(
        pendingRatingChange.itemKey,
        pendingRatingChange.kanda,
        pendingRatingChange.pannam,
        pendingRatingChange.rating
      );
    }
    setHasStudentStopped(true);
    setIsUnderperformingDialogOpen(false);
    setPendingRatingChange(null);
  };

  // Auto-save individual pannam rating to database
  const autoSavePannamRating = async (itemKey, kanda, pannam, rating) => {
    try {
      if (!teluguYearTelugu || !selectedPathasala || !selectedVatu) return;

      // For special Krishna cases (_special_kanda), ensure we're saving properly
      const isSpecialKrishna = kanda === '_special_kanda';
      const batchId = getBatchIdForPayload();
      
      const payload = {
        telugu_year: teluguYearTelugu,
        pathasala_id: selectedPathasala,
        vatu_id: selectedVatu,
        itemKey: itemKey,
        kanda: kanda,
        pannam: pannam,
        rating: rating,
        isSpecialKrishna: isSpecialKrishna,
        pariksha_type: selectedParikshaType,
        ...(batchId && { batch_id: batchId })
      };

      const response = await api.post('/api/result/save-pannam-rating', payload);
      
      if (response.ok) {
      } else {
      }
    } catch (err) {
      // Don't show error to user for auto-save, just log it
    }
  };

  // Batch save multiple pannam ratings in a single API call
  const autoSaveBatchPannamRatings = async (itemKey, kanda, pannams, rating) => {
    try {
      if (!teluguYearTelugu || !selectedPathasala || !selectedVatu) return;

      const batchId = getBatchIdForPayload();

      const payload = {
        telugu_year: teluguYearTelugu,
        pathasala_id: selectedPathasala,
        vatu_id: selectedVatu,
        itemKey: itemKey,
        kanda: kanda,
        pannams: pannams,
        rating: rating,
        pariksha_type: selectedParikshaType,
        ...(batchId && { batch_id: batchId })
      };

      await api.post('/api/result/save-batch-pannam-ratings', payload);
    } catch (err) {
      // Don't show error to user for auto-save, just log it
    }
  };

  // Handle clear all for a specific grandham (previous or current)
  const handleClearGrandham = (itemKey, isPrevious) => {
    // Clear all ratings for this grandham (works for both previous and current)
    setPannamRatings(prev => {
      const newState = { ...prev };
      delete newState[itemKey];
      return newState;
    });
  };

  // Handle individual pannam completion toggle (for previous grandham)
  const handlePannamToggle = (itemKey, kanda, pannam) => {
    setPannamRatings(prev => {
      const currentValue = prev[itemKey]?.[kanda]?.[pannam];
      // Toggle between 'pass' and 'fail' (or undefined -> 'pass')
      const newValue = currentValue === 'pass' ? 'fail' : 'pass';
      
      return {
        ...prev,
        [itemKey]: {
          ...prev[itemKey],
          [kanda]: {
            ...prev[itemKey]?.[kanda],
            [pannam]: newValue
          }
        }
      };
    });
    
    // Auto-save the pannam rating to DB instantly
    const currentValue = pannamRatings[itemKey]?.[kanda]?.[pannam];
    const newValue = currentValue === 'pass' ? 'fail' : 'pass';
    autoSavePannamRating(itemKey, kanda, pannam, newValue);
  };

  // Handle "All Given" toggle for a kanda
  const handleAllGivenToggle = (itemKey, kanda, pannams) => {
    const allCompleted = pannams.every(p => pannamRatings[itemKey]?.[kanda]?.[p] === 'pass');
    
    setPannamRatings(prev => {
      const newState = { ...prev };
      if (!newState[itemKey]) newState[itemKey] = {};
      if (!newState[itemKey][kanda]) newState[itemKey][kanda] = {};
      
      pannams.forEach(pannam => {
        newState[itemKey][kanda][pannam] = !allCompleted ? 'pass' : 'fail';
      });
      
      return newState;
    });
    
    // Auto-save all pannams in this kanda to DB in a single call
    const newValue = !allCompleted ? 'pass' : 'fail';
    autoSaveBatchPannamRatings(itemKey, kanda, pannams, newValue);
  };

  // Check if all pannams in a kanda are completed (rating === 'pass')
  const isAllCompleted = (itemKey, kanda, pannams) => {
    return pannams.every(p => pannamRatings[itemKey]?.[kanda]?.[p] === 'pass');
  };

  // Check if some pannams in a kanda are completed (for indeterminate state)
  const isSomeCompleted = (itemKey, kanda, pannams) => {
    const completed = pannams.filter(p => pannamRatings[itemKey]?.[kanda]?.[p] === 'pass').length;
    return completed > 0 && completed < pannams.length;
  };

  // Calculate marks for all previous grandhams based on overall completion percentage
  const calculateOverallMarks = () => {
    // If no previous grandham exists, return default 200
    if (grandhamToShow.previous.length === 0) return 200;
    
    let totalPannams = 0;
    let completedCount = 0;

    // Count ALL pannams from grandhamToShow.previous and check which are completed
    grandhamToShow.previous.forEach((item, idx) => {
      const itemKey = `prev_${item.section}_${idx}`;
      
      // Count all pannams in this previous item
      Object.keys(item.data || {}).forEach(kanda => {
        if (Array.isArray(item.data[kanda])) {
          item.data[kanda].forEach(pannam => {
            totalPannams++;
            // Check if this pannam is marked as 'pass' in ratings
            if (pannamRatings[itemKey]?.[kanda]?.[pannam] === 'pass') {
              completedCount++;
            }
          });
        }
      });
    });

    if (totalPannams === 0) return 100;

    // Calculate percentage and convert to marks (100-200 range)
    const percentage = completedCount / totalPannams;
    const calculatedMarks = Math.round(100 + (percentage * 100));

    // Ensure marks are between 100 and 200
    return Math.max(100, Math.min(200, calculatedMarks));
  };

  // Helper function to count అనుత్తీర్ణత ratings from non-excluded (regular) dropdowns only
  // This is used to enforce the rule: if 5 అనుత్తీర్ణత are selected, disable other dropdowns and enable save
  const countAnutheernathaRatings = () => {
    let count = 0;
    Object.keys(pannamRatings).forEach(itemKey => {
      // Only count current items (keys starting with 'curr_')
      if (itemKey.startsWith('curr_')) {
        const parts = itemKey.split('_');
        const sectionName = parts[1] || '';
        
        Object.keys(pannamRatings[itemKey] || {}).forEach(kanda => {
          Object.keys(pannamRatings[itemKey][kanda] || {}).forEach(pannam => {
            const rating = pannamRatings[itemKey][kanda][pannam];
            // Count only అనుత్తీర్ణత from non-excluded pannams
            // Check if this pannam is NOT excluded (not in previous)
            const isExcluded = isPannamInPrevious(sectionName, kanda, pannam);
            if (!isExcluded && rating <= 5) {
              count++;
            }
          });
        });
      }
    });
    return count;
  };

  // Helper function to check if a pannam exists in the stored previous selections.
  // Must match section, kanda, and pannam
  // Handles both old flat array format and new nested _selected format
  // Special handling for ఆరణ్యకభాగము where pannams can be at section level
  const isPannamInPrevious = (sectionName, kanda, pannam) => {
    if (!selectedVatuData) return false;
    const lastGrandhaNames = getGrandhamTypePhase(
      selectedVatuData, 'krishna_yajurveda', 'previous'
    );
    const aranyakamNames = ['ఆరణ్యకభాగము', 'आरण्यकभागः'];
    
    // Check if data is in new format (krishna_yajurveda as key)
    if (lastGrandhaNames) {
      const krishnaData = lastGrandhaNames;
      const sectionData = krishnaData[sectionName];
      if (sectionData && typeof sectionData === 'object') {
        // Special handling for ఆరణ్యకభాగము - pannams can be at section level with 'main' kanda
        if (aranyakamNames.includes(sectionName) && kanda === 'main') {
          // Check for direct pannams at section level
          const pannamData = sectionData[String(pannam)] || sectionData[Number(pannam)];
          if (pannamData && pannamData._selected === true) {
            return true;
          }
        }
        
        const kandaData = sectionData[kanda];
        if (kandaData) {
          // Check old format (array)
          if (Array.isArray(kandaData)) {
            return kandaData.includes(Number(pannam)) || kandaData.includes(String(pannam));
          }
          // Check new nested format
          if (typeof kandaData === 'object') {
            const pannamData = kandaData[String(pannam)] || kandaData[Number(pannam)];
            if (pannamData && pannamData._selected === true) {
              return true;
            }
          }
        }
      }
      return false;
    }
    
    // Old format: section names directly at top level
    const sectionData = lastGrandhaNames[sectionName];
    if (sectionData && typeof sectionData === 'object') {
      // Special handling for ఆరణ్యకభాగము - pannams can be at section level with 'main' kanda
      if (aranyakamNames.includes(sectionName) && kanda === 'main') {
        // Check for direct pannams at section level (new format)
        const pannamData = sectionData[String(pannam)] || sectionData[Number(pannam)];
        if (pannamData && pannamData._selected === true) {
          return true;
        }
      }
      
      // Check if this kanda exists in this specific section
      const kandaData = sectionData[kanda];
      if (kandaData) {
        // Check old format (array)
        if (Array.isArray(kandaData)) {
          return kandaData.includes(Number(pannam)) || kandaData.includes(String(pannam));
        }
        // Check new nested format
        if (typeof kandaData === 'object') {
          const pannamData = kandaData[String(pannam)] || kandaData[Number(pannam)];
          if (pannamData && pannamData._selected === true) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // Helper function to get percentage category
  const getPercentageCategory = (percentage) => {
    if (percentage === 0) return '';
    if (percentage > 0 && percentage <= 49) return lang === 'telugu' ? 'అనుత్తీర్ణత' : 'अधमम्';
    if (percentage >= 50 && percentage <= 69) return lang === 'telugu' ? 'సాధారణం' : 'मध्यमम्';
    if (percentage >= 70 && percentage <= 90) return lang === 'telugu' ? 'ఉత్తమం' : 'उत्तमम्';
    if (percentage > 90) return lang === 'telugu' ? 'అత్యుత్తమం' : 'अत्युत्तमम्';
    return '';
  };

  // Calculate pass percentage (ఉత్తీర్ణత) for current grandham
  // Helper to calculate the category based on count of failures for special Krishna/samanya cases
  const calculateSpecialKrishnaCategory = (failureCount) => {
    //if (failureCount <= 3) return lang === 'telugu' ? 'అద్భుతం' : 'अद्भुतम्';
    if (failureCount <= 6) return lang === 'telugu' ? 'అత్యుత్తమం' : 'अत्युत्तमम्';
    if (failureCount <= 8) return lang === 'telugu' ? 'ఉత్తమం' : 'उत्तमम्';
    if (failureCount <= 10) return lang === 'telugu' ? 'సాధారణం' : 'मध्यमम्';
    return lang === 'telugu' ? 'అనుత్తీర్ణుడయ్యాడు' : 'अनुत्तीर्णः अभवत्';
  };

  // Calculate pass percentage for non-Krishna Yajurveda pariksha types (pass/fail ratings)
  const calculateNonKrishnaPassPercentage = () => {
    let totalRatings = 0;
    let passCount = 0;

    // Count all pass/fail ratings for current items
    Object.keys(pannamRatings).forEach(itemKey => {
      // Only count current items (keys starting with 'curr_')
      if (itemKey.startsWith('curr_')) {
        const itemRatings = pannamRatings[itemKey] || {};
        
        // Handle both nested (kanda/pannam) and flat structures
        Object.keys(itemRatings).forEach(key => {
          if (key.startsWith('_')) return; // Skip special keys like _completed, _special_kanda
          
          const value = itemRatings[key];
          
          // Check if it's a nested structure (kanda -> pannam)
          if (typeof value === 'object' && value !== null) {
            Object.keys(value).forEach(pannam => {
              const rating = value[pannam];
              if (rating === 'pass' || rating === 'fail') {
                totalRatings++;
                if (rating === 'pass') passCount++;
              }
            });
          } else {
            // Flat structure (direct rating)
            if (value === 'pass' || value === 'fail') {
              totalRatings++;
              if (value === 'pass') passCount++;
            }
          }
        });
      }
    });

    if (totalRatings === 0) return 0;

    // Calculate percentage: (passCount / totalRatings) * 100
    const percentage = (passCount / totalRatings) * 100;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  };

  const calculatePassPercentage = () => {
    let totalPannams = 0;
    let totalScore = 0;

    // Count all pannams and sum scores for current items, excluding overlaps with previous grandham
    Object.keys(pannamRatings).forEach(itemKey => {
      // Only count current items (keys starting with 'curr_')
      if (itemKey.startsWith('curr_')) {
        // Extract section name from itemKey (format: curr_<section>_<index>)
        const sectionName = itemKey.split('_')[1];
        
        Object.keys(pannamRatings[itemKey] || {}).forEach(kanda => {
          Object.keys(pannamRatings[itemKey][kanda] || {}).forEach(pannam => {
            const rating = pannamRatings[itemKey][kanda][pannam];
            if (rating) {
              // Skip if pannam exists in the SAME section in previous grandham
              if (!isPannamInPrevious(sectionName, kanda, pannam)) {
                totalPannams++;
                totalScore += Number(rating) || 0;
              }
            }
          });
        });
      }
    });

    if (totalPannams === 0) return 0;

    // Calculate percentage: (sum of scores / total pannams) * 100
    const percentage = (totalScore / totalPannams) * 10;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  };

  // Calculate exam reward (పరీక్ష బహుమానం) for current grandham
  const calculateExamReward = () => {
    const padamu = lang === 'telugu' ? 'పదము' : 'पदम्';
    const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
    const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
    const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
    const kramamu = lang === 'telugu' ? 'క్రమము' : 'क्रमम्';
    const jata = lang === 'telugu' ? 'జటా' : 'जटा';
    const ghanam = lang === 'telugu' ? 'ఘనము' : 'घनम्';

    // Collect all pannams with ratings, excluding those in previous grandham
    const allPannamsWithRatings = [];
    Object.keys(pannamRatings).forEach(itemKey => {
      if (!itemKey.startsWith('curr_')) return;
      const parts = itemKey.split('_');
      const sectionName = parts[1] || '';
      const isPadam = sectionName === padamu;

      Object.keys(pannamRatings[itemKey] || {}).forEach(kanda => {
        Object.keys(pannamRatings[itemKey][kanda] || {}).forEach(pannam => {
          const ratingRaw = pannamRatings[itemKey][kanda][pannam];
          if (!ratingRaw) return;
          const rating = Number(ratingRaw);
          // Skip if pannam exists in the SAME section in previous grandham
          if (isPannamInPrevious(sectionName, kanda, pannam)) return;

          allPannamsWithRatings.push({
            section: sectionName,
            rating: isNaN(rating) ? 0 : rating,
            kanda,
            pannam
          });
        });
      });
    });

    if (allPannamsWithRatings.length === 0) return 0;

    // Group pannams by section and apply caps & per-pannam max rules
    const sectionGroups = {};
    allPannamsWithRatings.forEach(p => {
      if (!sectionGroups[p.section]) sectionGroups[p.section] = [];
      sectionGroups[p.section].push(p);
    });

    const isInGroup = (name, list) => list.includes(name);
    const brahAraSam = [brahmanam, aranyakam, samhita];

    let baseReward = 0;

    // Handle brahAraSam sections FIRST with COMBINED cap of 16
    const brahAraSamItems = [];
    brahAraSam.forEach(section => {
      if (sectionGroups[section]) {
        brahAraSamItems.push(...sectionGroups[section]);
      }
    });
    
    if (brahAraSamItems.length > 0) {
      // Sort by rating desc and pick top 16 COMBINED across all three sections
      const sorted = brahAraSamItems.slice().sort((a, b) => b.rating - a.rating);
      const selected = sorted.slice(0, 16); // COMBINED cap of 16, not per-section
      
      selected.forEach(it => {
        const r = Math.max(0, Math.min(10, Number(it.rating) || 0));
        baseReward += (r / 10) * 200; // perPannamMax is 200 for brahAraSam
      });
    }

    // Handle other sections (padamu, kramamu, jata, ghanam, etc.)
    Object.keys(sectionGroups).forEach(section => {
      // Skip brahAraSam sections - already handled above with combined cap
      if (brahAraSam.includes(section)) return;
      
      const items = sectionGroups[section];
      let cap = Infinity;
      let perPannamMax = 100; // default for kramamu/jata/ghanam

      if (section === padamu) {
        cap = 30;
        perPannamMax = 150;
      } else if (section === kramamu || section === jata || section === ghanam) {
        cap = Infinity; // include all
        perPannamMax = 100;
      } else {
        // Unknown section - treat as kramamu-like
        cap = Infinity;
        perPannamMax = 100;
      }

      // Sort by rating desc and pick top `cap`
      const sorted = items.slice().sort((a, b) => b.rating - a.rating);
      const selected = cap === Infinity ? sorted : sorted.slice(0, cap);

      // Sum per-pannam contribution: (rating/10) * perPannamMax
      selected.forEach(it => {
        const r = Math.max(0, Math.min(10, Number(it.rating) || 0));
        baseReward += (r / 10) * perPannamMax;
      });
    });

    // Scale reward by previous marks factor (preserve calculateOverallMarks logic)
    const previousMarks = calculateOverallMarks(); // 100-200 range
    const scale = previousMarks / 200; // normalize to 0-1 (200 is max)

    const reward = baseReward * scale;
    
    // Check if this is krishna_yajurveda with first_varshika_pariksha type
    // If so, apply max cap of 4500
    if (selectedVatuData) {
      const parikshaTypeArray = Array.isArray(selectedVatuData.pariksha_type) 
        ? selectedVatuData.pariksha_type 
        : [selectedVatuData.pariksha_type];
      
      const isKrishnaYajurveda = parikshaTypeArray.includes('krishna_yajurveda');
      const isFirstVarshika = selectedVatuData.krishnaYajurvedaType === 'first_varshika_pariksha';
      
      if (isKrishnaYajurveda && isFirstVarshika) {
        // Apply max cap of 4500 for krishna_yajurveda first_varshika_pariksha
        return Math.round(Math.min(reward, 4500) * 100) / 100;
      }
    }
    
    return Math.round(reward * 100) / 100;
  };

  // Compute base reward based on previous pannam completions (pro rata)
  // Formula: 100 + ((max_cap - 100) / total_previous_pannams) * completed_previous_pannams
  const computeBaseReward = (currentSection) => {
    if (!currentSection) return 100;

    const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
    const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
    const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
    const padamu = lang === 'telugu' ? 'పదము' : 'पदम्';

    // Determine max cap for current section
    let sectionMaxCap = 100;
    if ([brahmanam, aranyakam, samhita].includes(currentSection)) {
      sectionMaxCap = 200;
    } else if (currentSection === padamu) {
      sectionMaxCap = 150;
    }

    // Count total previous pannams and completed ones
    let totalPreviousPannams = 0;
    let completedPreviousPannams = 0;

    grandhamToShow.previous.forEach((item, idx) => {
      const key = `prev_${item.section}_${idx}`;
      Object.keys(item.data || {}).forEach(kanda => {
        if (Array.isArray(item.data[kanda])) {
          item.data[kanda].forEach(pannam => {
            totalPreviousPannams++;
            if (pannamRatings[key]?.[kanda]?.[pannam] === 'pass') {
              completedPreviousPannams++;
            }
          });
        }
      });
    });

    if (totalPreviousPannams === 0) return 100;

    // Pro rata calculation: per-pannam increment based on section max cap
    const difference = sectionMaxCap - 100;
    const perPannamIncrement = difference / totalPreviousPannams;
    const baseReward = 100 + (completedPreviousPannams * perPannamIncrement);

    return Math.round(baseReward * 100) / 100;
  };

  // Old computeBaseReward code (no longer used - keeping as reference)
  const _unused_computeBaseReward_old = () => {
    const padamu = lang === 'telugu' ? 'పదము' : 'पदम्';
    const brahmanam = lang === 'telugu' ? 'బ్రాహ్మణము' : 'ब्राह्मणम्';
    const aranyakam = lang === 'telugu' ? 'ఆరణ్యకభాగము' : 'आरण्यकभागः';
    const samhita = lang === 'telugu' ? 'సంహితా' : 'संहिता';
    const kramamu = lang === 'telugu' ? 'క్రమము' : 'क्रमम्';
    const jata = lang === 'telugu' ? 'జటా' : 'जटा';
    const ghanam = lang === 'telugu' ? 'ఘనము' : 'घनम्';

    const allPannamsWithRatings = [];
    Object.keys(pannamRatings).forEach(itemKey => {
      if (!itemKey.startsWith('curr_')) return;
      const parts = itemKey.split('_');
      const sectionName = parts[1] || '';

      Object.keys(pannamRatings[itemKey] || {}).forEach(kanda => {
        Object.keys(pannamRatings[itemKey][kanda] || {}).forEach(pannam => {
          const ratingRaw = pannamRatings[itemKey][kanda][pannam];
          if (!ratingRaw) return;
          const rating = Number(ratingRaw);
          if (isPannamInPrevious(sectionName, kanda, pannam)) return;
          allPannamsWithRatings.push({ section: sectionName, rating: isNaN(rating) ? 0 : rating });
        });
      });
    });

    if (allPannamsWithRatings.length === 0) return 100;

    const sectionGroups = {};
    allPannamsWithRatings.forEach(p => {
      if (!sectionGroups[p.section]) sectionGroups[p.section] = [];
      sectionGroups[p.section].push(p);
    });

    const isInGroup = (name, list) => list.includes(name);
    const brahAraSam = [brahmanam, aranyakam, samhita];

    let baseReward = 100;
    Object.keys(sectionGroups).forEach(section => {
      const items = sectionGroups[section];
      let cap = Infinity;
      let perPannamMax = 100;

      if (section === padamu) {
        cap = 30;
        perPannamMax = 150;
      } else if (isInGroup(section, brahAraSam)) {
        cap = 16;
        perPannamMax = 200;
      } else if (section === kramamu || section === jata || section === ghanam) {
        cap = Infinity;
        perPannamMax = 100;
      } else {
        cap = Infinity;
        perPannamMax = 100;
      }

      const sorted = items.slice().sort((a, b) => b.rating - a.rating);
      const selected = cap === Infinity ? sorted : sorted.slice(0, cap);
      selected.forEach(it => {
        const r = Math.max(0, Math.min(10, Number(it.rating) || 0));
        baseReward += (r / 10) * perPannamMax;
      });
    });

    return Math.round(baseReward * 100) / 100;
  };

  // Render grandham tree structure
  const renderGrandhamItem = (item, index, isPreviousSection) => {
    const key = `${isPreviousSection ? 'prev' : 'curr'}_${item.section}_${index}`;
    
    // ============================================================================
    // NON-KRISHNA PARIKSHA TYPE RENDERING
    // For sanskrit, vedadhyayi_sahityam, lakshanam, etc.
    // Current: Show pass/fail dropdowns for each grandham/sub-item
    // Previous: Show checkboxes for completion (unless "ఏమీ ఇవ్వలేదు")
    // ============================================================================
    if (item.isNonKrishna) {
      // For previous grandhams - show checkboxes
      if (isPreviousSection) {
        return (
          <Accordion 
            key={key}
            defaultExpanded
            sx={{ 
              mb: 2,
              '&:before': { display: 'none' },
              boxShadow: 2
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#fff9e6',
                '&:hover': { backgroundColor: '#fff3cd' },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, color: '#7f4b2d', fontSize: '1.1rem' }}>
                {item.section}
              </Typography>
              <Box sx={{
                px: 1.5, py: 0.5,
                backgroundColor: '#ffc107',
                color: '#fff',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {labels[lang].previousGrandham}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {item.data._items ? (
                  // Has sub-items (like kandas)
                  item.data._items.map((subItem, subIdx) => {
                    const currentChecked = pannamRatings[key]?.[subItem] === 'pass';
                    return (
                      <FormControlLabel
                        key={subIdx}
                        control={
                          <Checkbox
                            checked={currentChecked}
                            onChange={(e) => {
                              const newRatings = { ...pannamRatings };
                              if (!newRatings[key]) newRatings[key] = {};
                              const newValue = e.target.checked ? 'pass' : '';
                              newRatings[key][subItem] = newValue;
                              setPannamRatings(newRatings);
                              
                              // Auto-save to database using same pattern as handleNonKrishnaRatingChange
                              const saveToBackend = async () => {
                                try {
                                  const payload = {
                                    telugu_year: teluguYearTelugu,
                                    pathasala_id: selectedPathasala,
                                    vatu_id: selectedVatu,
                                    itemKey: key,
                                    kanda: subItem,
                                    pannam: '1',
                                    rating: newValue,
                                    pariksha_type: selectedParikshaType
                                  };
                                  
                                  await api.post('/api/result/save-pannam-rating', payload);
                                } catch (error) {
                                  console.error('Error auto-saving non-Krishna previous grandham rating:', error);
                                }
                              };
                              
                              setTimeout(saveToBackend, 100);
                            }}
                            sx={{
                              color: '#7f4b2d',
                              '&.Mui-checked': { color: '#4caf50' }
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontWeight: 500, color: currentChecked ? '#4caf50' : '#333' }}>
                            {subItem}
                          </Typography>
                        }
                      />
                    );
                  })
                ) : (
                  // Single grandham without sub-items
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pannamRatings[key]?.['_completed'] === 'pass'}
                        onChange={(e) => {
                          const newRatings = { ...pannamRatings };
                          if (!newRatings[key]) newRatings[key] = {};
                          const newValue = e.target.checked ? 'pass' : '';
                          newRatings[key]['_completed'] = newValue;
                          setPannamRatings(newRatings);
                          
                          // Auto-save to database using same pattern as handleNonKrishnaRatingChange
                          const saveToBackend = async () => {
                            try {
                              const payload = {
                                telugu_year: teluguYearTelugu,
                                pathasala_id: selectedPathasala,
                                vatu_id: selectedVatu,
                                itemKey: key,
                                kanda: '_completed',
                                pannam: '1',
                                rating: newValue,
                                pariksha_type: selectedParikshaType
                              };
                              
                              await api.post('/api/result/save-pannam-rating', payload);
                            } catch (error) {
                              console.error('Error auto-saving non-Krishna single grandham rating:', error);
                            }
                          };
                          
                          setTimeout(saveToBackend, 100);
                        }}
                        sx={{
                          color: '#7f4b2d',
                          '&.Mui-checked': { color: '#4caf50' }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500 }}>
                        {lang === 'telugu' ? 'పూర్తయింది' : 'पूर्णम्'}
                      </Typography>
                    }
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      }
      
      // For current grandhams - show pass/fail dropdowns
      return (
        <Accordion 
          key={key}
          defaultExpanded
          sx={{ 
            mb: 2,
            '&:before': { display: 'none' },
            boxShadow: 2
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                justifyContent: 'space-between'
              }
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#7f4b2d', fontSize: '1.1rem' }}>
              {item.section}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                px: 1.5, py: 0.5,
                backgroundColor: '#4caf50',
                color: '#fff',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {labels[lang].currentGrandham}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {item.data._items ? (
                // Has sub-items (like kandas) - show pass/fail for each
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {item.data._items.map((subItem, subIdx) => {
                    const currentRating = pannamRatings[key]?.[subItem];
                    return (
                      <Box
                        key={subIdx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          backgroundColor: currentRating 
                            ? (currentRating === 'pass' ? '#e8f5e9' : '#ffebee')
                            : '#e3f2fd',
                          borderRadius: 1,
                          border: `2px solid ${
                            currentRating 
                              ? (currentRating === 'pass' ? '#4caf50' : '#c62828')
                              : '#7f4b2d'
                          }`,
                          minWidth: '200px'
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, color: '#7f4b2d', mr: 1 }}>
                          {subItem}:
                        </Typography>
                        <Select
                          value={currentRating || ''}
                          onChange={(e) => {
                            handleNonKrishnaRatingChange(key, subItem, e.target.value);
                          }}
                          displayEmpty
                          size="small"
                          sx={{
                            minWidth: '120px',
                            height: '32px',
                            backgroundColor: '#fff'
                          }}
                        >
                          <MenuItem value="" sx={{ color: '#999' }}>
                            <em>--</em>
                          </MenuItem>
                          <MenuItem value="pass" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            {lang === 'telugu' ? 'ఉత్తీర్ణత' : 'उत्तीर्णम्'}
                          </MenuItem>
                          <MenuItem value="fail" sx={{ fontWeight: 600, color: '#c62828' }}>
                            {lang === 'telugu' ? 'అనుత్తీర్ణత' : 'अनुत्तीर्णम्'}
                          </MenuItem>
                        </Select>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                // Single grandham without sub-items - show single pass/fail
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  backgroundColor: pannamRatings[key]?.['_single'] 
                    ? (pannamRatings[key]['_single'] === 'pass' ? '#e8f5e9' : '#ffebee')
                    : '#e3f2fd',
                  borderRadius: 1,
                  border: `2px solid ${
                    pannamRatings[key]?.['_single'] 
                      ? (pannamRatings[key]['_single'] === 'pass' ? '#4caf50' : '#c62828')
                      : '#7f4b2d'
                  }`,
                  maxWidth: '300px'
                }}>
                  <Typography sx={{ fontWeight: 600, color: '#7f4b2d', mr: 1 }}>
                    {lang === 'telugu' ? 'ఫలితం' : 'फलम्'}:
                  </Typography>
                  <Select
                    value={pannamRatings[key]?.['_single'] || ''}
                    onChange={(e) => {
                      handleNonKrishnaRatingChange(key, '_single', e.target.value);
                    }}
                    displayEmpty
                    size="small"
                    sx={{
                      minWidth: '120px',
                      height: '32px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <MenuItem value="" sx={{ color: '#999' }}>
                      <em>--</em>
                    </MenuItem>
                    <MenuItem value="pass" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {lang === 'telugu' ? 'ఉత్తీర్ణత' : 'उत्तीर्णम्'}
                    </MenuItem>
                    <MenuItem value="fail" sx={{ fontWeight: 600, color: '#c62828' }}>
                      {lang === 'telugu' ? 'అనుత్తీర్ణత' : 'अनుत్తీర్णम్'}
                    </MenuItem>
                  </Select>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    }
    
    // ============================================================================
    // ATHYUTHAMA EXAM RENDERING
    // Show rating dropdowns (1-10) for each available parikshaadhikari
    // ============================================================================
    if (item.isAthyuthama && item.data._athyuthama_ratings) {
      const parikshaadhikariList = item.data._athyuthama_ratings;
      
      const handleAthyuthamaRatingChange = (parikshaadhikariId, rating) => {
        setPannamRatings(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            '_athyuthama_ratings': {
              ...prev[key]?.['_athyuthama_ratings'],
              [parikshaadhikariId]: rating
            }
          }
        }));
        
        // Auto-save the rating
        const saveToBackend = async () => {
          try {
            const payload = {
              telugu_year: teluguYearTelugu,
              pathasala_id: selectedPathasala,
              vatu_id: selectedVatu,
              itemKey: key,
              kanda: '_athyuthama_ratings',
              pannam: parikshaadhikariId,
              rating: rating,
              pariksha_type: 'athyuthama_pariksha'
            };
            
            await api.post('/api/result/save-pannam-rating', payload);
          } catch (error) {
            console.error('Error auto-saving athyuthama rating:', error);
          }
        };
        
        setTimeout(saveToBackend, 100);
      };
      
      return (
        <Accordion 
          key={key}
          defaultExpanded
          sx={{ 
            mb: 2,
            '&:before': { display: 'none' },
            boxShadow: 3
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#e3f2fd',
              '&:hover': { backgroundColor: '#bbdefb' },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                justifyContent: 'space-between'
              }
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1.2rem' }}>
              {item.section}
            </Typography>
            <Box sx={{
              px: 2, py: 0.5,
              backgroundColor: '#1976d2',
              color: '#fff',
              borderRadius: 1,
              fontSize: '0.8rem',
              fontWeight: 700
            }}>
              {lang === 'telugu' ? `${parikshaadhikariList.length} పరీక్షాధికారులు` : `${parikshaadhikariList.length} परीक्षाधिकारिणः`}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic' }}>
              {lang === 'telugu' 
                ? 'ప్రతి పరీక్షాధికారి కొరకు 1 నుండి 10 వరకు రేటింగ్ ఇవ్వండి' 
                : 'प्रत्येक परीक्षाधिकारिणे 1 तः 10 पर्यन्तं मूल्याङ्कनं ददातु'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
              {parikshaadhikariList.map((p, idx) => {
                const currentRating = pannamRatings[key]?.['_athyuthama_ratings']?.[p.parikshaadhikari_id];
                const ratingColor = currentRating 
                  ? (currentRating >= 8 ? '#4caf50' : currentRating >= 5 ? '#ff9800' : '#f44336')
                  : '#1976d2';
                
                return (
                  <Box
                    key={p.parikshaadhikari_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      backgroundColor: currentRating 
                        ? (currentRating >= 8 ? '#e8f5e9' : currentRating >= 5 ? '#fff3e0' : '#ffebee')
                        : '#f5f5f5',
                      borderRadius: 2,
                      border: `2px solid ${ratingColor}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#7f4b2d', flex: 1 }}>
                      {idx + 1}. {lang === 'telugu' ? p.parikshaadhikari_telugu : p.parikshaadhikari_sanskrit}
                    </Typography>
                    <Select
                      value={currentRating || ''}
                      onChange={(e) => handleAthyuthamaRatingChange(p.parikshaadhikari_id, e.target.value)}
                      displayEmpty
                      size="small"
                      sx={{
                        minWidth: '80px',
                        height: '40px',
                        fontSize: '1rem',
                        fontWeight: 700,
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': {
                          py: 1
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ratingColor
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1565c0'
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ color: '#999' }}>
                        <em>--</em>
                      </MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(rating => (
                        <MenuItem 
                          key={rating} 
                          value={rating}
                          sx={{ 
                            fontWeight: 600,
                            color: rating >= 8 ? '#2e7d32' : rating >= 5 ? '#f57c00' : '#c62828'
                          }}
                        >
                          {rating}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    }
    
    // Special handling for Krishna Yajurveda rating dropdowns
    if (item.isSpecialKrishna && item.totalDropdowns) {
      const generatePannamsForSpecial = (total) => {
        const pannams = [];
        for (let i = 1; i <= total; i++) {
          pannams.push(i);
        }
        return pannams;
      };
      
      const pannams = generatePannamsForSpecial(item.totalDropdowns);
      
      const formatSpecialKrishnaData = () => {
        const subSection = '_special_kanda'; // Generic name for special Krishna dropdowns
        
        return (
          <Box sx={{ pl: 2 }}>
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
              {/* Display all rating dropdowns in grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1.5 }}>
                {pannams.map((val, idx) => {
                  const currentRating = pannamRatings[key]?.[subSection]?.[val];
                  const isFailed = currentRating === 'fail';
                  const isPassed = currentRating === 'pass';
                  
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1,
                        backgroundColor: currentRating 
                          ? (isPassed ? '#e8f5e9' : '#ffebee')
                          : '#e3f2fd',
                        borderRadius: 1,
                        border: `2px solid ${
                          currentRating 
                            ? (isPassed ? '#4caf50' : '#c62828')
                            : '#7f4b2d'
                        }`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      <Typography sx={{ 
                        fontSize: '0.875rem', 
                        fontWeight: 600, 
                        color: '#7f4b2d'
                      }}>
                        {val}
                      </Typography>
                      <Select
                        value={currentRating || ''}
                        onChange={(e) => handlePannamRatingChange(key, subSection, val, e.target.value)}
                        disabled={hasStudentStopped && !currentRating}
                        displayEmpty
                        size="small"
                        sx={{
                          width: '100%',
                          height: '32px',
                          fontSize: '0.75rem',
                          backgroundColor: '#fff',
                          '& .MuiSelect-select': {
                            py: 0.5,
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: currentRating 
                              ? (currentRating === 'pass' ? '#4caf50' : '#c62828')
                              : '#90caf9'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: currentRating 
                              ? (currentRating === 'pass' ? '#4caf50' : '#c62828')
                              : '#4caf50'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: currentRating 
                              ? (currentRating === 'pass' ? '#4caf50' : '#c62828')
                              : '#4caf50',
                            borderWidth: 2
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.75rem', color: '#999' }}>
                          <em>--</em>
                        </MenuItem>
                        <MenuItem 
                          value="pass"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#2e7d32'
                          }}
                        >
                          {lang === 'telugu' ? 'ఉత్తీర్ణత' : 'उत्तीर्णता'}
                        </MenuItem>
                        <MenuItem 
                          value="fail"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#c62828'
                          }}
                        >
                          {lang === 'telugu' ? 'అనుత్తీర్ణత' : 'अनुत्तीर्णता'}
                        </MenuItem>
                      </Select>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        );
      };
      
      return (
        <Accordion 
          key={key}
          defaultExpanded
          sx={{ 
            mb: 2,
            '&:before': { display: 'none' },
            boxShadow: 2
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f0f4ff',
              '&:hover': {
                backgroundColor: '#e8eeff',
              },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                justifyContent: 'space-between'
              }
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#7f4b2d', fontSize: '1.1rem' }}>
              {item.section}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                {labels[lang].currentGrandham}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Display special Krishna rating dropdowns */}
              <Box>
                {formatSpecialKrishnaData()}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    }
    
    // Format the data display with checkboxes for previous items (standard handling)
    const formatGrandhamData = (data) => {
      if (!data || typeof data !== 'object') return 'No data';
      
      // Special handling for non-Krishna items (has _items array)
      if (item.isNonKrishna && data._items) {
        return (
          <Box sx={{ pl: 2 }}>
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {data._items.map((itemName, idx) => {
                  // For non-Krishna, pannamRatings[key] is flat: {itemName: 'pass'/'fail'/''}
                  const isCompleted = isPreviousSection && pannamRatings[key]?.[itemName] === 'pass';
                  const currentRating = !isPreviousSection && pannamRatings[key]?.[itemName];
                  
                  return isPreviousSection ? (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: isCompleted ? '#c8e6c9' : '#fff3cd',
                        borderRadius: 1,
                        border: `2px solid ${isCompleted ? '#4caf50' : '#ffc107'}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      <Checkbox
                        checked={isCompleted}
                        onChange={() => {
                          // Toggle between 'pass' and '' (empty/unchecked)
                          const newValue = isCompleted ? '' : 'pass';
                          
                          // Update local state
                          const newRatings = { ...pannamRatings };
                          if (!newRatings[key]) newRatings[key] = {};
                          newRatings[key][itemName] = newValue;
                          setPannamRatings(newRatings);
                          
                          // Auto-save to database using same pattern as handleNonKrishnaRatingChange
                          // For non-Krishna, use '1' as pannam (constant for non-Krishna types)
                          const saveToBackend = async () => {
                            try {
                              const payload = {
                                telugu_year: teluguYearTelugu,
                                pathasala_id: selectedPathasala,
                                vatu_id: selectedVatu,
                                itemKey: key,
                                kanda: itemName,
                                pannam: '1',
                                rating: newValue,
                                pariksha_type: selectedParikshaType
                              };
                              
                              await api.post('/api/result/save-pannam-rating', payload);
                            } catch (error) {
                              console.error('Error auto-saving non-Krishna previous grandham rating:', error);
                            }
                          };
                          
                          setTimeout(saveToBackend, 100);
                        }}
                        size="small"
                        sx={{
                          p: 0,
                          mr: 0.5,
                          color: '#ffc107',
                          '&.Mui-checked': { color: '#4caf50' }
                        }}
                      />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: isCompleted ? 600 : 400 }}>
                        {itemName}
                      </Typography>
                    </Box>
                  ) : (
                    <FormControl key={idx} sx={{ minWidth: 150 }}>
                      <Select
                        value={currentRating || ''}
                        onChange={(e) => {
                          const newRatings = { ...pannamRatings };
                          if (!newRatings[key]) newRatings[key] = {};
                          newRatings[key][itemName] = e.target.value;
                          setPannamRatings(newRatings);
                          
                          // Auto-save for Non-Krishna current items using same pattern
                          const saveToBackend = async () => {
                            try {
                              const payload = {
                                telugu_year: teluguYearTelugu,
                                pathasala_id: selectedPathasala,
                                vatu_id: selectedVatu,
                                itemKey: key,
                                kanda: itemName,
                                pannam: '1',
                                rating: e.target.value,
                                pariksha_type: selectedParikshaType
                              };
                              
                              await api.post('/api/result/save-pannam-rating', payload);
                            } catch (error) {
                              console.error('Error auto-saving non-Krishna current grandham rating:', error);
                            }
                          };
                          
                          setTimeout(saveToBackend, 100);
                        }}
                        size="small"
                      >
                        <MenuItem value="">Select Rating</MenuItem>
                        <MenuItem value="pass">Pass</MenuItem>
                        <MenuItem value="fail">Fail</MenuItem>
                      </Select>
                      <FormHelperText>{itemName}</FormHelperText>
                    </FormControl>
                  );
                })}
              </Box>
            </Box>
          </Box>
        );
      }
      
      // Original Krishna handling
      return (
        <Box sx={{ pl: 2 }}>
          {Object.keys(data).map(subSection => {
            const values = data[subSection];
            if (Array.isArray(values)) {
              const allChecked = isPreviousSection && isAllCompleted(key, subSection, values);
              const someChecked = isPreviousSection && isSomeCompleted(key, subSection, values);
              
              return (
                <Box key={subSection} sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
                  {/* Kanda header with "All Given" checkbox */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#7f4b2d', flex: 1 }}>
                      {subSection}
                    </Typography>
                    {isPreviousSection && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={() => handleAllGivenToggle(key, subSection, values)}
                            sx={{
                              color: '#ffc107',
                              '&.Mui-checked': { color: '#4caf50' },
                              '&.MuiCheckbox-indeterminate': { color: '#ff9800' }
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontWeight: 600, color: '#7f4b2d', fontSize: '0.9rem' }}>
                            {labels[lang].allGiven}
                          </Typography>
                        }
                      />
                    )}
                  </Box>
                  {/* Pannams with individual checkboxes for previous, rating dropdowns for current */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: isPreviousSection ? 0 : 2 }}>
                    {values.map((val, idx) => {
                      const isCompleted = isPreviousSection && pannamRatings[key]?.[subSection]?.[val] === 'pass';
                      const currentRating = !isPreviousSection && pannamRatings[key]?.[subSection]?.[val];
                      
                      // Debug logging for previous grandham
                      if (isPreviousSection && idx === 0) {
                      }
                      
                      // Check if this pannam is excluded due to overlap with previous grandham
                      const sectionName = item.section;
                      const isExcludedOverlap = !isPreviousSection && isPannamInPrevious(sectionName, subSection, val);
                      
                      return isPreviousSection ? (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            backgroundColor: isCompleted ? '#c8e6c9' : '#fff3cd',
                            borderRadius: 1,
                            border: `2px solid ${isCompleted ? '#4caf50' : '#ffc107'}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: 2,
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <Checkbox
                            checked={isCompleted}
                            onChange={() => handlePannamToggle(key, subSection, val)}
                            size="small"
                            sx={{
                              p: 0,
                              mr: 0.5,
                              color: '#ffc107',
                              '&.Mui-checked': { color: '#4caf50' }
                            }}
                          />
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: isCompleted ? 600 : 400 }}>
                            {val}
                          </Typography>
                        </Box>
                      ) : (
                        <Tooltip 
                          title={isExcludedOverlap ? (lang === 'telugu' ? 'ఈ పన్నం మునుపటి గ్రంథములో ఉంది - లెక్కింపులో చేర్చబడలేదు' : 'यह पन्नम् पूर्वग्रन्थे अस्ति - गणनायां न समाविष्टम्') : ''}
                          arrow
                          placement="top"
                        >
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: isExcludedOverlap
                                ? (currentRating === 'pass' ? '#e8f5e9' : currentRating === 'fail' ? '#ffebee' : '#f5f5f5')
                                : item.isSpecialRule
                                  ? (currentRating === 'pass' ? '#e8f5e9' : currentRating === 'fail' ? '#ffebee' : '#e3f2fd')
                                  : currentRating 
                                    ? (currentRating >= 8 ? '#e8f5e9' : currentRating >= 4 ? '#fff3e0' : '#ffebee')
                                    : '#e3f2fd',
                              borderRadius: 1,
                              border: isExcludedOverlap
                                ? `2px dashed ${currentRating === 'pass' ? '#4caf50' : currentRating === 'fail' ? '#c62828' : '#9e9e9e'}`
                                : item.isSpecialRule
                                  ? `2px solid ${currentRating === 'pass' ? '#4caf50' : currentRating === 'fail' ? '#c62828' : '#7f4b2d'}`
                                  : `2px solid ${
                                      currentRating 
                                        ? (currentRating >= 8 ? '#4caf50' : currentRating >= 4 ? '#f57c00' : '#c62828')
                                        : '#7f4b2d'
                                    }`,
                              transition: 'all 0.3s ease',
                              opacity: isExcludedOverlap ? 0.6 : 1,
                              '&:hover': {
                                boxShadow: 2,
                                transform: 'translateY(-1px)',
                                opacity: isExcludedOverlap ? 0.8 : 1
                              }
                            }}
                          >
                          <Typography sx={{ 
                            fontSize: '0.875rem', 
                            fontWeight: 600, 
                            color: isExcludedOverlap ? '#757575' : '#7f4b2d', 
                            mr: 0.5,
                            textDecoration: isExcludedOverlap ? 'line-through' : 'none'
                          }}>
                            {val}:
                          </Typography>
                          {isExcludedOverlap && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', mr: 0.5 }}>⚠</Typography>
                          )}
                          <Select
                            value={currentRating || ''}
                            onChange={(e) => handlePannamRatingChange(key, subSection, val, e.target.value)}
                            disabled={hasStudentStopped && !currentRating}
                            displayEmpty
                            size="small"
                            sx={{
                              minWidth: isExcludedOverlap ? '70px' : '55px',
                              height: '28px',
                              fontSize: '0.875rem',
                              backgroundColor: '#fff',
                              '& .MuiSelect-select': {
                                py: 0.5,
                                pr: 3,
                                display: 'flex',
                                alignItems: 'center'
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: currentRating 
                                  ? (currentRating === 'pass' ? '#4caf50' : currentRating === 'fail' ? '#c62828' : currentRating >= 8 ? '#4caf50' : currentRating >= 4 ? '#f57c00' : '#c62828')
                                  : '#90caf9'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: currentRating 
                                  ? (currentRating === 'pass' ? '#4caf50' : currentRating === 'fail' ? '#c62828' : currentRating >= 8 ? '#4caf50' : currentRating >= 4 ? '#f57c00' : '#c62828')
                                  : '#4caf50'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: currentRating 
                                  ? (currentRating === 'pass' ? '#4caf50' : currentRating === 'fail' ? '#c62828' : currentRating >= 8 ? '#4caf50' : currentRating >= 4 ? '#f57c00' : '#c62828')
                                  : '#4caf50',
                                borderWidth: 2
                              }
                            }}
                          >
                            <MenuItem value="" sx={{ fontSize: '0.875rem', color: '#999' }}>
                              <em>--</em>
                            </MenuItem>
                            {isExcludedOverlap || (item.isSpecialRule && item.isSpecialRule !== 'saamaanya_kramamu') ? (
                              // Pass/Fail options for excluded pannams OR special rule items (except saamaanya_kramamu)
                              [
                                <MenuItem key="pass" value="pass" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#2e7d32' }}>
                                  {lang === 'telugu' ? 'ఉత్తీర్ణత' : 'उत्तीर्णम्'}
                                </MenuItem>,
                                <MenuItem key="fail" value="fail" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#c62828' }}>
                                  {lang === 'telugu' ? 'అనుత్తీర్ణత' : 'अनुत्तीर्णम्'}
                                </MenuItem>
                              ]
                            ) : (
                              // 1-10 rating options (for regular current items and saamaanya_kramamu)
                              [...([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                                  <MenuItem 
                                    key={rating} 
                                    value={rating}
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                      color: rating >= 8 ? '#2e7d32' : rating >= 4 ? '#f57c00' : '#c62828'
                                    }}
                                  >
                                    {rating}
                                  </MenuItem>
                                )))
                              ]
                            )}
                          </Select>
                          {!isExcludedOverlap && !(item.isSpecialRule && item.isSpecialRule !== 'saamaanya_kramamu') && (
                            <Typography sx={{ 
                              fontSize: '0.875rem', 
                              fontWeight: 600, 
                              color: '#7f4b2d', 
                              ml: 0.5 
                            }}>
                              /10
                            </Typography>
                          )}
                        </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              );
            }
            return null;
          })}
        </Box>
      );
    };
    
    return (
      <Accordion 
        key={key}
        defaultExpanded
        sx={{ 
          mb: 2,
          '&:before': { display: 'none' },
          boxShadow: 2
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: isPreviousSection ? '#fff9e6' : '#f5f5f5',
            '&:hover': {
              backgroundColor: isPreviousSection ? '#fff3cd' : '#e0e0e0',
            },
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between'
            }
          }}
        >
          <Typography sx={{ fontWeight: 600, color: '#7f4b2d', fontSize: '1.1rem' }}>
            {item.section}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: isPreviousSection ? '#ffc107' : '#4caf50',
                color: '#fff',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              {isPreviousSection ? labels[lang].previousGrandham : labels[lang].currentGrandham}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Display grandham data structure */}
            <Box>
              {formatGrandhamData(item.data)}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 } }}
    >
      {/* Show access denied message if parikshaadhikari not found or API failed */}
      {accessDenied && (
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      )}
      
      {/* Show main content only if access is granted */}
      {!accessDenied && (
        <>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            mb: { xs: 3, md: 4 },
            color: "#7f4b2d",
            fontWeight: 600,
            textAlign: "center",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2rem" },
            borderBottom: "3px solid #7f4b2d",
            pb: 2,
          }}
        >
          {labels[lang].title}
        </Typography>

        {/* Athyuthama Mode Alert */}
        {isAthyuthamaMode && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              backgroundColor: '#e3f2fd',
              borderLeft: '4px solid #1976d2',
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiAlert-icon': {
                color: '#1976d2'
              }
            }}
          >
            {lang === 'telugu' 
              ? '🏆 అత్యుత్తమ పరీక్ష మోడ్ సక్రియం - టాప్ వాటువులను మాత్రమే చూపిస్తోంది' 
              : '🏆 अत्युत्तम परीक्षा मोड सक्रिय - केवल शीर्ष वटुः दर्शयति'}
          </Alert>
        )}

        {/* Unavailable Today Alert */}
        {isUnavailableToday && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3, 
              backgroundColor: '#fff3cd',
              borderLeft: '4px solid #ff9800',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            {lang === 'telugu' 
              ? '⚠️ మీరు ఈ రోజు అందుబాటులో లేనని గుర్తించారు.' 
              : '⚠️ आप आज उपलब्ध नहीं हैं।'}
          </Alert>
        )}

        {/* Show all content only if parikshaadhikari is available today */}
        {!isUnavailableToday && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(2, 1fr)",
                },
                rowGap: 2,
                columnGap: 2,
                mb: { xs: 3, md: 4 },
              }}
            >
          {/* Pathasala Dropdown */}
          <Box>
            <FormControl
              fullWidth
              disabled={loading}
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                  fontWeight: 600,
                  color: "#7f4b2d",
                  "&.Mui-focused": {
                    color: "#7f4b2d",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                  "& fieldset": {
                    borderColor: "#7f4b2d",
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: "#001a47",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#7f4b2d",
                    borderWidth: 2,
                  },
                },
              }}
            >
              <InputLabel id="pathasala-select-label">
                {labels[lang].pathasala}
              </InputLabel>
              <Select
                labelId="pathasala-select-label"
                id="pathasala-select"
                value={selectedPathasala}
                label={labels[lang].pathasala}
                onChange={handlePathasalaChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      width: "auto",
                      minWidth: "250px",
                    },
                  },
                }}
                sx={{
                  minHeight: { xs: 48, sm: 52, md: 56 },
                  "& .MuiSelect-select": {
                    py: { xs: 1.5, md: 2 },
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    py: 1.5,
                    minWidth: "250px",
                  }}
                >
                  <em>{labels[lang].selectPathasala}</em>
                </MenuItem>
                {pathasalaList.map((pathasala) => (
                  <MenuItem
                    key={pathasala._id}
                    value={pathasala._id}
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      py: 1.5,
                      minWidth: "250px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#e3f2fd",
                        "&:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      },
                    }}
                  >
                    {pathasala.pathasalaName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FormControl
              fullWidth
              disabled={loading || !selectedPathasala}
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                  fontWeight: 600,
                  color: "#7f4b2d",
                  "&.Mui-focused": {
                    color: "#7f4b2d",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                  "& fieldset": {
                    borderColor: "#7f4b2d",
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: "#001a47",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#7f4b2d",
                    borderWidth: 2,
                  },
                  "&.Mui-disabled fieldset": {
                    borderColor: "#ccc",
                  },
                },
              }}
            >
              <InputLabel id="vatu-select-label">
                {labels[lang].vatuName}
              </InputLabel>
              <Select
                labelId="vatu-select-label"
                id="vatu-select"
                value={selectedVatu}
                label={labels[lang].vatuName}
                onChange={handleVatuChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      width: "auto",
                      minWidth: "250px",
                    },
                  },
                }}
                sx={{
                  minHeight: { xs: 48, sm: 52, md: 56 },
                  "& .MuiSelect-select": {
                    py: { xs: 1.5, md: 2 },
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    py: 1.5,
                    minWidth: "250px",
                  }}
                >
                  <em>{labels[lang].selectVatu}</em>
                </MenuItem>
                {vatuList.map((vatu) => (
                  <MenuItem
                    key={vatu.vatu_id}
                    value={vatu.vatu_id}
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      py: 1.5,
                      minWidth: "250px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#e3f2fd",
                        "&:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      },
                    }}
                  >
                    {vatu.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Pariksha Type Selection - Show when multiple types are available */}
        {selectedVatu && availableParikshaTypes.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <FormControl
              fullWidth
              sx={{
                maxWidth: '400px',
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#7f4b2d",
                  "&.Mui-focused": { color: "#7f4b2d" },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#7f4b2d", borderWidth: 2 },
                  "&:hover fieldset": { borderColor: "#001a47" },
                  "&.Mui-focused fieldset": { borderColor: "#7f4b2d", borderWidth: 2 },
                },
              }}
            >
              <InputLabel id="pariksha-type-select-label">
                {lang === 'telugu' ? 'పరీక్ష రకం ఎంచుకోండి' : 'परीक्षा प्रकारं चिनोतु'}
              </InputLabel>
              <Select
                labelId="pariksha-type-select-label"
                id="pariksha-type-select"
                value={selectedParikshaType}
                label={lang === 'telugu' ? 'పరీక్ష రకం ఎంచుకోండి' : 'परीक्षा प्रकारं चिनोतु'}
                onChange={(e) => setSelectedParikshaType(e.target.value)}
              >
                <MenuItem value="">
                  <em>{lang === 'telugu' ? 'ఎంచుకోండి...' : 'चिनोतु...'}</em>
                </MenuItem>
                {availableParikshaTypes.map((type) => {
                  const typeLabels = {
                    'krishna_yajurveda': { telugu: 'కృష్ణ యజుర్వేదం', sanskrit: 'कृष्ण यजुर्वेदम्' },
                    'sanskrit': { telugu: 'సంస్కృతం', sanskrit: 'संस्कृतम्' },
                    'vedadhyayi_sahityam': { telugu: 'వేదాధ్యయి సాహిత్యం', sanskrit: 'वेदाध्ययि साहित्यम्' },
                    'lakshanam': { telugu: 'లక్షణం', sanskrit: 'लक्षणम्' }
                  };
                  return (
                    <MenuItem key={type} value={type}>
                      {typeLabels[type]?.[lang] || type}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* No intersection warning */}
        {selectedVatu && availableParikshaTypes.length === 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {lang === 'telugu' 
              ? '⚠️ ఈ వటువు పరీక్ష రకాలు మీ పరీక్షాధికారి హక్కులతో సరిపోలలేదు.'
              : '⚠️ इस वटु के परीक्षा प्रकार आपके परीक्षाधिकारी अधिकारों से मेल नहीं खाते।'}
          </Alert>
        )}

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              gap: 2,
            }}
          >
            <CircularProgress size={30} sx={{ color: "#7f4b2d" }} />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: "1.1rem" }}
            >
              {labels[lang].loading}
            </Typography>
          </Box>
        )}

        {selectedVatu && selectedVatuData && selectedParikshaType && (
          <Box
            sx={{
              mt: 4,
              p: 3,
              backgroundColor: "#fff",
              borderRadius: 2,
              border: "2px solid #7f4b2d",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                pb: 2,
                borderBottom: "2px solid #7f4b2d",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#7f4b2d",
                  fontWeight: 600,
                  fontSize: "1.2rem",
                }}
              >
                {labels[lang].grandhamDetails}
              </Typography>
            </Box>

            {grandhamToShow.previous.length > 0 ||
            grandhamToShow.current.length > 0 ? (
              <Box>
                {/* Current Grandham Section */}
                {grandhamToShow.current.length > 0 && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#4caf50",
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#4caf50",
                          }}
                        />
                        {(() => {
                          let label = labels[lang].currentGrandham;
                          if (grandhamToShow.specialRule === 'saamaanya_kramamu') {
                            label += ` - ${lang === 'telugu' ? 'సామాన్య క్రమాంతం' : 'सामान्य क्रमन्थम्'}`;
                          } else if (grandhamToShow.specialRule === 'saamaanya_ghanamu') {
                            label += ` - ${lang === 'telugu' ? 'సామాన్య ఘనము' : 'सामान्य घनम्'}`;
                          }
                          return label;
                        })()}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {(() => {
                          // Check if current grandham has special cases
                          const hasSpecialCase = grandhamToShow.current.some(item => 
                            (item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule
                          );
                          
                          if (hasSpecialCase) {
                            // For special cases, count failures and show category
                            let totalFailures = 0;
                            grandhamToShow.current.forEach((item, idx) => {
                              if ((item.isSpecialKrishna && item.totalDropdowns) || item.isSpecialRule) {
                                const key = `curr_${item.section}_${idx}`;
                                
                                if (item.isSpecialKrishna && item.totalDropdowns) {
                                  // Count 'fail' ratings
                                  const kandaData = pannamRatings[key]?.['_special_kanda'];
                                  if (kandaData) {
                                    Object.keys(kandaData).forEach(pannam => {
                                      if (kandaData[pannam] === 'fail') {
                                        totalFailures++;
                                      }
                                    });
                                  }
                                } else if (item.isSpecialRule) {
                                  // Count 'fail' ratings
                                  const ratingObj = pannamRatings[key];
                                  if (ratingObj) {
                                    Object.keys(ratingObj || {}).forEach(kanda => {
                                      Object.keys(ratingObj[kanda] || {}).forEach(pannam => {
                                        if (ratingObj[kanda][pannam] === 'fail') {
                                          totalFailures++;
                                        }
                                      });
                                    });
                                  }
                                }
                              }
                            });
                            
                            const category = calculateSpecialKrishnaCategory(totalFailures);
                            const isFailed = totalFailures > 10;
                            
                            return (
                              <>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: isFailed ? "#c62828" : "#1565c0",
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {lang === 'telugu' ? 'అనుత్తీర్ణత సంఖ్య' : 'अनुत्तीर्णता संख्या'}: {totalFailures}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: isFailed ? "#c62828" : "#2e7d32",
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {lang === 'telugu' ? 'వర్గీకరణ' : 'वर्गीकरणम्'}: {category}
                                </Typography>
                              </>
                            );
                          } else if (hasStudentStopped && countAnutheernathaRatings() >= 5) {
                            return (
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#c62828",
                                  fontWeight: 700,
                                  fontSize: "1rem",
                                }}
                              >
                                {lang === 'telugu' ? 'ఉత్తీర్ణత' : 'उत्तीर्णम्'}: {lang === 'telugu' ? 'అనుత్తీర్ణుడయ్యాడు' : 'अनुत्तीर्णः अभवत्'}
                              </Typography>
                            );
                          } else {
                            return (
                              <>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {labels[lang].passPercentage}:{" "}
                                  {calculatePassPercentage()}%
                                  {(() => {
                                    const percentage = calculatePassPercentage();
                                    const category = getPercentageCategory(percentage);
                                    return category ? ` (${category})` : "";
                                  })()}
                                </Typography>
                                {['Admin','SuperUser'].includes(user?.role) && (<Typography
                                  variant="body1"
                                  sx={{
                                    color: "#2e7d32",
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {labels[lang].examReward}: {calculateExamReward()}
                                </Typography>)}
                              </>
                            );
                          }
                        })()}
                      </Box>
                    </Box>
                    {grandhamToShow.current.map((item, index) =>
                      renderGrandhamItem(item, index, false)
                    )}
                  </Box>
                )}

                {/* Previous Grandham Section */}
                {grandhamToShow.previous.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#ff9800",
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#ff9800",
                          }}
                        />
                        {labels[lang].previousGrandham}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        {['Admin','SuperUser'].includes(user?.role) && (() => {
                          const seen = new Set();
                          const items = [];
                          grandhamToShow.current.forEach((item) => {
                            const displayName = moolamSections.includes(item.section) ? labels[lang].moolam : item.section;
                            if (!seen.has(displayName)) {
                              seen.add(displayName);
                              items.push({ displayName, section: item.section });
                            }
                          });
                          return items.map((it, idx) => (
                            <Typography
                              key={idx}
                              variant="body1"
                              sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '1rem' }}
                            >
                              {it.displayName} - {labels[lang].marks}: {computeBaseReward(it.section)}
                            </Typography>
                          ));
                        })()}
                      </Box>
                    </Box>
                    {grandhamToShow.previous.map((item, index) =>
                      renderGrandhamItem(item, index, true)
                    )}
                  </Box>
                )}
              </Box>
      ) : (
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  py: 4,
                }}
              >
                {lang === "telugu"
                  ? "ప్రదర్శించడానికి గ్రంథ వివరాలు లేవు"
                  : "प्रदर्शनाय ग्रन्थविवराणि नास्ति"}
              </Typography>
            )}
          </Box>
        )}

        {/* Parikshaadhikari Feedback Section */}
        {selectedVatu && batchParikshaadhikaris.length > 0 && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#7f4b2d', 
                    mb: 3,
                    fontSize: '1.3rem'
                  }}
                >
                  {lang === 'telugu' 
                    ? '🎤 పరిక్షాధికారుల ఫీడ్‌బ్యాక్' 
                    : '🎤 परीक्षाधिकारी प्रतिक्रिया'}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Parikshaadhikaris Feedback Cards */}
                {batchParikshaadhikaris.map((parikshadhikari) => (
                  <Card 
                    key={parikshadhikari._id} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ddd'
                    }}
                  >
                    <Typography 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#7f4b2d', 
                        mb: 2,
                        fontSize: '1rem'
                      }}
                    >
                      👤 {lang === 'telugu' ? parikshadhikari.telugu : parikshadhikari.sanskrit}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      {/* Positive Feedback */}
                      <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                        <Typography sx={{ fontWeight: 600, color: '#2e7d32', mb: 1.5, fontSize: '0.9rem' }}>
                          ✅ {lang === 'telugu' ? 'సానుకూల ఫీడ్‌బ్యాక్' : 'सकारात्मक'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={recordingState[`${parikshadhikari._id}_positive`] ? (lang === 'telugu' ? labels.telugu.recordingStop : labels.sanskrit.recordingStop) : (lang === 'telugu' ? labels.telugu.recordingStart : labels.sanskrit.recordingStart)}>
                            <IconButton
                              onClick={() => recordingState[`${parikshadhikari._id}_positive`] ? stopRecording(`${parikshadhikari._id}_positive`) : startRecording(`${parikshadhikari._id}_positive`)}
                              disabled={recordingState[`${parikshadhikari._id}_improvement`]}
                              sx={{
                                color: recordingState[`${parikshadhikari._id}_positive`] ? '#c62828' : '#4caf50',
                                backgroundColor: recordingState[`${parikshadhikari._id}_positive`] ? '#ffebee' : '#e8f5e9',
                                borderRadius: '50%',
                                width: 56,
                                height: 56,
                                fontSize: '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: recordingState[`${parikshadhikari._id}_positive`] ? '#ffcdd2' : '#c8e6c9',
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              {recordingState[`${parikshadhikari._id}_positive`] ? '⏹' : '⏺'}
                            </IconButton>
                          </Tooltip>

                          {audioBlobs[`${parikshadhikari._id}_positive`] && (
                            <Tooltip title={lang === 'telugu' ? labels.telugu.playRecording : labels.sanskrit.playRecording}>
                              <IconButton
                                onClick={() => playRecording(`${parikshadhikari._id}_positive`)}
                                sx={{
                                  color: '#1976d2',
                                  backgroundColor: '#e3f2fd',
                                  borderRadius: '50%',
                                  width: 56,
                                  height: 56,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  '&:hover': {
                                    backgroundColor: '#bbdefb',
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                ▶
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Typography 
                          sx={{ 
                            textAlign: 'center', 
                            mt: 1, 
                            fontSize: '0.75rem', 
                            color: audioBlobs[`${parikshadhikari._id}_positive`] ? '#4caf50' : '#999'
                          }}
                        >
                          {audioBlobs[`${parikshadhikari._id}_positive`] 
                            ? ('✓ ' + (lang === 'telugu' ? labels.telugu.recordedSuccessfully : labels.sanskrit.recordedSuccessfully))
                            : (lang === 'telugu' ? labels.telugu.notRecorded : labels.sanskrit.notRecorded)}
                        </Typography>
                      </Box>

                      {/* Improvement Feedback */}
                      <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                        <Typography sx={{ fontWeight: 600, color: '#e65100', mb: 1.5, fontSize: '0.9rem' }}>
                          📈 {lang === 'telugu' ? 'మెరుదరణ ప్రాంతాలు' : 'सुधार क्षेत्र'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={recordingState[`${parikshadhikari._id}_improvement`] ? (lang === 'telugu' ? labels.telugu.recordingStop : labels.sanskrit.recordingStop) : (lang === 'telugu' ? labels.telugu.recordingStart : labels.sanskrit.recordingStart)}>
                            <IconButton
                              onClick={() => recordingState[`${parikshadhikari._id}_improvement`] ? stopRecording(`${parikshadhikari._id}_improvement`) : startRecording(`${parikshadhikari._id}_improvement`)}
                              disabled={recordingState[`${parikshadhikari._id}_positive`]}
                              sx={{
                                color: recordingState[`${parikshadhikari._id}_improvement`] ? '#c62828' : '#ff9800',
                                backgroundColor: recordingState[`${parikshadhikari._id}_improvement`] ? '#ffebee' : '#fff3e0',
                                borderRadius: '50%',
                                width: 56,
                                height: 56,
                                fontSize: '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: recordingState[`${parikshadhikari._id}_improvement`] ? '#ffcdd2' : '#ffe0b2',
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              {recordingState[`${parikshadhikari._id}_improvement`] ? '⏹' : '⏺'}
                            </IconButton>
                          </Tooltip>

                          {audioBlobs[`${parikshadhikari._id}_improvement`] && (
                            <Tooltip title={lang === 'telugu' ? labels.telugu.playRecording : labels.sanskrit.playRecording}>
                              <IconButton
                                onClick={() => playRecording(`${parikshadhikari._id}_improvement`)}
                                sx={{
                                  color: '#1976d2',
                                  backgroundColor: '#e3f2fd',
                                  borderRadius: '50%',
                                  width: 56,
                                  height: 56,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  '&:hover': {
                                    backgroundColor: '#bbdefb',
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                ▶
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Typography 
                          sx={{ 
                            textAlign: 'center', 
                            mt: 1, 
                            fontSize: '0.75rem', 
                            color: audioBlobs[`${parikshadhikari._id}_improvement`] ? '#4caf50' : '#999'
                          }}
                        >
                          {audioBlobs[`${parikshadhikari._id}_improvement`] 
                            ? ('✓ ' + (lang === 'telugu' ? labels.telugu.recordedSuccessfully : labels.sanskrit.recordedSuccessfully))
                            : (lang === 'telugu' ? labels.telugu.notRecorded : labels.sanskrit.notRecorded)}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}

                {/* Feedback Requirement Info */}
                {!isFeedbackComplete() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {lang === 'telugu'
                      ? '⚠️ కనీసం ఒక పరిక్షాధికారి కోసం సానుకూల మరియు మెరుదరణ ఫీడ్‌బ్యాక్ రెండూ నిర్బంధమైనవి'
                      : '⚠️ कम से कम एक परीक्षाधिकारी के लिए सकारात्मक और सुधार प्रतिक्रिया दोनों अनिवार्य हैं'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Save Button */}
        {selectedVatu && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={saving ? null : <SaveIcon />}
              onClick={handleSave}
              disabled={
                saving || !teluguYearTelugu || !selectedPathasala || !selectedVatu || !isAllDisplayedGrandhamCompleted() || (!(isAthyuthamaMode && grandhamToShow?.isAthyuthama) && !isFeedbackComplete())
              }
              sx={{ minWidth: 200 }}
            >
              {saving ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  {lang === "telugu" ? "భద్రపరుస్తోంది..." : "रक्षति..."}
                </>
              ) : lang === "telugu" ? (
                "ఫలితాలను భద్రపరచు"
              ) : (
                "फलानि रक्षतु")}
            </Button>
          </Box>
        )}
      </>
        )}
      </Paper>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!saveMessage}
        autoHideDuration={6000}
        onClose={() => setSaveMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {saveMessage}
        </Alert>
      </Snackbar>

      {/* Error Message Snackbar */}
      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {saveError}
        </Alert>
      </Snackbar>

      {/* Underperforming Student Dialog */}
      <Dialog
        open={isUnderperformingDialogOpen}
        onClose={() => {
          setIsUnderperformingDialogOpen(false);
          setPendingRatingChange(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 5
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#c62828',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.2rem',
            py: 2
          }}
        >
          {labels[lang].underperformingTitle}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#333',
              lineHeight: 1.6,
              fontSize: '1rem'
            }}
          >
            {labels[lang].underperformingMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleStopExam}
            sx={{
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: '#ffebee'
              }
            }}
          >
            {labels[lang].stopExam}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleContinueExam}
            sx={{
              fontWeight: 600
            }}
          >
            {labels[lang].continueExam}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Special Krishna Conversion Dialog */}
      <Dialog
        open={isSpecialKrishnaConversionDialogOpen}
        onClose={() => {
          setIsSpecialKrishnaConversionDialogOpen(false);
          setSpecialKrishnaConversionInfo(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 5
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#ff6f00',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.1rem',
            py: 2
          }}
        >
          {lang === 'telugu' ? 'హెచ్చరిక: విశేష పరీక్ష నియమ ఉల్లంఘన' : 'चेतावनी: विशेष परीक्षा नियम उल्लंघन'}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {specialKrishnaConversionInfo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#d32f2f',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                {lang === 'telugu' 
                  ? `⚠️ ఈ విద్యార్థి నియమమైన 6 కంటే ఎక్కువ దోషాలు రావడం వలన ${specialKrishnaConversionInfo.from} పరీక్షను నిలిపివేయడం జరిగింది మరియు సామాన్య క్రమాంత పరీక్షగా మార్చబడింది।`
                  : `⚠️ एषः छात्रः नियमेन निर्दिष्टानां षट् अपेक्षा अतिक्रान्तानां दोषानां प्राप्तेः ${specialKrishnaConversionInfo.from} परीक्षा स्थगिता अभवत् तथा सा सामान्यक्रमान्त परीक्षा इति परिवर्तिता।`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSpecialKrishnaConversionConfirm}
            sx={{
              fontWeight: 600,
              backgroundColor: '#ff6f00',
              px: 4,
              '&:hover': {
                backgroundColor: '#e65100'
              }
            }}
          >
            {lang === 'telugu' ? 'సరే' : 'ठीक है'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Container>
  );
}
